import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Space, Button, Tag, Typography, Descriptions, Tabs, Table, Modal, message, Collapse, Tooltip, Select, Input, Checkbox, Form, InputNumber, Switch } from 'antd';
import { ReloadOutlined, EditOutlined, CloseCircleOutlined, PlusOutlined, CheckCircleFilled, CloseCircleFilled, CopyOutlined, QuestionCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import request from '../../utils/request';
import { useTabs } from '../../contexts/TabsContext';
import { getInventoryUnitDetail } from '../../services/inventory';
import { getProjectDetail } from '../../services/project';
import InstructionTargetsOverview from './components/InstructionTargetsOverview';
import NonStdWorkbench from '../nonstandard-ops/NonStdWorkbench';
import { downloadFileAsBlob } from '../../utils/downloadFile';
import { getAttachmentUrl } from '../../utils/attachmentUrl';

const { Title, Text } = Typography;

// 是否允许管理员/员工在详情页手动填写包装/运单信息（"包装与运单"面板的"填写包装/运单信息"按钮）。
// 目前关闭（按钮保留显示但置灰）：手动填写会绕过执行流程，包装和运单信息必须通过指令执行阶段
// 提交。只是禁用按钮，弹窗与保存逻辑都保留，以后需要恢复把它改回 true 即可。
const ALLOW_MANUAL_PACK_RESULT_EDIT = false;

const InstructionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const instructionId = useMemo(() => {
    const raw = String(params.id || '').trim();
    if (!raw) return NaN;
    if (/^\d+$/.test(raw)) return Number(raw);
    const m = raw.match(/^i-(\d+)$/i);
    if (m && m[1]) return Number(m[1]);
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [params.id]);

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [showTplBrief, setShowTplBrief] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [appendAvailableIocCount, setAppendAvailableIocCount] = useState(0);
  const [appendAvailabilityLoading, setAppendAvailabilityLoading] = useState(false);
  const [shownTips, setShownTips] = useState<Record<string, boolean>>({});
  const [showQTip, setShowQTip] = useState(false);
  const [qTipText, setQTipText] = useState<string>('');
  const [qTipTitle, setQTipTitle] = useState<string>('');
  // 日志相关状态
  const [logLoading, setLogLoading] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logInspect, setLogInspect] = useState<any | null>(null);
  const [actorNameMap, setActorNameMap] = useState<Record<string, string>>({});
  const [templateNameMap, setTemplateNameMap] = useState<Record<string, string>>({});
  const [projectCodeMap, setProjectCodeMap] = useState<Record<string, string>>({});
  const [expandedAttachBatch, setExpandedAttachBatch] = useState<Record<string, boolean>>({});
  const [billingSummary, setBillingSummary] = useState<any | null>(null);
  const [woList, setWoList] = useState<any[]>([]);
  const [dagFocusId, setDagFocusId] = useState<number | null>(null);
  // 手动添加日志
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addNoteSubmitting, setAddNoteSubmitting] = useState(false);
  const [noteText, setNoteText] = useState<string>('');
  const [noteSeverity, setNoteSeverity] = useState<'info'|'warn'|'error'>('info');
  const [noteVisibility, setNoteVisibility] = useState<'internal'|'public'>('public');
  // 计划预览（最终计划：override/路由/默认）
  const [planPreview, setPlanPreview] = useState<any | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  // 实际包装结果（仓库回填）
  const [packResultOpen, setPackResultOpen] = useState(false);
  const [packResultSaving, setPackResultSaving] = useState(false);
  const [packResultForm] = Form.useForm();
  const [packCopyDone, setPackCopyDone] = useState(false);

  // 指令执行结果（终审附件）选择
  const [execResultSelectedKeys, setExecResultSelectedKeys] = useState<string[]>([]);
  const [execResultIocRows, setExecResultIocRows] = useState<any[]>([]);
  const [execResultIocLoading, setExecResultIocLoading] = useState(false);
  const [execResultIocHierarchyByCode, setExecResultIocHierarchyByCode] = useState<{
    parentByCode: Record<string, string>;
    childByParentCode: Record<string, string[]>;
  }>({ parentByCode: {}, childByParentCode: {} });

  // 附件在线预览（CSV/XLSX 表格、PDF iframe、图片 img）
  const [attPreviewOpen, setAttPreviewOpen] = useState(false);
  const [attPvLoading, setAttPvLoading] = useState(false);
  const [attPvKind, setAttPvKind] = useState<'csv' | 'xlsx' | 'pdf' | 'image' | 'video' | 'other'>('other');
  // 视频预览：下载按钮用的文件名
  const [attPvVideoName, setAttPvVideoName] = useState<string>('');
  const [attPvVideoDownloading, setAttPvVideoDownloading] = useState(false);
  const [attPvCols, setAttPvCols] = useState<any[]>([]);
  const [attPvData, setAttPvData] = useState<any[]>([]);
  const [attPvSheetName, setAttPvSheetName] = useState<string>('');
  const [attPvSheetNames, setAttPvSheetNames] = useState<string[]>([]);
  const [attPvActiveSheet, setAttPvActiveSheet] = useState<string>('');
  const attPvXlsxWbRef = useRef<any>(null);
  const attPvXlsxLibRef = useRef<any>(null);
  const attPvXlsxCacheRef = useRef<Record<string, { cols: any[]; data: any[] }>>({});
  const [attPvUrl, setAttPvUrl] = useState<string>('');
  const [attPvError, setAttPvError] = useState<string>('');
  const [attPvPageSize, setAttPvPageSize] = useState<number>(20);
  const [attPvTitle, setAttPvTitle] = useState<string>('');
  // 压缩包（仅前端在线解压预览，不改后端附件）
  const [attPvArchList, setAttPvArchList] = useState<Array<{ path: string; dir: boolean; size: number; }>>([]);
  const [attPvArchiveRoot, setAttPvArchiveRoot] = useState<string>('');
  const [attPvEntryName, setAttPvEntryName] = useState<string>('');
  // 压缩包条目列表分页（受控）
  const [attPvArchPage, setAttPvArchPage] = useState<number>(1);
  const [attPvArchPageSize, setAttPvArchPageSize] = useState<number>(20);
  // 通用归档：类型与缓冲
  const [attPvArchiveKind, setAttPvArchiveKind] = useState<string>('');
  const [attPvArchiveBuf, setAttPvArchiveBuf] = useState<ArrayBuffer | null>(null);
  const [attPvArchiveAttachId, setAttPvArchiveAttachId] = useState<number | null>(null);
  // 动态 import 助手
  const dynamicImport = async (moduleName: string): Promise<any> => {
    // eslint-disable-next-line no-new-func
    const importer = new Function('m', 'return import(m);') as any;
    return importer(moduleName);
  };
  // 管理端/员工端判定（客户端隐藏部分卡片）
  const isAdminOrStaff = useMemo(() => {
    try {
      const path = String((window as any)?.location?.pathname || '');
      return path.startsWith('/dashboard/admin') || path.startsWith('/dashboard/staff');
    } catch { return false; }
  }, []);
  // 仅管理员端（指令计费汇总仅管理员可见）
  const isAdminPath = useMemo(() => {
    try {
      return String((window as any)?.location?.pathname || '').startsWith('/dashboard/admin');
    } catch { return false; }
  }, []);

  useEffect(() => {
    // 指令切换时清空“执行结果附件”的选中状态
    setExecResultSelectedKeys([]);
    setExecResultIocHierarchyByCode({ parentByCode: {}, childByParentCode: {} });
  }, [instructionId]);

  const downloadExecResultIocList = useCallback(async () => {
    try {
      const sourceRows = (Array.isArray(execResultIocRows) ? [...execResultIocRows] : []).filter((r: any) => {
        if (isAdminOrStaff) return true;
        return String(r?.output_content_kind || '').toLowerCase() !== 'exception';
      });
      const parentByCode = execResultIocHierarchyByCode?.parentByCode || {};
      const childByParentCode = execResultIocHierarchyByCode?.childByParentCode || {};
      const parseCodeSortMeta = (row: any) => {
        const code = String(row?.output_code || row?.display_code || '').trim().toUpperCase();
        if (!code) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
        const parts = code.split('-');
        if (parts.length < 2) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
        const groupRaw = parts[parts.length - 2];
        const tailRaw = parts[parts.length - 1];
        const groupNum = Number(groupRaw);
        const tailNum = Number(tailRaw);
        return {
          groupNum: Number.isFinite(groupNum) ? groupNum : -1,
          tailNum: Number.isFinite(tailNum) ? tailNum : Number.MAX_SAFE_INTEGER,
        };
      };
      const getRoleByCode = (code: string): 'parent' | 'child' | 'standalone' => {
        const parentCode = String(parentByCode?.[code] || '').trim();
        const childCodes = Array.isArray(childByParentCode?.[code]) ? childByParentCode[code] : [];
        return parentCode ? 'child' : (childCodes.length > 0 ? 'parent' : 'standalone');
      };
      sourceRows.sort((a: any, b: any) => {
        const ca = String(a?.output_code || a?.display_code || '').trim();
        const cb = String(b?.output_code || b?.display_code || '').trim();
        const ra = getRoleByCode(ca);
        const rb = getRoleByCode(cb);
        const rootA = String(parentByCode?.[ca] || ca).trim();
        const rootB = String(parentByCode?.[cb] || cb).trim();
        const ga = parseCodeSortMeta({ output_code: rootA });
        const gb = parseCodeSortMeta({ output_code: rootB });
        if (ga.groupNum !== gb.groupNum) return gb.groupNum - ga.groupNum;
        if (ga.tailNum !== gb.tailNum) return ga.tailNum - gb.tailNum;
        const groupKeyA = ra === 'standalone' ? `self::${ca}` : `hier::${rootA}`;
        const groupKeyB = rb === 'standalone' ? `self::${cb}` : `hier::${rootB}`;
        if (groupKeyA !== groupKeyB) return groupKeyA.localeCompare(groupKeyB);
        const roleOrderA = ra === 'parent' ? 0 : (ra === 'child' ? 1 : 0);
        const roleOrderB = rb === 'parent' ? 0 : (rb === 'child' ? 1 : 0);
        if (roleOrderA !== roleOrderB) return roleOrderA - roleOrderB;
        const sa = parseCodeSortMeta({ output_code: ca });
        const sb = parseCodeSortMeta({ output_code: cb });
        if (sa.tailNum !== sb.tailNum) return sa.tailNum - sb.tailNum;
        return ca.localeCompare(cb);
      });
      const rows = sourceRows;
      if (rows.length === 0) {
        message.warning('暂无可下载的IOC数据');
        return;
      }
      const ExcelJSImport = await import('exceljs');
      const ExcelJS: any = (ExcelJSImport as any)?.default || ExcelJSImport;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('指令产出容器IOC');
      const headers = [
        '容器系统编码',
        '容器类型',
        '层级标识',
        '内容类型',
        '容器内容统计',
        '容器内容',
        '容器尺寸',
        '容器重量',
        '最新占用状态',
        '占用指令',
      ];
      ws.columns = [
        { header: headers[0], key: 'output_code', width: 28 },
        { header: headers[1], key: 'container_type', width: 14 },
        { header: headers[2], key: 'hierarchy_role', width: 14 },
        { header: headers[3], key: 'content_kind', width: 14 },
        { header: headers[4], key: 'content_summary', width: 16 },
        { header: headers[5], key: 'content_detail', width: 60 },
        { header: headers[6], key: 'dims', width: 24 },
        { header: headers[7], key: 'weight', width: 14 },
        { header: headers[8], key: 'occupy_status', width: 16 },
        { header: headers[9], key: 'occupy_instruction', width: 24 },
      ];
      const instructionCode = String(detail?.system_code || instructionId || 'instruction').trim();
      ws.spliceRows(1, 1, [`指令系统编码: ${instructionCode}`]);
      ws.mergeCells(1, 1, 1, headers.length);
      const titleCell = ws.getCell(1, 1);
      titleCell.font = { bold: true, color: { argb: 'FF1677FF' } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(1).height = 24;

      ws.getRow(2).height = 22;
      const headerRow = ws.getRow(2);
      headerRow.values = headers;
      const borderStyle = {
        top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
      } as any;
      headerRow.eachCell((cell: any) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = borderStyle;
      });

      rows.forEach((r: any) => {
        const code = String(r?.output_code || r?.display_code || '');
        const dims = (r?.length_cm != null && r?.width_cm != null && r?.height_cm != null)
          ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm`
          : '';
        const weight = r?.weight_g != null ? `${r.weight_g}g` : '';
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const qty = Number(r?.output_contents_total_qty || 0);
        const summaryText = kind === 'exception'
          ? `${Number.isFinite(qty) ? qty : 0}件`
          : String(r?.output_contents_summary || '');
        const detailText = kind === 'exception'
          ? '无法操作等异常产品'
          : String(r?.output_contents_detail || '');
        const detailTextForCsv = kind === 'exception'
          ? detailText
          : detailText
              .split(/[；;]/)
              .map((x: string) => String(x || '').trim())
              .filter(Boolean)
              .join('\n');
        const s = String(r?.latest_occupy_status || '').trim().toLowerCase();
        const rawStatusText = String(r?.latest_occupy_status_display || '').trim();
        const isOccupied = ['active', 'occupied', 'occupying', 'hold', 'holding'].includes(s);
        const isConsumed = s.includes('consumed') || rawStatusText.includes('已消耗');
        const occupyStatus = isOccupied
          ? (rawStatusText || '占用中')
          : (isConsumed ? '已消耗' : '未占用');
        const relatedInstr = isConsumed
          ? String(r?.consuming_instruction_code || (Number(r?.consuming_instruction_id || 0) > 0 ? `#${Number(r.consuming_instruction_id)}` : ''))
          : String(r?.occupying_instruction_code || (Number(r?.occupying_instruction_id || 0) > 0 ? `#${Number(r.occupying_instruction_id)}` : ''));
        const codeForRole = String(r?.output_code || r?.display_code || '').trim();
        const role = getRoleByCode(codeForRole);
        const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
        const row = ws.addRow([
          code,
          String(r?.container_type_display || ''),
          roleText,
          String(r?.output_content_kind_display || ''),
          summaryText,
          detailTextForCsv,
          dims,
          weight,
          occupyStatus,
          relatedInstr,
        ]);
        row.height = 22;
        row.eachCell((cell: any) => {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          cell.border = borderStyle;
        });
      });

      // 兜底：确保工作表已用范围内所有单元格均有边框与居中样式
      const lastRow = ws.rowCount;
      const lastCol = headers.length;
      for (let r = 1; r <= lastRow; r += 1) {
        for (let c = 1; c <= lastCol; c += 1) {
          const cell = ws.getCell(r, c);
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          cell.border = borderStyle;
        }
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const dateText = `${yyyy}-${mm}-${dd}`;
      a.href = href;
      a.download = `指令${instructionCode}_产出容器IOC完整列表_${dateText}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => { try { URL.revokeObjectURL(href); } catch {} }, 1000);
      message.success('IOC列表已开始下载');
    } catch {
      message.error('下载失败');
    }
  }, [execResultIocRows, execResultIocHierarchyByCode, detail?.system_code, instructionId, isAdminOrStaff]);

  useEffect(() => {
    if (!instructionId || !Number.isFinite(instructionId)) {
      setExecResultIocRows([]);
      setExecResultIocLoading(false);
      return;
    }
    let cancelled = false;
    const statusTextMap: Record<string, string> = {
      active: '可用',
      closed: '已关闭',
      archived: '已归档',
      consumed: '已消耗',
      cancelled: '已作废',
      lost: '丢失',
    };
    const containerTypeTextMap: Record<string, string> = {
      pallet: '托盘',
      box: '箱子',
      shipping_bag: '物流袋',
      tote_bag: '仓库周转袋',
      direct: '直接存储',
      cart: '移动车',
      other: '其他',
    };
    const loadExecResultIocs = async () => {
      try {
        setExecResultIocLoading(true);
        const isRelabelInstruction = (() => {
          const code = String(detail?.template_snapshot?.code || detail?.template_detail?.code || '').trim().toUpperCase();
          const name = String(detail?.template_snapshot?.name || detail?.template_detail?.name || '').trim();
          if (code.includes('RELABEL_PACK')) return true;
          return name.includes('换标') && name.includes('装箱');
        })();
        // 先取该指令“全量产出IOC”（不区分是否被占用）
        const allResp = await request.get(`/api/v1/instruction/instructions/${instructionId}/ioc/with-contents/`);
        if (cancelled) return;
        const allRows = Array.isArray(allResp?.data?.results) ? allResp.data.results : [];
        const normalizeCode = (v: any) => String(v || '').trim().toUpperCase();
        const formatIuInnerDetail = (items: Array<{ barcode: string; qty: number }>) => {
          const arr = Array.isArray(items) ? items : [];
          return arr
            .map((x: any) => {
              const bc = String(x?.barcode || '').trim();
              const q = Number(x?.qty || 0) || 0;
              return bc ? `${bc}×${q}` : '';
            })
            .filter(Boolean)
            .join(' | ');
        };
        const buildItemsFromIuDetail = (d: any): Array<{ barcode: string; qty: number }> => {
          const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
          return (arr || []).map((it: any) => {
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '').trim()).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '').trim();
            }
            return { barcode, qty };
          }).filter((x: any) => x?.barcode || x?.qty);
        };
        const iuInnerById = new Map<string, string>();
        const missingIuIds = new Set<string>();
        (allRows || []).forEach((oc: any) => {
          const contents = Array.isArray(oc?.contents) ? oc.contents : [];
          (contents || []).forEach((c: any) => {
            const idStr = String(c?.inventory_unit_id || '').trim();
            if (!idStr) return;
            const cachedItems = unitContentCache[idStr];
            if (Array.isArray(cachedItems) && cachedItems.length > 0) {
              iuInnerById.set(idStr, formatIuInnerDetail(cachedItems));
            } else {
              missingIuIds.add(idStr);
            }
          });
        });
        if (missingIuIds.size > 0) {
          const cacheUpdates: Record<string, Array<{ barcode: string; qty: number }>> = {};
          await Promise.all(Array.from(missingIuIds).map(async (idStr: string) => {
            try {
              const resp = await getInventoryUnitDetail(idStr);
              const d: any = (resp as any)?.data || resp;
              const items = buildItemsFromIuDetail(d);
              if (items.length > 0) {
                cacheUpdates[idStr] = items;
                iuInnerById.set(idStr, formatIuInnerDetail(items));
              } else {
                iuInnerById.set(idStr, '');
              }
            } catch {
              iuInnerById.set(idStr, '');
            }
          }));
          if (!cancelled && Object.keys(cacheUpdates).length > 0) {
            setUnitContentCache((prev) => ({ ...prev, ...cacheUpdates }));
          }
        }
        let mapped = (allRows || []).map((oc: any, idx: number) => {
          const contents = Array.isArray(oc?.contents) ? oc.contents : [];
          const totalQty = (contents || []).reduce((acc: number, c: any) => acc + (Number(c?.quantity || 0) || 0), 0);
          const detailText = (contents || []).map((c: any) => {
            const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
            const qty = Number(c?.quantity || 0) || 0;
            if (!code) return '';
            const iuId = String(c?.inventory_unit_id || '').trim();
            const inner = iuId ? String(iuInnerById.get(iuId) || '').trim() : '';
            return inner ? `${code}(${inner}) × ${qty}件` : `${code}×${qty}`;
          }).filter(Boolean).join('；');
          const kind = String(oc?.content_kind || '').trim().toLowerCase();
          const rawStatus = String(oc?.status || '').trim().toLowerCase();
          const containerTypeCode = String(oc?.container_type || '').trim().toLowerCase();
          return {
            __key: `exec-ioc-${String(oc?.id || idx)}`,
            output_container_id: oc?.id,
            instruction_id: Number(instructionId) || undefined,
            output_code: String(oc?.code || '-'),
            display_code: String(oc?.code || '-'),
            container_type: containerTypeCode,
            container_type_display: String(containerTypeTextMap[containerTypeCode] || oc?.container_type || '-'),
            output_content_kind: kind,
            output_content_kind_display: kind === 'exception' ? '异常内容容器' : (kind ? '正常内容容器' : '-'),
            output_contents_summary: `${contents.length}项${Math.max(0, Number(totalQty || 0))}件`,
            output_contents_total_qty: Number(totalQty || 0) || 0,
            output_contents_detail: detailText,
            length_cm: oc?.length_cm,
            width_cm: oc?.width_cm,
            height_cm: oc?.height_cm,
            weight_g: oc?.weight_g,
            lifecycle_status: rawStatus,
            latest_occupy_status: rawStatus,
            latest_occupy_status_display: String(statusTextMap[rawStatus] || rawStatus || '-'),
            occupying_instruction_id: undefined,
            occupying_instruction_code: undefined,
            consuming_instruction_id: undefined,
            consuming_instruction_code: undefined,
          };
        });

        // 再用 unified 补齐展示字段（换标IOC内容、占用状态等）；未命中的行保留全量结果
        try {
          const unifiedResp = await request.get('/api/v1/storage/containers/unified/', {
            params: {
              page: 1,
              page_size: 500,
              container_kind: 'output',
              instruction_id: Number(instructionId),
              ordering: '-created_at',
            },
          });
          if (!cancelled) {
            const unifiedRows = Array.isArray(unifiedResp?.data?.results) ? unifiedResp.data.results : [];
            const byId = new Map<string, any>();
            const byCode = new Map<string, any>();
            (unifiedRows || []).forEach((r: any) => {
              const idKey = String(r?.id || '').trim();
              const codeKey = normalizeCode(r?.system_code || r?.display_code || '');
              if (idKey) byId.set(idKey, r);
              if (codeKey) byCode.set(codeKey, r);
            });
            mapped = (mapped || []).map((row: any) => {
              const hit = byId.get(String(row?.output_container_id || '')) || byCode.get(normalizeCode(row?.output_code || row?.display_code || ''));
              if (!hit) return row;
              const lifecycleStatus = String(hit?.status || row?.lifecycle_status || row?.latest_occupy_status || '').trim().toLowerCase();
              const rawStatus = String(hit?.latest_reservation_status || hit?.occupy_status || lifecycleStatus || row?.latest_occupy_status || '').trim().toLowerCase();
              const finalStatus = lifecycleStatus.includes('consumed') ? 'consumed' : rawStatus;
              const occupyingInstructionId = Number(
                hit?.latest_reservation_instruction_id
                || hit?.occupying_instruction_id
                || hit?.reservation_instruction_id
                || 0
              );
              const occupyingInstructionCode = String(
                hit?.latest_reservation_instruction_code
                || hit?.occupying_instruction_code
                || hit?.reservation_instruction_code
                || ''
              ).trim();
              return {
                ...row,
                output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '-'),
                display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '-'),
                container_type_display: String(hit?.container_type_display || row?.container_type_display || '-'),
                output_content_kind: String(hit?.output_content_kind || row?.output_content_kind || '').trim().toLowerCase(),
                output_content_kind_display: String(hit?.output_content_kind_display || row?.output_content_kind_display || '-'),
                output_contents_summary: String(hit?.output_contents_summary || row?.output_contents_summary || '-'),
                output_contents_total_qty: Number(hit?.output_contents_total_qty ?? row?.output_contents_total_qty ?? 0) || 0,
                // 换标指令优先展示 unified 文案（new barcode）；其余指令保持原优先级
                output_contents_detail: isRelabelInstruction
                  ? String(hit?.output_contents_detail || row?.output_contents_detail || '')
                  : String(row?.output_contents_detail || hit?.output_contents_detail || ''),
                lifecycle_status: lifecycleStatus || row?.lifecycle_status,
                latest_occupy_status: finalStatus,
                latest_occupy_status_display: String(
                  finalStatus === 'consumed'
                    ? '已消耗'
                    : (hit?.latest_reservation_status_display
                      || hit?.occupy_status_display
                      || statusTextMap[finalStatus]
                      || statusTextMap[String(hit?.status || '').trim().toLowerCase()]
                      || finalStatus
                      || '-')
                ),
                occupying_instruction_id: Number.isFinite(occupyingInstructionId) && occupyingInstructionId > 0 ? occupyingInstructionId : undefined,
                occupying_instruction_code: occupyingInstructionCode || undefined,
                consuming_instruction_id: Number(hit?.consuming_instruction_id || 0) > 0 ? Number(hit.consuming_instruction_id) : row?.consuming_instruction_id,
                consuming_instruction_code: String(hit?.consuming_instruction_code || '').trim() || row?.consuming_instruction_code,
              };
            });
          }
        } catch {}

        // defer 模式下不再依赖 facets 物化表；展示口径以统一容器接口返回为准（已包含 draft/lease 补齐）。

        // 仅覆盖“占用状态/占用指令”：口径来自 IOC 占用记录快照，不影响其它显示字段
        try {
          const occResp = await request.get(`/api/v1/instruction/instructions/${instructionId}/ioc/occupancy/`);
          if (!cancelled) {
            const occRows = Array.isArray(occResp?.data?.results) ? occResp.data.results : [];
            const occById = new Map<string, any>();
            const occByCode = new Map<string, any>();
            (occRows || []).forEach((x: any) => {
              const idKey = String(x?.container_id || '').trim();
              const codeKey = normalizeCode(x?.container_code || '');
              if (idKey) occById.set(idKey, x);
              if (codeKey) occByCode.set(codeKey, x);
            });
            mapped = (mapped || []).map((row: any) => {
              const hit = occById.get(String(row?.output_container_id || '')) || occByCode.get(normalizeCode(row?.output_code || row?.display_code || ''));
              if (!hit) return row;
              const s = String(hit?.latest_reservation_status || '').trim().toLowerCase();
              const lifecycleStatus = String(row?.lifecycle_status || '').trim().toLowerCase();
              const isLifecycleConsumed = lifecycleStatus.includes('consumed');
              const finalStatus = isLifecycleConsumed ? 'consumed' : (s || 'unoccupied');
              return {
                ...row,
                latest_occupy_status: finalStatus,
                latest_occupy_status_display: String(
                  finalStatus === 'consumed'
                    ? '已消耗'
                    : (hit?.latest_reservation_status_display || (s ? s : '未占用'))
                ),
                occupying_instruction_id: Number(hit?.occupying_instruction_id || 0) > 0 ? Number(hit.occupying_instruction_id) : undefined,
                occupying_instruction_code: String(hit?.occupying_instruction_code || '').trim() || undefined,
                consuming_instruction_id: Number(hit?.consuming_instruction_id || 0) > 0 ? Number(hit.consuming_instruction_id) : row?.consuming_instruction_id,
                consuming_instruction_code: String(hit?.consuming_instruction_code || '').trim() || row?.consuming_instruction_code,
              };
            });
          }
        } catch {}

        // 层级口径：用于“层级标识”与父子组边框展示
        try {
          const hResp = await request.get('/api/v1/storage/output-containers/', {
            params: { instruction_id: Number(instructionId) },
          });
          if (!cancelled) {
            const hRows = Array.isArray(hResp?.data?.results) ? hResp.data.results : [];
            const parentByCode: Record<string, string> = {};
            const childByParentCode: Record<string, string[]> = {};
            (hRows || []).forEach((row: any) => {
              const code = String(row?.code || '').trim();
              const parentCode = String(row?.parent_code || '').trim();
              const childCodes = Array.isArray(row?.child_codes) ? row.child_codes : [];
              if (code && parentCode) parentByCode[code] = parentCode;
              if (code && childCodes.length > 0) {
                childByParentCode[code] = childCodes
                  .map((x: any) => String(x || '').trim())
                  .filter(Boolean);
              }
            });
            setExecResultIocHierarchyByCode({ parentByCode, childByParentCode });
          }
        } catch {
          if (!cancelled) setExecResultIocHierarchyByCode({ parentByCode: {}, childByParentCode: {} });
        }

        const filteredRows = (mapped || []).filter((row: any) => {
          const lifecycleStatus = String(row?.lifecycle_status || '').trim().toLowerCase();
          return lifecycleStatus !== 'cancelled';
        });
        setExecResultIocRows(filteredRows);
      } catch {
        if (!cancelled) setExecResultIocRows([]);
      } finally {
        if (!cancelled) setExecResultIocLoading(false);
      }
    };
    void loadExecResultIocs();
    return () => { cancelled = true; };
  }, [
    instructionId,
    detail?.template_snapshot?.code,
    detail?.template_snapshot?.name,
    detail?.template_detail?.code,
    detail?.template_detail?.name,
  ]);

  useEffect(() => {
    let cancelled = false;
    const parseTemplateId = (v: any): number | undefined => {
      try {
        if (v == null || v === '') return undefined;
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return n;
        const s = String(v).trim();
        if (!s) return undefined;
        const n2 = Number(s);
        return Number.isFinite(n2) && n2 > 0 ? n2 : undefined;
      } catch {
        return undefined;
      }
    };
    const loadTemplateNames = async () => {
      try {
        const ids = new Set<number>();
        (logs || []).forEach((r: any) => {
          const evt = String(r?.event_type || '').trim();
          if (evt !== 'instruction.updated') return;
          const c = r?.context?.changes?.template;
          if (!c || typeof c !== 'object') return;
          const b = parseTemplateId(c?.before);
          const a = parseTemplateId(c?.after);
          if (b) ids.add(b);
          if (a) ids.add(a);
        });
        const missing = Array.from(ids).filter((id) => !templateNameMap[String(id)]);
        if (missing.length === 0) return;
        const next: Record<string, string> = {};
        await Promise.all(missing.map(async (id) => {
          try {
            const resp = await request.get(`/api/v1/instruction/templates/${id}/`);
            const d = resp?.data || {};
            const nm = String(d?.name || '').trim();
            if (nm) next[String(id)] = nm;
          } catch {}
        }));
        if (cancelled || Object.keys(next).length === 0) return;
        setTemplateNameMap((prev) => ({ ...prev, ...next }));
      } catch {}
    };
    void loadTemplateNames();
    return () => { cancelled = true; };
  }, [logs, templateNameMap]);

  useEffect(() => {
    let cancelled = false;
    const parseProjectId = (v: any): number | undefined => {
      try {
        if (v == null || v === '') return undefined;
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return n;
        const s = String(v).trim();
        if (!s) return undefined;
        const n2 = Number(s);
        return Number.isFinite(n2) && n2 > 0 ? n2 : undefined;
      } catch {
        return undefined;
      }
    };
    const loadProjectCodes = async () => {
      try {
        const ids = new Set<number>();
        (logs || []).forEach((r: any) => {
          const evt = String(r?.event_type || '').trim();
          if (evt !== 'instruction.updated') return;
          const c1 = r?.context?.changes?.project_id;
          if (c1 && typeof c1 === 'object') {
            const b = parseProjectId(c1?.before);
            const a = parseProjectId(c1?.after);
            if (b) ids.add(b);
            if (a) ids.add(a);
          }
          const c2 = r?.context?.changes?.project;
          if (c2 && typeof c2 === 'object') {
            const b = parseProjectId(c2?.before);
            const a = parseProjectId(c2?.after);
            if (b) ids.add(b);
            if (a) ids.add(a);
          }
        });
        const missing = Array.from(ids).filter((id) => !projectCodeMap[String(id)]);
        if (missing.length === 0) return;
        const next: Record<string, string> = {};
        await Promise.all(missing.map(async (id) => {
          try {
            const resp = await getProjectDetail(id);
            const d: any = resp?.data || {};
            const code = String(d?.project_code || d?.code || id).trim();
            if (code) next[String(id)] = code;
          } catch {}
        }));
        if (cancelled || Object.keys(next).length === 0) return;
        setProjectCodeMap((prev) => ({ ...prev, ...next }));
      } catch {}
    };
    void loadProjectCodes();
    return () => { cancelled = true; };
  }, [logs, projectCodeMap]);
  // 简易 TAR 解析与 GZIP 解包（与列表页一致）
  const parseTarEntriesFromBuffer = (buf: ArrayBuffer): Array<{ path: string; dir: boolean; size: number; offset: number }> => {
    const entries: Array<{ path: string; dir: boolean; size: number; offset: number }> = [];
    const u8 = new Uint8Array(buf);
    const textDecoder = new TextDecoder('utf-8');
    const getString = (start: number, length: number) => {
      const slice = u8.subarray(start, start + length);
      let end = 0;
      while (end < slice.length && slice[end] !== 0) end++;
      return textDecoder.decode(slice.subarray(0, end));
    };
    const getOctal = (start: number, length: number) => {
      const str = getString(start, length).trim();
      if (!str) return 0;
      const cleaned = str.replace(/[^\d]/g, '');
      return parseInt(cleaned || '0', 8) || 0;
    };
    let i = 0;
    while (i + 512 <= u8.length) {
      let allZero = true;
      for (let k = 0; k < 512; k++) { if (u8[i + k] !== 0) { allZero = false; break; } }
      if (allZero) break;
      const name = getString(i + 0, 100);
      const size = getOctal(i + 124, 12);
      const typeFlag = u8[i + 156];
      const isDir = typeFlag === 53 || name.endsWith('/');
      const dataOffset = i + 512;
      entries.push({ path: name, dir: isDir, size, offset: dataOffset });
      const dataSize = Math.ceil(size / 512) * 512;
      i = dataOffset + dataSize;
    }
    return entries;
  };
  const gunzipToArrayBuffer = async (gzBuf: ArrayBuffer): Promise<ArrayBuffer> => {
    const mod: any = await dynamicImport('pako');
    const pako = mod?.default || mod;
    const outU8: Uint8Array = pako.ungzip(new Uint8Array(gzBuf));
    const out = new ArrayBuffer(outU8.byteLength);
    new Uint8Array(out).set(outU8);
    return out;
  };

  const loadPlanPreview = async () => {
    if (!instructionId) return;
    setPlanLoading(true);
    try {
      const resp = await request.post(`/api/v1/instruction/instructions/${instructionId}/plan/preview/`, {});
      setPlanPreview(resp?.data || null);
    } catch {
      setPlanPreview(null);
    } finally {
      setPlanLoading(false);
    }
  };

  const closeAttPreview = () => {
    try {
      if (attPvUrl && attPvUrl.startsWith('blob:')) {
        URL.revokeObjectURL(attPvUrl);
      }
    } catch {}
    setAttPreviewOpen(false);
    setAttPvUrl('');
  };

  const getExtLower = (att: any): string => {
    try {
      const name = String(att?.file_name || att?.file || att?.file_path || '');
      const ext = name.includes('.') ? name.split('.').pop() : '';
      if (ext) return String(ext).toLowerCase();
      const mime = String(att?.mime_type || '').toLowerCase();
      if (mime.includes('pdf')) return 'pdf';
      if (mime.includes('csv')) return 'csv';
      if (mime.includes('excel') || mime.includes('sheet')) return 'xlsx';
      return '';
    } catch { return ''; }
  };
  const getBaseName = (pathOrUrl?: string) => {
    try {
      const s = String(pathOrUrl || '');
      const q = s.split('?')[0];
      const hash = q.split('#')[0];
      const parts = hash.split('/');
      const last = parts[parts.length - 1] || s;
      try { return decodeURIComponent(last); } catch { return last; }
    } catch { return String(pathOrUrl || ''); }
  };
  const prettifyStorageName = (name?: string) => {
    const s = String(name || '');
    // 形如: 名称_随机后缀.xlsx → 去掉 _随机后缀
    const m = s.match(/^(.+)_([A-Za-z0-9]{5,12})\.([A-Za-z0-9]+)$/);
    if (m && m[1] && m[3]) return `${m[1]}.${m[3]}`;
    return s;
  };
  const getDisplayFileName = (row: any) => {
    const v = row?.file_name;
    if (v) return v;
    const metaName = row?.meta?.original_name || row?.meta?.original_file_name || row?.meta?.originalFilename;
    if (metaName) return String(metaName);
    const base = getBaseName(row?.file) || getBaseName(row?.file_path);
    return prettifyStorageName(base);
  };

  const COL1_WIDTH = 280; // 名称/问题/附件名称
  const COL2_WIDTH = 420; // 编码/答案/文件
  const COL3_WIDTH = 200; // 按钮

  const Star: React.FC = () => (<span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>);
  const resolveAttachmentUrl = (att: any): string | undefined => {
    try {
      const raw = att?.file_url || att?.file || att?.file_path || '';
      if (!raw) return undefined;
      const url = getAttachmentUrl(att);
      return url || undefined;
    } catch {
      return undefined;
    }
  };
  const handlePreviewDownload = (att: any) => {
    try {
      const url = resolveAttachmentUrl(att);
      if (!url) { message.warning('无可用文件'); return; }
      try {
        const raw = att?.file || att?.file_path || att?.file_name || '';
        console.debug('[PreviewDownload] raw=', raw, 'resolved=', url);
      } catch {}
      const extLower = String(getExtLower(att) || '').trim().toLowerCase();
      const isImg = ['png','jpg','jpeg','gif','webp'].includes(extLower);
      const isPdf = extLower === 'pdf';
      const fileName = String(att?.file_name || '').trim() || getBaseName(url) || 'download';

      // 图片/PDF：强制 blob 下载，避免被浏览器当作“打开新窗口预览”
      if (isImg || isPdf) {
        (async () => {
          try {
            const resp = await fetch(url, { credentials: 'include' as any });
            if (!resp.ok) throw new Error('download failed');
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch {} }, 1500);
          } catch {
            message.error('下载失败');
          }
        })();
        return;
      }

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      if (fileName) anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch {
      message.error('无法预览/下载该文件');
    }
  };
  // 在线预览实现：CSV/XLSX 解析为表格，PDF 使用 iframe
  const previewAttachment = async (att: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const ext = getExtLower(att);
    const extLower = ext.toLowerCase();

    setAttPvError('');
    setAttPvSheetName('');
    setAttPvSheetNames([]);
    setAttPvActiveSheet('');
    attPvXlsxWbRef.current = null;
    attPvXlsxLibRef.current = null;
    attPvXlsxCacheRef.current = {};
    setAttPvCols([]);
    setAttPvData([]);
    setAttPvUrl('');
    setAttPvPageSize(20);
    // 打开预览时重置归档列表分页
    setAttPvArchPage(1);
    setAttPvArchPageSize(20);
    setAttPreviewOpen(true);
    setAttPvLoading(true);
    try {
      const disp = getDisplayFileName(att) || (att?.file_name ? prettifyStorageName(att.file_name) : '') || getBaseName(url);
      setAttPvTitle(String(disp || '附件预览'));
    } catch { setAttPvTitle('附件预览'); }

    try {
      // 视频：不下载成 blob（最大 100MB，要边下边播），直接把地址交给 <video>，在弹窗里播放
      if (['mp4', 'mov', 'webm'].includes(extLower)) {
        setAttPvKind('video');
        setAttPvVideoName(String(getDisplayFileName(att) || att?.file_name || getBaseName(url) || `video.${extLower}`));
        setAttPvUrl(url);
        return;
      }
      if (extLower === 'pdf') {
        setAttPvKind('pdf');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setAttPvUrl(objectUrl);
        return;
      }
      // 图片：拉取为 blob 再预览（避免跨端口/鉴权导致 img 直链失败）
      if (['png','jpg','jpeg','gif','webp'].includes(extLower)) {
        setAttPvKind('image');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('图片加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setAttPvUrl(objectUrl);
        return;
      }
      // 归档类型集合
      const isArchive = (()=>{
        const s = new Set(['zip','rar','7z','tar','tgz','tar.gz','tbz2','tar.bz2','txz','tar.xz']);
        return s.has(extLower);
      })();
      if (isArchive) {
        setAttPvKind('other');
        // RAR 走后端列表接口，其余保持前端解析
        if (extLower === 'rar') {
          try {
            const id = Number(att?.id);
            if (!Number.isFinite(id)) throw new Error('缺少附件ID');
            const resp = await request.get(`/api/v1/instruction/attachments/${id}/archive/list/`);
            const list = Array.isArray(resp?.data) ? resp.data : [];
            setAttPvArchList(list);
            setAttPvArchiveAttachId(id);
            setAttPvArchiveKind('rar');
            setAttPvArchiveBuf(null);
            const root = 'arc:' + String(id);
            setAttPvArchiveRoot(root);
            setAttPvEntryName('');
            setAttPvUrl(root);
            setAttPvArchPage(1);
            return;
          } catch (e:any) {
            setAttPvError(e?.response?.data?.detail || e?.message || 'RAR 解析失败');
            return;
          }
        }
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('压缩包加载失败');
        const buf = await resp.arrayBuffer();
        const list: Array<{ path: string; dir: boolean; size: number; }> = [];
        const finish = (entries: typeof list, kind: string) => {
          const sorted = [...entries].sort((a,b)=> (a.dir === b.dir ? a.path.localeCompare(b.path) : (a.dir ? -1 : 1)));
          setAttPvArchList(sorted);
          setAttPvKind('other');
          const root = 'arc:' + url;
          setAttPvArchiveRoot(root);
          setAttPvArchiveKind(kind);
          setAttPvArchiveBuf(buf);
          setAttPvEntryName('');
          setAttPvUrl(root);
          setAttPvArchPage(1);
        };
        if (extLower === 'zip') {
          let JSZip: any = null;
          try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
          if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
          const zip = await JSZip.loadAsync(buf);
          zip.forEach((relPath: string, entry: any) => {
            list.push({ path: relPath, dir: !!entry.dir, size: Number(entry?._dataUncompressedSize || entry?.uncompressedSize || 0) || 0 });
          });
          finish(list, 'zip');
          return;
        }
        if (extLower === 'tar') {
          try {
            const entries = parseTarEntriesFromBuffer(buf);
            finish(entries.map(e => ({ path: e.path, dir: e.dir, size: e.size })), 'tar');
            return;
          } catch {
            setAttPvError('TAR 解析失败'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        if (extLower === 'tgz' || extLower === 'tar.gz') {
          try {
            const tarBuf = await gunzipToArrayBuffer(buf) as ArrayBuffer;
            const entries = parseTarEntriesFromBuffer(tarBuf as ArrayBuffer);
            setAttPvArchiveBuf(tarBuf as ArrayBuffer);
            finish(entries.map(e => ({ path: e.path, dir: e.dir, size: e.size })), 'tar');
            return;
          } catch (e:any) {
            setAttPvError(e?.message || 'TGZ 解压失败'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        if (['7z','tbz2','tar.bz2','txz','tar.xz'].includes(extLower)) {
          setAttPvError('该压缩格式在线解压暂不可用，请下载查看'); setAttPvKind('other'); setAttPvUrl(url); return;
        }
      }
      if (extLower === 'csv') {
        setAttPvKind('csv');
        let Papa: any = null;
        try {
          Papa = (await import('papaparse') as any)?.default || (await import('papaparse') as any);
        } catch {}
        if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
        const resp = await fetch(url, { credentials: 'include' as any });
        const text = await resp.text();
        const parsed = Papa.parse(text, { skipEmptyLines: true });
        const rows: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
        const arr = rows.filter((r:any) => Array.isArray(r) && r.length > 0);
        if (arr.length === 0) { setAttPvError('文件为空'); return; }
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h:any, i:number) => ({
          title: String(h || `列${i+1}`),
          dataIndex: `c${i}`,
          align: 'center',
          width: undefined,
        }));
        const data = dataRows.map((row:any[], idx:number) => {
          const obj: any = { __idx: idx + 1 };
          cols.forEach((c:any, i:number) => { obj[c.dataIndex] = String(row?.[i] ?? ''); });
          return obj;
        });
        setAttPvCols(cols);
        setAttPvData(data);
        return;
      }
      if (extLower === 'xlsx' || extLower === 'xls') {
        setAttPvKind('xlsx');
        let XLSX: any = null;
        try {
          XLSX = await import('xlsx') as any;
        } catch {}
        if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
        const resp = await fetch(url, { credentials: 'include' as any });
        const buf = await resp.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetNames = Array.isArray(wb?.SheetNames) ? (wb.SheetNames as string[]) : [];
        const sheetName = String(sheetNames?.[0] || '').trim();
        const sh = sheetName ? wb.Sheets?.[sheetName] : null;
        if (!sheetName || !sh) { setAttPvError('未找到工作表'); return; }
        setAttPvSheetNames(sheetNames);
        setAttPvActiveSheet(sheetName);
        setAttPvSheetName(String(sheetName));
        attPvXlsxWbRef.current = wb;
        attPvXlsxLibRef.current = XLSX;
        attPvXlsxCacheRef.current = {};
        const aoa: any[] = XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false });
        const arr = Array.isArray(aoa) ? aoa.filter((r:any)=> Array.isArray(r) && r.length>0) : [];
        if (arr.length === 0) { setAttPvError('工作表为空'); return; }
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h:any, i:number) => ({
          title: String(h || `列${i+1}`),
          dataIndex: `c${i}`,
          align: 'center',
          width: undefined,
        }));
        const data = dataRows.map((row:any[], idx:number) => {
          const obj: any = { __idx: idx + 1 };
          cols.forEach((c:any, i:number) => { obj[c.dataIndex] = String(row?.[i] ?? ''); });
          return obj;
        });
        attPvXlsxCacheRef.current[sheetName] = { cols, data };
        setAttPvCols(cols);
        setAttPvData(data);
        return;
      }
      // 其他格式：提示并提供原文件打开
      setAttPvKind('other');
      setAttPvUrl(url);
    } catch (e:any) {
      console.error('[AttachmentPreview] error:', e);
      setAttPvError(String(e?.message || '预览失败'));
    } finally {
      setAttPvLoading(false);
    }
  };

  const lazyLoadJSZip = async (): Promise<any> => {
    try {
      // 尝试 ESM 动态导入
      // @ts-ignore
      const mod = await import('jszip');
      return (mod as any).default || mod;
    } catch {
      // 尝试通过 CDN 注入全局 JSZip
      if ((window as any).JSZip) return (window as any).JSZip;
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('JSZip加载失败'));
        document.body.appendChild(s);
      });
      return (window as any).JSZip;
    }
  };

  const handleBatchDownloadAll = async () => {
    try {
      const isInitialPhaseAttachment = (a: any): boolean => {
        const meta = (a?.meta && typeof a.meta === 'object') ? a.meta : {};
        const phase = String((meta as any)?.source_phase || (meta as any)?.phase || '').trim().toLowerCase();
        if (phase) return phase === 'initial';
        const from = String((meta as any)?.from || '').trim().toLowerCase();
        if (['step_attachment', 'stage_submission', 'ioc_with_contents', 'baseline_file', 'final_review'].includes(from)) return false;
        const label = String(a?.label || '').trim().toLowerCase();
        if (label === 'final_review') return false;
        return true;
      };
      const rawList = (Array.isArray(detail?.attachments_detail) && detail.attachments_detail.length > 0)
        ? detail.attachments_detail
        : (attachments || []);
      const initialOnlyList = Array.isArray(rawList) ? rawList.filter((a: any) => isInitialPhaseAttachment(a)) : [];
      const normalizeWaybillSourceForFilter = (raw: any): string => {
        const s = String(raw || '').trim();
        if (!s) return '';
        if (s === 'warehouse' || s === 'upload') return s;
        if (s === '仓库提供') return 'warehouse';
        if (s === '上传运单') return 'upload';
        return s;
      };
      const waybillLabelSet = new Set(['物流运单', '运输标签(物流运单/箱唛/托盘标)']);
      const ans = (detail?.customer_answers || {}) as any;
      const isWarehouseWaybill = normalizeWaybillSourceForFilter(ans?.waybill_source) === 'warehouse';
      const shouldHideWarehouseWaybill = !isAdminOrStaff && isWarehouseWaybill;
      const list = shouldHideWarehouseWaybill
        ? initialOnlyList.filter((a: any) => !waybillLabelSet.has(String(a?.label || '').trim()))
        : initialOnlyList;
      if (!list || list.length === 0) { message.warning('没有可下载的附件'); return; }
      const JSZip = await lazyLoadJSZip();
      const zip = new JSZip();
      const addedNames = new Set<string>();
      for (let i = 0; i < list.length; i++) {
        const a = list[i];
        const url = resolveAttachmentUrl(a);
        if (!url) continue;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) continue;
        const blob = await resp.blob();
        let name = String(a.file_name || a.file || a.file_path || `附件_${i+1}`);
        name = name.split('/')?.pop() || name;
        if (addedNames.has(name)) {
          const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
          const base = ext ? name.slice(0, -ext.length) : name;
          let idx = 2; let candidate = `${base}(${idx})${ext}`;
          while (addedNames.has(candidate)) { idx++; candidate = `${base}(${idx})${ext}`; }
          name = candidate;
        }
        addedNames.add(name);
        zip.file(name, blob);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      const code = detail?.system_code || `instruction-${instructionId}`;
      a.href = url;
      a.download = `${code}-attachments.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('批量下载开始');
    } catch (e) {
      console.error(e);
      message.error('批量下载失败');
    }
  };
  const rowBoxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #eaeaea',
    borderRadius: 4,
    overflow: 'hidden',
  };
  const col1BoxStyle: React.CSSProperties = {
    width: COL1_WIDTH,
    padding: '6px 8px',
    background: '#fafafa',
    borderRight: '1px solid #f0f0f0',
  };
  const col2BoxStyle: React.CSSProperties = {
    width: COL2_WIDTH,
    padding: '6px 8px',
    background: '#ffffff',
    borderRight: '1px solid #f0f0f0',
  };
  const col3BoxStyle: React.CSSProperties = {
    width: COL3_WIDTH,
    padding: '6px 0',
    background: '#fafafa',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  };
  // Tabs 标题更新（在 TabsProvider 环境下可用）
  const tabsCtx: any = useTabs();

  const refresh = useCallback(async () => {
    if (!instructionId) return;
    setLoading(true);
    try {
      const ins = await request.get(`/api/v1/instruction/instructions/${instructionId}/`);
      const d = ins?.data || null;
      // 补充仓库可读信息
      if (d && !d.warehouse_detail && !d.warehouse && d.warehouse_id) {
        try {
          const wh = await request.get(`/api/v1/warehouse/warehouses/${d.warehouse_id}/`);
          if (wh?.data) {
            d.warehouse = wh.data;
          }
        } catch {}
      }
      setDetail(d);
      // 计费汇总（仅管理员端）
      try {
        if (!isAdminPath) throw new Error('skip');
        const sum = await request.get(`/api/v1/instruction/instructions/${instructionId}/billing/summary/`);
        setBillingSummary(sum?.data || null);
      } catch {
        setBillingSummary(null);
      }
      const att = await request.get('/api/v1/instruction/attachments/', { params: { instruction: instructionId, page_size: 200 } });
      setAttachments(Array.isArray(att?.data?.results) ? att.data.results : (att?.data || []));
      // 设置标签标题缓存，供 TabsManager 读取
      try {
        const basePath = (window as any)?.location?.pathname || '';
        const code = d?.system_code || String(instructionId);
        if (basePath && code) {
          sessionStorage.setItem(`tab_title_instruction:${basePath}`, code);
          window.dispatchEvent(new CustomEvent('dashboard-tab-title-updated', { detail: { path: basePath } }));
        }
      } catch {}
      // 加载指令下的工单列表（用于 DAG 展示）
      try {
        // 同时携带 instruction 与 instruction_id，兼容不同后端实现；客户端再严格过滤
        const resp = await request.get('/api/v1/workorders/workorders/', { params: { instruction: instructionId, instruction_id: instructionId, page_size: 200 } });
        const raw = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const extractInstructionId = (w:any): number => {
          try {
            // 1) 明确的 instruction_id 字段（数值或数字字符串）
            if (w?.instruction_id != null) {
              const n = Number(w.instruction_id);
              if (Number.isFinite(n)) return n;
            }
            // 2) instruction 为数值
            if (typeof w?.instruction === 'number') {
              const n = Number(w.instruction);
              if (Number.isFinite(n)) return n;
            }
            // 3) instruction 为数字字符串或超链接字符串（提取末尾数字）
            if (typeof w?.instruction === 'string') {
              const s = String(w.instruction);
              // 纯数字
              if (/^\d+$/.test(s)) {
                const n = Number(s);
                if (Number.isFinite(n)) return n;
              }
              // DRF 超链接风格 .../instructions/<id>/
              const m = s.match(/(\d+)(?:\/)?$/);
              if (m && m[1]) {
                const n = Number(m[1]);
                if (Number.isFinite(n)) return n;
              }
            }
            // 4) instruction 为对象（常见于嵌套），或 instruction_detail
            if (w?.instruction && typeof w.instruction === 'object' && w.instruction.id != null) {
              const n = Number(w.instruction.id);
              if (Number.isFinite(n)) return n;
            }
            if (w?.instruction_detail && typeof w.instruction_detail === 'object' && w.instruction_detail.id != null) {
              const n = Number(w.instruction_detail.id);
              if (Number.isFinite(n)) return n;
            }
            // 5) 一些实现会把 instruction_id 放在 context 内
            if (w?.context && typeof w.context === 'object') {
              if (w.context.instruction_id != null) {
                const n = Number(w.context.instruction_id);
                if (Number.isFinite(n)) return n;
              }
              if (w.context.instruction != null) {
                const s = String(w.context.instruction);
                if (/^\d+$/.test(s)) {
                  const n = Number(s);
                  if (Number.isFinite(n)) return n;
                }
              }
            }
          } catch {}
          return NaN;
        };
        const onlyThisInstruction = (raw || []).filter((w:any) => extractInstructionId(w) === Number(instructionId));
        try {
          const miss = (raw || []).filter((w:any) => extractInstructionId(w) !== Number(instructionId)).slice(0, 5);
          // 调试输出：帮助定位未被识别的字段形态（使用 console.log 避免被静音）
          // eslint-disable-next-line no-console
          console.log('[InstructionDetail] WorkOrders loaded:', {
            instructionId,
            total: (raw || []).length,
            matched: onlyThisInstruction.length,
            sample_unmatched: miss.map((w:any) => ({
              id: w?.id,
              instruction_id: w?.instruction_id,
              instruction: w?.instruction,
              instruction_detail_id: w?.instruction_detail?.id,
              context_instruction_id: w?.context?.instruction_id,
            }))
          });
          if ((raw || []).length > 0 && onlyThisInstruction.length === 0) {
            message.info('已加载工单数据，但未匹配到属于当前指令的工单（请检查 instruction 字段形态）');
          }
        } catch {}
        setWoList(onlyThisInstruction);
      } catch {
        setWoList([]);
      }
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  }, [instructionId]);
  useEffect(() => {
    setPackCopyDone(false);
  }, [detail?.id, detail?.updated_at]);
  const copyPackInfo = useCallback(async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        message.success('已复制');
        return true;
      }
    } catch {}
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success('已复制');
      return true;
    } catch {
      message.error('复制失败');
      return false;
    }
  }, []);
  const openPackResultEditor = useCallback(() => {
    try {
      packResultForm.setFieldsValue({
        actual_pack_length_cm: detail?.actual_pack_length_cm ?? undefined,
        actual_pack_width_cm: detail?.actual_pack_width_cm ?? undefined,
        actual_pack_height_cm: detail?.actual_pack_height_cm ?? undefined,
        actual_gross_weight_g: detail?.actual_gross_weight_g ?? undefined,
        waybill_carrier: detail?.waybill_carrier ?? undefined,
        waybill_tracking_no: detail?.waybill_tracking_no ?? undefined,
      });
    } catch {}
    setPackResultOpen(true);
  }, [detail, packResultForm]);
  const normalizeWaybillSource = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (s === 'warehouse' || s === 'upload') return s;
    if (s === '仓库提供') return 'warehouse';
    if (s === '上传运单') return 'upload';
    return s;
  }, []);
  const normalizeWaybillSubmitMode = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (s === 'immediate' || s === 'after_pack') return s;
    if (s === '立即上传') return 'immediate';
    if (s === '先申请包装数据') return 'after_pack';
    return s;
  }, []);
  const savePackResult = useCallback(async () => {
    try {
      const v = await packResultForm.validateFields();
      const ans = (detail?.customer_answers || {}) as any;
      const waybillSource = normalizeWaybillSource(ans?.waybill_source);
      const submitMode = normalizeWaybillSubmitMode(ans?.waybill_submit_mode);
      const needPackInfo = waybillSource === 'warehouse' || (waybillSource === 'upload' && submitMode === 'after_pack');
      const needWaybillInfo = waybillSource === 'warehouse';
      const payload: any = {
        actual_pack_length_cm: v?.actual_pack_length_cm ?? undefined,
        actual_pack_width_cm: v?.actual_pack_width_cm ?? undefined,
        actual_pack_height_cm: v?.actual_pack_height_cm ?? undefined,
        actual_gross_weight_g: v?.actual_gross_weight_g ?? undefined,
        waybill_carrier: v?.waybill_carrier ? String(v.waybill_carrier).trim() : undefined,
        waybill_tracking_no: v?.waybill_tracking_no ? String(v.waybill_tracking_no).trim() : undefined,
      };
      const hasPack =
        payload.actual_pack_length_cm != null
        || payload.actual_pack_width_cm != null
        || payload.actual_pack_height_cm != null
        || payload.actual_gross_weight_g != null;
      const hasWaybill = !!payload.waybill_carrier || !!payload.waybill_tracking_no;
      if (needPackInfo && !hasPack) {
        message.warning('请填写完整的包装信息');
        return;
      }
      if (needWaybillInfo && !hasWaybill) {
        message.warning('请填写运单信息');
        return;
      }
      if (!hasPack && !payload.waybill_carrier && !payload.waybill_tracking_no) {
        message.warning('请至少填写一项包装或运单信息');
        return;
      }
      if (hasPack) {
        payload.pack_result_status = 'provided';
      }
      if (needWaybillInfo && hasWaybill) {
        payload.waybill_status = 'uploaded';
      }
      setPackResultSaving(true);
      await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, payload);
      message.success('已保存包装/运单信息');
      setPackResultOpen(false);
      refresh();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setPackResultSaving(false);
    }
  }, [instructionId, packResultForm, refresh, detail, normalizeWaybillSource, normalizeWaybillSubmitMode]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (isAdminOrStaff) loadPlanPreview(); }, [instructionId, isAdminOrStaff]);

  // 加载指令相关日志
  const fetchInstructionLogs = useCallback(async () => {
    if (!instructionId) return;
    setLogLoading(true);
    try {
      const debugMode = (() => {
        try {
          const qs = String((window as any)?.location?.search || '');
          return /(^|[?&])logs_debug=1(&|$)/.test(qs);
        } catch { return false; }
      })();
      const params: any = {
        target_type: 'instruction',
        target_id: instructionId,
        page: logsPage,
        page_size: debugMode ? 200 : logsPageSize,
        ordering: '-created_at'
      };
      const resp = await request.get('/api/v1/audit/logs/', { params });
      const payload = resp?.data || {};
      let list: any[] = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      const originalList = Array.isArray(list) ? [...list] : [];
      try {
        // 调试输出：原始事件类型与 correlation_id 分布
        const typeCounts: Record<string, number> = {};
        const cidCounts: Record<string, number> = {};
        (originalList || []).forEach((r:any)=> {
          const t = String(r?.event_type || '');
          typeCounts[t] = (typeCounts[t] || 0) + 1;
          const cid = String(r?.correlation_id || '');
          if (cid) cidCounts[cid] = (cidCounts[cid] || 0) + 1;
        });
        // eslint-disable-next-line no-console
        console.debug('[Logs][Fetch]', {
          page: params.page, page_size: params.page_size,
          count: Number(payload?.count || 0), results: originalList.length,
          typeCounts, cidCounts
        });
      } catch {}
			// 不展示"领取整单"的内部动作日志
			list = list.filter((r:any) => String(r?.event_type) !== 'instruction.claimed');
			// 客户端默认隐藏仓内阶段类日志（阶段开始/完成）以及内部备注/执行提醒
			if (!isAdminOrStaff) {
				const hideSet = new Set(['instruction.stage_started', 'instruction.stage_completed']);
				list = (list || []).filter((r:any) => {
					const evt = String(r?.event_type || '');
					if (hideSet.has(evt)) return false;
					if (evt === 'instruction.ops_tips_set') return false;
					if (evt === 'instruction.note') {
						const vis = String(r?.context?.visibility || 'internal');
						if (vis !== 'public') return false;
					}
					return true;
				});
			}
      // 将同批"附件变更"折叠：优先使用服务端批次日志，若存在批次则移除同 correlation_id 的零散附件变更（非创建阶段）
      try {
        const cidToBatchIdx = new Map<string, number>();
        (list || []).forEach((r:any, idx:number) => {
          if (String(r?.event_type||'') === 'instruction.attachments_batch') {
            const cid = String(r?.correlation_id || '');
            if (cid) cidToBatchIdx.set(cid, idx);
          }
        });
        if (cidToBatchIdx.size > 0) {
          const toRemove: number[] = [];
          list.forEach((r:any, idx:number) => {
            if (String(r?.event_type||'') !== 'instruction.attachments_changed') return;
            if (String(r?.context?.phase||'') === 'initial') return;
            const cid = String(r?.correlation_id || '');
            if (cid && cidToBatchIdx.has(cid)) toRemove.push(idx);
          });
          if (toRemove.length > 0) {
            const kept:any[] = [];
            list.forEach((r:any, idx:number) => { if (!toRemove.includes(idx)) kept.push(r); });
            list = kept;
          }
        }
      } catch {}
      // 前端兜底折叠：当服务端未对某些批次产生 attachments_batch 时，仅对"缺少批次"的附件变更按锚点聚合
      try {
        // 已有服务端批次的 correlation_id 列表（这些不再做前端聚合）
        const serverBatchCidSet = new Set<string>(
          (list || [])
            .filter((r:any)=> String(r?.event_type||'')==='instruction.attachments_batch')
            .map((r:any)=> String(r?.correlation_id || '')).filter((s:string)=> !!s)
        );
        // 仅当存在"未被服务端聚合"的附件变更时才启用前端兜底
        const needFallback = (list || []).some((r:any)=> {
          if (String(r?.event_type||'') !== 'instruction.attachments_changed') return false;
          if (String(r?.context?.phase||'') === 'initial') return false;
          const cid = String(r?.correlation_id || '');
          return !cid || !serverBatchCidSet.has(cid);
        });
        if (needFallback) {
          const parseTs = (s:any) => { try { return new Date(String(s)).getTime(); } catch { return 0; } };
          // 按时间升序处理 updated 锚点，避免"后来的锚点回吸更早的附件日志"
          const updatedAsc = (list || [])
            .map((r:any, idx:number)=>({r, idx}))
            .filter(x=> String(x.r?.event_type||'')==='instruction.updated')
            .sort((a:any,b:any)=> parseTs(a?.r?.created_at) - parseTs(b?.r?.created_at));
          // 标记：已被吸附的附件日志下标
          const usedIdx = new Set<number>();
          const makeBatchNode = (anchor:any, items:any[], cid:string|undefined) => {
            const added = items.filter(it=> String(it?.context?.action||'')==='added').length;
            const removed = items.filter(it=> String(it?.context?.action||'')==='removed').length;
            const updatedCnt = items.filter(it=> String(it?.context?.action||'')==='updated').length;
            // eslint-disable-next-line no-console
            try { console.debug('[Logs][Batch][Synthetic]', { anchor_cid: cid || anchor?.correlation_id, count: items.length, added, removed, updated: updatedCnt }); } catch {}
            // 为合成批次补齐操作者快照（优先复用锚点的快照，否则根据可用字段拼装）
            const anchorSnap = (anchor?.actor_snapshot || {}) as any;
            const snapDisplayName = anchorSnap?.display_name || anchor?.actor_username || (anchor?.actor_id != null ? `用户${anchor.actor_id}` : undefined);
            const snap: any = (anchorSnap && (anchorSnap.display_name || anchorSnap.username || anchorSnap.id))
              ? anchorSnap
              : {
                  id: anchor?.actor_id,
                  username: anchor?.actor_username,
                  display_name: snapDisplayName,
                  role: anchor?.actor_role,
                };
            return {
              id: `synthetic-batch-${anchor?.id || anchor?.created_at || Math.random()}`,
              event_type: 'instruction.attachments_batch',
              actor_id: anchor?.actor_id,
              actor_role: anchor?.actor_role,
              actor_snapshot: snap,
              correlation_id: cid || anchor?.correlation_id,
              created_at: anchor?.created_at,
              severity: 'info',
              context: {
                aggregate: true,
                summary: { added, removed, updated: updatedCnt, total: items.length },
                items: {
                  added: items.filter(it=> String(it?.context?.action||'')==='added').slice(0, 10).map((it:any)=> ({
                    label: it?.context?.label, file_name: it?.context?.file_name, scope: it?.context?.scope
                  })),
                  removed: items.filter(it=> String(it?.context?.action||'')==='removed').slice(0, 10).map((it:any)=> ({
                    label: it?.context?.label, file_name: it?.context?.file_name, scope: it?.context?.scope
                  })),
                  updated: items.filter(it=> String(it?.context?.action||'')==='updated').slice(0, 10).map((it:any)=> ({
                    before: { label: it?.context?.label, file_name: it?.context?.file_name, scope: it?.context?.scope },
                    after: {},
                  })),
                },
                anchor_event: 'instruction.updated',
                batch: 'update',
              }
            };
          };
          // 逐个 updated 锚点吸附"尚未被服务端聚合"的附件日志
          updatedAsc.forEach(({r: anchor}) => {
            const at = parseTs(anchor?.created_at);
            const cid = String(anchor?.correlation_id || '');
            const actor = anchor?.actor_id;
            const candidates = (list || []).map((x:any, i:number)=>({x, i})).filter(({x, i}) => {
              if (usedIdx.has(i)) return false;
              if (String(x?.event_type||'') !== 'instruction.attachments_changed') return false;
              if (String(x?.context?.phase||'') === 'initial') return false;
              const xid = String(x?.correlation_id || '');
              // 已有服务端批次的 correlation_id 不再纳入前端兜底
              if (xid && serverBatchCidSet.has(xid)) return false;
              const xt = parseTs(x?.created_at);
              // 规则1：cid 完全匹配
              if (cid && xid && cid === xid) return true;
              // 规则2（保守）：无 cid 或锚点/附件之一缺 cid 时，仅"向前锚定"，同 actor 且 xt 在 [at, at+30s]
              if (actor != null && x?.actor_id === actor && at > 0 && xt > 0 && xt >= at && (xt - at) <= 30_000) return true;
              return false;
            });
            if (candidates.length > 0) {
              const batchItems = candidates.map(c=> c.x);
              // 从后往前删除零散项，避免下标偏移
              candidates.sort((a,b)=> b.i - a.i).forEach(c=> { usedIdx.add(c.i); list.splice(c.i, 1); });
              // 重新定位 anchor 位置并插入合成批次节点到其下方
              const newAnchorIdx = list.findIndex((v:any)=> v === anchor);
              const synthetic = makeBatchNode(anchor, batchItems, cid || undefined);
              list.splice(newAnchorIdx + 1, 0, synthetic);
            }
          });
        }
      } catch {}
      // 合并"创建阶段附件上传"日志：与 instruction.created 同一 correlation_id 或在其后60秒内的附件变更
      try {
        const parseTs = (s:any) => {
          if (!s) return 0;
          try { return new Date(String(s)).getTime(); } catch { return 0; }
        };
        // 先按时间升序找第一条创建事件
        const asc = [...list].sort((a:any,b:any)=> parseTs(a?.created_at)-parseTs(b?.created_at));
        const createdEvt = asc.find((r:any)=> String(r?.event_type||'')==='instruction.created');
        if (createdEvt) {
          const t0 = parseTs(createdEvt?.created_at);
          const cid0 = String(createdEvt?.correlation_id || '');
          const withinWindow = (r:any) => {
            const t = parseTs(r?.created_at);
            return t0 > 0 && t >= t0 && (t - t0) <= 60_000; // 60秒窗口
          };
          const sameCid = (r:any) => !!cid0 && String(r?.correlation_id || '') === cid0;
          const isAttach = (r:any) => String(r?.event_type||'') === 'instruction.attachments_changed';
          const initialAttachIdx: number[] = [];
          list.forEach((r:any, idx:number) => {
            if (!isAttach(r)) return;
            if (sameCid(r) || withinWindow(r)) initialAttachIdx.push(idx);
          });
          if (initialAttachIdx.length > 0) {
            const count = initialAttachIdx.length;
            const kept: any[] = [];
            list.forEach((r:any, idx:number) => { if (!initialAttachIdx.includes(idx)) kept.push(r); });
            // 构造合并后的"创建阶段上传X个附件"虚拟记录（紧邻创建事件时间）
            const synthetic = {
              id: `initial-attachments-${instructionId}-${createdEvt?.id || createdEvt?.created_at || 't0'}`,
              event_type: 'instruction.attachments_changed',
              actor_id: createdEvt?.actor_id,
              actor_role: createdEvt?.actor_role,
              correlation_id: createdEvt?.correlation_id,
              created_at: createdEvt?.created_at,
              severity: 'info',
              context: {
                phase: 'initial',
                action: 'added',
                initial_attachments_count: count,
              }
            };
            kept.push(synthetic);
            list = kept;
          }
        }
      } catch {}
      // 去重与合并：instruction.updated 可能因 PATCH 委托 update 产生重复，这里按 correlation_id 或时间窗口合并
      const merged: any[] = [];
      const keyOf = (r:any) => {
        const evt = String(r?.event_type || '');
        if (evt !== 'instruction.updated') return `${evt}|${String(r?.id || '')}`;
        const cid = String(r?.correlation_id || '');
        if (cid) return `${evt}|cid:${cid}`;
        // 无 correlation_id 时，用时间戳(到秒)+actor 合并
        const ts = String(r?.created_at || '').slice(0, 19); // YYYY-MM-DDTHH:mm:ss
        const actor = String(r?.actor_id || '');
        return `${evt}|ts:${ts}|actor:${actor}`;
      };
      const bucket = new Map<string, any>();
      for (const r of list) {
        if (String(r?.event_type || '') !== 'instruction.updated') { merged.push(r); continue; }
        const k = keyOf(r);
        if (!bucket.has(k)) {
          bucket.set(k, r);
        } else {
          // 合并 changed_fields 与 changes
          const base = bucket.get(k);
          const cfA: string[] = Array.isArray(base?.context?.changed_fields) ? base.context.changed_fields : [];
          const cfB: string[] = Array.isArray(r?.context?.changed_fields) ? r.context.changed_fields : [];
          const cfU = Array.from(new Set([...cfA, ...cfB]));
          const chA = (base?.context?.changes || {}) as any;
          const chB = (r?.context?.changes || {}) as any;
          const mergedChanges: any = { ...chA };
          Object.keys(chB).forEach((k2) => {
            const a = chA[k2];
            const b = chB[k2];
            if (!a) { mergedChanges[k2] = b; return; }
            if (!b) { mergedChanges[k2] = a; return; }
            // 简单合并规则：
            // - list 差分：取"体量更大"的一侧（added+removed+updated 总和更大）
            // - 标量 before/after：优先保留 before，after 取较新的记录
            if (String(a.kind || '') === 'list' || String(b.kind || '') === 'list') {
              const sz = (x:any) => (Array.isArray(x?.added)?x.added.length:0) + (Array.isArray(x?.removed)?x.removed.length:0) + (Array.isArray(x?.updated)?x.updated.length:0);
              mergedChanges[k2] = (sz(b) > sz(a)) ? b : a;
            } else {
              mergedChanges[k2] = {
                before: (a.before !== undefined ? a.before : b.before),
                after: (b.after !== undefined ? b.after : a.after),
              };
            }
          });
          bucket.set(k, {
            ...base,
            context: {
              ...(base?.context || {}),
              changed_fields: cfU,
              changes: mergedChanges,
            }
          });
        }
      }
      // 把更新后的 bucket 写回（保持原顺序：先保留非 updated，再追加合并后的 updated）
      const updatedMerged = Array.from(bucket.values());
      let finalList = [...merged.filter(r=>String(r?.event_type||'')!=='instruction.updated'), ...updatedMerged];
      // 最终渲染前强制按时间倒序（兜底）
      try {
        const parseTs2 = (s:any) => { try { return new Date(String(s)).getTime(); } catch { return 0; } };
        finalList = finalList.sort((a:any,b:any)=> parseTs2(b?.created_at) - parseTs2(a?.created_at));
        // 将 attachments_batch 吸附到对应 updated 下方（按 correlation_id）
        const findIdxBy = (pred:(x:any)=>boolean) => finalList.findIndex(pred);
        finalList = (() => {
          const arr = [...finalList];
          const moved = new Set<number>();
          for (let i=0;i<arr.length;i++) {
            const r = arr[i];
            if (String(r?.event_type||'') !== 'instruction.attachments_batch') continue;
            const cid = String(r?.correlation_id || '');
            if (!cid) continue;
            const idxUpdated = findIdxBy(x => String(x?.event_type||'')==='instruction.updated' && String(x?.correlation_id||'')===cid);
            if (idxUpdated >= 0 && idxUpdated < i && !moved.has(i)) {
              const node = arr.splice(i,1)[0];
              arr.splice(idxUpdated+1, 0, node);
              moved.add(idxUpdated+1);
            }
          }
          return arr;
        })();
        // 规则：创建指令永远放在列表最后一条（底部）；创建阶段附件紧随其上
        const idxCreated = finalList.findIndex((r:any)=> String(r?.event_type||'')==='instruction.created');
        if (idxCreated >= 0) {
          const created = finalList[idxCreated];
          finalList.splice(idxCreated, 1);
          // 收集"创建阶段附件"记录
          const initialIdxs: number[] = [];
          finalList.forEach((r:any, i:number) => {
            const isInitial = String(r?.event_type||'')==='instruction.attachments_changed' && String(r?.context?.phase||'')==='initial';
            if (isInitial) initialIdxs.push(i);
          });
          const initialNodes = initialIdxs.map(i=> finalList[i]);
          // 从列表中删除这些节点（从后往前删）
          initialIdxs.sort((a,b)=> b-a).forEach(i=> finalList.splice(i,1));
          // 末尾插入：先初始附件（旧→新），再创建日志（保证创建在最底部）
          if (initialNodes.length > 0) {
            initialNodes.sort((a:any,b:any)=> parseTs2(a?.created_at) - parseTs2(b?.created_at));
            finalList.push(...initialNodes);
          }
          finalList.push(created);
        }
      } catch {}
      setLogs(finalList);
      setLogsTotal(Number(payload?.count || list.length || 0));
      return;
    } catch (e) {
      message.error('加载日志失败');
    } finally {
      setLogLoading(false);
    }
  }, [instructionId, logsPage, logsPageSize]);

  useEffect(() => { setLogsPage(1); }, [instructionId]);
  useEffect(() => { fetchInstructionLogs(); }, [fetchInstructionLogs]);

  // 解析操作者用户名
  useEffect(() => {
    (async () => {
      try {
        const ids: string[] = [];
        (logs || []).forEach((r:any) => {
          // 若后端已提供快照或直接提供了 actor_username，则无需请求
          if (r?.actor_snapshot && (r.actor_snapshot.display_name || r.actor_snapshot.username)) {
            return;
          }
          if (r?.actor_username) {
            return;
          }
          const aid = r?.actor_id;
          if (aid !== undefined && aid !== null) {
            const key = String(aid);
            if (!actorNameMap[key]) ids.push(key);
          }
        });
        const uniq = Array.from(new Set(ids));
        if (uniq.length === 0) return;
        const pairs: Array<[string,string]> = [];
        for (const id of uniq) {
          try {
            const resp = await request.get(`/api/v1/users/${id}/`);
            const u = resp?.data || {};
            const name = u.username || u.name || `用户${id}`;
            pairs.push([id, name]);
          } catch {
            pairs.push([id, `用户${id}`]);
          }
        }
        setActorNameMap(prev => {
          const next = { ...prev } as Record<string,string>;
          pairs.forEach(([id, name]) => { next[id] = name; });
          return next;
        });
      } catch {}
    })();
  }, [logs]);

  const eventText = (evt?: string) => {
    const v = String(evt || '');
    const map: Record<string, string> = {
      'instruction.created': '创建指令',
      'instruction.status_changed': '状态变更',
      'instruction.cancelled': '取消指令',
      'instruction.review_accepted': '指令审核通过，待执行',
      'instruction.review_rejected': '指令审核驳回，待修改指令',
      'instruction.result_submitted': '指令结果提交',
      'instruction.final_review_updated': '终审结果已更新',
      'instruction.plan_override_updated': '计划覆盖变更',
      'instruction.claimed': '领取整单',
      'instruction.execution_started': '开始操作',
			'instruction.stage_started': '开始阶段',
			'instruction.stage_completed': '完成阶段',
      'instruction.ops_tips_set': '设置执行提醒',
      'instruction.note': '备注',
      'instruction.waybill_uploaded': '仓库购买运单信息上传',
      'instruction.updated': '指令已修改',
      'instruction.attachments_changed': '附件变更',
      'instruction.attachments_batch': '附件变更（本次）',
    };
    return map[v] || v;
  };
	// 带行上下文的事件标题（在阶段类事件中包含第N阶段）
	const eventTextWithRow = (r?: any) => {
		try {
			const evt = String(r?.event_type || '');
			if (evt === 'instruction.stage_started' || evt === 'instruction.stage_completed') {
				const idx = (() => { try { const n = Number(r?.context?.stage_index); return Number.isFinite(n) ? n : undefined; } catch { return undefined; } })();
				const base = evt === 'instruction.stage_started' ? '开始阶段' : '完成阶段';
				return idx ? `${base} · 第${idx}阶段` : base;
			}
			return eventText(evt);
		} catch {
			return eventText(String(r?.event_type || ''));
		}
	};
  const extractStatusValue = (val: any): string => {
    if (val == null) return '';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      const direct = (val as any).status ?? (val as any).value ?? (val as any).code ?? (val as any).new ?? (val as any).old ?? (val as any).after ?? (val as any).before;
      if (typeof direct === 'string' || typeof direct === 'number') return String(direct);
      const statusLikeKey = Object.keys(val).find(k => /status/i.test(k));
      if (statusLikeKey) {
        const nested = (val as any)[statusLikeKey];
        if (typeof nested === 'string' || typeof nested === 'number') return String(nested);
      }
      try { return JSON.stringify(val); } catch { return String(val); }
    }
    return String(val);
  };
  const renderLogContent = (r: any) => {
    const evt: string = String(r?.event_type || '');
    const ctx = r?.context || {};
    const colorAdd = { color: '#0958d9' };
    const colorRemove = { color: '#cf1322' };
    const colorChange = { color: '#ad6800' };
    if (evt === 'instruction.status_changed') {
      const before = extractStatusValue(ctx.before_status ?? ctx.before);
      const after = extractStatusValue(ctx.after_status ?? ctx.after);
      return (
        <Space size={6}>
          <span>状态：</span>
          <Tag color={statusColor(String(before || ''))}>{statusText(String(before || ''))}</Tag>
          <span>→</span>
          <Tag color={statusColor(String(after || ''))}>{statusText(String(after || ''))}</Tag>
        </Space>
      );
    }
    if (evt === 'instruction.created') {
      return <span>创建指令</span>;
    }
    if (evt === 'instruction.cancelled') {
      return <span>取消指令</span>;
    }
    if (evt === 'instruction.review_accepted') {
      return <span>指令审核通过，待执行</span>;
    }
    if (evt === 'instruction.review_rejected') {
      const reason = ctx?.reason ?? ctx?.remark ?? ctx?.remarks;
      return <span>指令审核驳回，待修改指令{reason ? `。驳回原因：${String(reason)}` : ''}</span>;
    }
    if (evt === 'instruction.execution_started') {
      const days = (() => {
        try {
          const d = ctx?.estimated_working_days ?? ctx?.estimatedDays ?? ctx?.estimated;
          return (d !== undefined && d !== null && d !== '') ? Number(d) : undefined;
        } catch { return undefined; }
      })();
      return (
        <span>
          开始操作{days !== undefined && !Number.isNaN(days) ? `（预计 ${days} 个工作日内完成）` : ''}
        </span>
      );
    }
		if (evt === 'instruction.stage_started') {
			const idx = (() => { try { const n = Number(ctx?.stage_index); return Number.isFinite(n) ? n : undefined; } catch { return undefined; } })();
			const cnt = (() => { try { const c = Number(ctx?.work_order_count ?? ctx?.started_count); return Number.isFinite(c) ? c : 0; } catch { return 0; } })();
			return <span>开始{idx ? `第 ${idx} 阶段` : '当前阶段'}（{cnt}个工单）</span>;
		}
		if (evt === 'instruction.stage_completed') {
			const idx = (() => { try { const n = Number(ctx?.stage_index); return Number.isFinite(n) ? n : undefined; } catch { return undefined; } })();
			const cnt = (() => { try { const c = Number(ctx?.work_order_count ?? ctx?.completed_count); return Number.isFinite(c) ? c : 0; } catch { return 0; } })();
			return <span>完成{idx ? `第 ${idx} 阶段` : '当前阶段'}（{cnt}个工单）</span>;
		}
    if (evt === 'instruction.claimed') {
      return <span>领取整单</span>;
    }
    if (evt === 'instruction.updated') {
      const fields: string[] = Array.isArray(ctx?.changed_fields) ? ctx.changed_fields : [];
      const changes: any = ctx?.changes || {};
      const hasDetail = changes && Object.keys(changes).length > 0;
      const packFieldKeys = ['actual_pack_length_cm', 'actual_pack_width_cm', 'actual_pack_height_cm', 'actual_gross_weight_g', 'pack_result_status'];
      const fieldLabelMap: Record<string, string> = {
        name: '名称',
        remarks: '备注',
        priority: '优先级',
        customer_answers: '问题回答',
        meta: '扩展信息',
        warehouse_id: '仓库',
        project_id: '项目',
        targets: '操作对象',
        attachments: '附件',
        actual_pack_length_cm: '实际包装长度(cm)',
        actual_pack_width_cm: '实际包装宽度(cm)',
        actual_pack_height_cm: '实际包装高度(cm)',
        actual_gross_weight_g: '实际包装毛重(g)',
        pack_result_status: '包装结果状态',
      };
      const toCN = (k: string) => fieldLabelMap[k] || k;
      const toCNPriority = (v: any) => (v === 'super' ? '超级优先' : v === 'high' ? '一般优先' : v === 'normal' ? '普通' : String(v ?? '-'));
      const toPackStatusCN = (v: any) => {
        const s = String(v ?? '').trim().toLowerCase();
        if (!s || s === 'none' || s === 'null' || s === 'undefined' || s === '-') return '-';
        if (s === 'provided') return '已提供';
        if (s === 'pending') return '待提供';
        return String(v ?? '-');
      };
      const toPosNum = (v: any): number | null => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const fmtPackDims = (l: any, w: any, h: any) => {
        const ln = toPosNum(l); const wn = toPosNum(w); const hn = toPosNum(h);
        if (ln == null || wn == null || hn == null) return '-';
        return `${ln}×${wn}×${hn} cm`;
      };
      const fmtPackWeight = (g: any) => {
        const gn = toPosNum(g);
        if (gn == null) return '-';
        return `${gn} g`;
      };
      const hasPackChanged = packFieldKeys.some((k) => Object.prototype.hasOwnProperty.call(changes || {}, k) || fields.includes(k));
      const nonPackFields = (fields || []).filter((k) => !packFieldKeys.includes(k));
      const displayChangedFieldsText = (() => {
        if (fields.length <= 0) return '发生修改';
        if (!hasPackChanged) return `修改字段：${fields.map(toCN).join('、')}`;
        if (nonPackFields.length === 0) return '修改字段：实际打包结果';
        return `修改字段：实际打包结果、${nonPackFields.map(toCN).join('、')}`;
      })();
      const qn = (detail?.questionnaire_snapshot || {}) as any;
      const qList: any[] = Array.isArray(qn?.questions) ? qn.questions : [];
      const qKeyToLabel = new Map<string, string>(qList.map((q:any)=> [String(q?.key||'').trim(), String(q?.label||q?.key||'')]));
      const packBefore = {
        l: changes?.actual_pack_length_cm?.before,
        w: changes?.actual_pack_width_cm?.before,
        h: changes?.actual_pack_height_cm?.before,
        g: changes?.actual_gross_weight_g?.before,
        s: changes?.pack_result_status?.before,
      };
      const packAfter = {
        l: changes?.actual_pack_length_cm?.after,
        w: changes?.actual_pack_width_cm?.after,
        h: changes?.actual_pack_height_cm?.after,
        g: changes?.actual_gross_weight_g?.after,
        s: changes?.pack_result_status?.after,
      };
      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
          <span>{displayChangedFieldsText}</span>
          {hasDetail && (
            <div style={{ color:'#595959' }}>
              {hasPackChanged ? (
                <div style={{ marginBottom: 2 }}>
                  实际打包结果：
                  尺寸 {fmtPackDims(packBefore.l, packBefore.w, packBefore.h)} → {fmtPackDims(packAfter.l, packAfter.w, packAfter.h)}；
                  重量 {fmtPackWeight(packBefore.g)} → {fmtPackWeight(packAfter.g)}；
                  状态 {toPackStatusCN(packBefore.s)} → {toPackStatusCN(packAfter.s)}
                </div>
              ) : null}
              {Object.keys(changes).filter((k) => !packFieldKeys.includes(k)).slice(0, 6).map((k) => {
                const c = changes[k];
                if (!c) return null;
                const kind = String(c.kind || '');
                if (kind === 'list') {
                  const added = Array.isArray(c.added) ? c.added.length : 0;
                  const removed = Array.isArray(c.removed) ? c.removed.length : 0;
                  const updatedCnt = Array.isArray(c.updated) ? c.updated.length : 0;
                  const blocks: any[] = [];
                  if (k === 'targets' || k === 'attachments') {
                    // 操作对象、附件：仅显示 新增/移除，不显示"变更X条"
                    blocks.push(
                      <div key={`${k}-summary`}>
                        {toCN(k)}：
                        <span style={colorAdd}>新增{added}条</span>，
                        <span style={colorRemove}>移除{removed}条</span>
                      </div>
                    );
                  } else {
                    blocks.push(
                      <div key={`${k}-summary`}>
                        {toCN(k)}：
                        <span style={colorAdd}>新增{added}条</span>，
                        <span style={colorRemove}>移除{removed}条</span>，
                        <span style={colorChange}>变更{updatedCnt}条</span>
                      </div>
                    );
                  }
                  // 显示"移除+新增"及 expected_qty 合计（仅针对本次变更项，不影响原始JSON明细）
                  try {
                    const adds = Array.isArray(c.added) ? c.added : [];
                    const rems = Array.isArray(c.removed) ? c.removed : [];
                    const ups = Array.isArray(c.updated) ? c.updated : [];
                    const qty = (o:any) => {
                      const v = (o?.expected_qty ?? o?.quantity ?? o?.qty ?? o?.count);
                      const n = Number(v);
                      return Number.isFinite(n) ? n : 0;
                    };
                    const sum = (arr:any[], pick:(x:any)=>number) => (arr || []).reduce((acc, x) => acc + (pick(x) || 0), 0);
                    const beforeTotal = sum(rems, qty) + sum(ups, (u:any)=> qty(u?.before));
                    const afterTotal  = sum(adds, qty) + sum(ups, (u:any)=> qty(u?.after));
                    // 非"操作对象"字段时才展示"移除+新增"行；操作对象不展示
                    if (k !== 'targets' && (added + removed > 0)) {
                      blocks.push(
                        <div key={`${k}-rmadd`} style={{ marginLeft: 8 }}>
                          移除+新增：{removed}+{added}
                        </div>
                      );
                    }
                    blocks.push(
                      <div key={`${k}-totals`} style={{ marginLeft: 8, color: '#8c8c8c' }}>
                        数量合计（仅变更项）：{beforeTotal} → {afterTotal}
                      </div>
                    );
                  } catch {}
                  // 对操作对象（targets）的数量变更进行明细展示（最多3条）
                  if (k === 'targets') {
                    // 需求：不展示"移除+新增"组合行与逐条"ID=xxx：数量 A → B"行，保留其它摘要与总量
                  }
                  return <div key={k}>{blocks}</div>;
                }
                if (c.before !== undefined || c.after !== undefined) {
                  // 特殊字段格式化
                  if (k === 'project_id' || k === 'project') {
                    const parseProjectId = (v: any): string => {
                      try {
                        if (v == null || v === '') return '';
                        const n = Number(v);
                        if (Number.isFinite(n) && n > 0) return String(n);
                        const s = String(v).trim();
                        const n2 = Number(s);
                        return Number.isFinite(n2) && n2 > 0 ? String(n2) : '';
                      } catch {
                        return '';
                      }
                    };
                    const beforeId = parseProjectId(c.before);
                    const afterId = parseProjectId(c.after);
                    const beforeCode = beforeId ? (projectCodeMap[beforeId] || beforeId) : '-';
                    const afterCode = afterId ? (projectCodeMap[afterId] || afterId) : '-';
                    return <div key={k}>项目修改：{beforeCode} → {afterCode}</div>;
                  }
                  if (k === 'template') {
                    const parseTemplateId = (v: any): string => {
                      try {
                        if (v == null || v === '') return '';
                        const n = Number(v);
                        if (Number.isFinite(n) && n > 0) return String(n);
                        const s = String(v).trim();
                        const n2 = Number(s);
                        return Number.isFinite(n2) && n2 > 0 ? String(n2) : '';
                      } catch {
                        return '';
                      }
                    };
                    const beforeId = parseTemplateId(c.before);
                    const afterId = parseTemplateId(c.after);
                    const beforeName = beforeId ? (templateNameMap[beforeId] || beforeId) : '-';
                    const afterName = afterId ? (templateNameMap[afterId] || afterId) : '-';
                    return <div key={k}>修改指令模板：{beforeName} → {afterName}</div>;
                  }
                  let beforeStr: any = c.before;
                  let afterStr: any = c.after;
                  if (k === 'priority') { beforeStr = toCNPriority(beforeStr); afterStr = toCNPriority(afterStr); }
                  if (k === 'customer_answers') {
                    try {
                      const b = c.before || {}; const a = c.after || {};
                      const keys = Array.from(new Set([...Object.keys(b||{}), ...Object.keys(a||{})]));
                      const diffs = keys.filter(qk => String((b as any)[qk]) !== String((a as any)[qk]));
                      if (diffs.length > 0) {
                        return (
                          <div key={k}>
                            {toCN(k)}：
                            {diffs.slice(0, 5).map((qk) => {
                              const label = qKeyToLabel.get(String(qk)) || qk;
                              const bv = (b as any)[qk]; const av = (a as any)[qk];
                              const fmt = (v:any) => {
                                if (Array.isArray(v)) return v.join('、');
                                if (typeof v === 'boolean') return v ? '是' : '否';
                                return String(v ?? '-');
                              };
                              return <div key={qk} style={{ marginLeft: 8 }}>{label}：{fmt(bv)} → {fmt(av)}</div>;
                            })}
                          </div>
                        );
                      }
                    } catch {}
                  }
                  return <div key={k}>{toCN(k)}：{String(beforeStr ?? '-')} → {String(afterStr ?? '-')}</div>;
                }
                return <div key={k}>{toCN(k)}：已修改</div>;
              })}
            </div>
          )}
        </div>
      );
    }
    if (evt === 'instruction.attachments_changed') {
      // 创建阶段合并展示
      if (String(ctx?.phase || '') === 'initial') {
        const cnt = Number(ctx?.initial_attachments_count || 0);
        return <span>创建阶段上传{cnt}个附件</span>;
      } else {
        const action = String(ctx?.action || '');
        const actionCN = action === 'added' ? '新增' : action === 'updated' ? '变更' : action === 'removed' ? '移除' : action || '-';
        const scopeCN = String(ctx?.scope || '') === 'actions' ? '动作' : '整单';
        const label = ctx?.label || '-';
        const fileName = ctx?.file_name || '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div>
              动作：
              <span style={actionCN==='新增'?colorAdd: actionCN==='移除'?colorRemove: colorChange}>{actionCN}</span>
            </div>
            <div>作用域：{scopeCN}</div>
            <div>附件：{label}</div>
            <div>文件：{fileName}</div>
          </div>
        );
      }
    }
    if (evt === 'instruction.attachments_batch') {
      const summary = ctx?.summary || {};
      const items = ctx?.items || {};
      const added = Number(summary?.added || 0);
      const removed = Number(summary?.removed || 0);
      // 取消"变更"概念：不显示 updated，total 仅统计新增+移除
      const total = Number(added + removed);
      const batchKey = `${String((r as any)?.correlation_id || '')}|${String((r as any)?.id || '')}|${String((r as any)?.created_at || '')}`;
      const open = !!expandedAttachBatch[batchKey];
      const Line: React.FC<{children:any}> = ({ children }) => <div style={{ marginLeft: 8 }}>{children}</div>;
      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 2 }}>
          <span>
            附件变更（本次）：
            <span style={colorAdd}>新增{added}</span>{' '}
            <span style={colorRemove}>移除{removed}</span>
            （共{total}）
            {total > 0 && (
              <span
                onClick={()=> setExpandedAttachBatch(prev => ({ ...prev, [batchKey]: !prev[batchKey] }))}
                style={open
                  ? { cursor:'pointer', display:'inline-inline', padding:'1px 6px', marginLeft: 8, border:'1px solid #faad14', background:'#fff7e6', color:'#ad6800', borderRadius: 4 }
                  : { cursor:'pointer', display:'inline-inline', padding:'1px 6px', marginLeft: 8, border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius: 4 }
                }
              >
                {open ? '收起明细' : '展开明细'}
              </span>
            )}
          </span>
          {open && (
            <div style={{ color:'#595959' }}>
              {(Array.isArray(items?.added) ? items.added : []).map((it:any, i:number) => (
                <Line key={`a-${i}`}><span style={colorAdd}>[新增]</span> {String(it?.label||'-')} · {String(it?.file_name||'-')} · {(String(it?.scope||'')==='actions'?'动作':'整单')}</Line>
              ))}
              {(Array.isArray(items?.removed) ? items.removed : []).map((it:any, i:number) => (
                <Line key={`r-${i}`}><span style={colorRemove}>[移除]</span> {String(it?.label||'-')} · {String(it?.file_name||'-')} · {(String(it?.scope||'')==='actions'?'动作':'整单')}</Line>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (evt === 'instruction.plan_override_updated') {
      return <span>计划覆盖变更</span>;
    }
    if (evt === 'instruction.ops_tips_set') {
      const text = String(ctx?.text || '').trim();
      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 2 }}>
          <span>
            <Tag color="gold">内部</Tag>
            设置执行提醒
          </span>
          <div style={{ whiteSpace: 'pre-wrap', color:'#595959' }}>{text || '-'}</div>
        </div>
      );
    }
    if (evt === 'instruction.note') {
      const vis = String(ctx?.visibility || 'internal');
      const text = String(ctx?.text || '').trim();
      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 2 }}>
          <span>
            {vis !== 'public' ? <Tag color="gold">内部</Tag> : null}
            仓库端添加备注
          </span>
          <div style={{ whiteSpace: 'pre-wrap', color:'#595959' }}>{text || '-'}</div>
        </div>
      );
    }
    if (evt === 'instruction.waybill_uploaded') {
      const carrier = String(ctx?.waybill_carrier || '').trim();
      const trackingNo = String(ctx?.waybill_tracking_no || '').trim();
      const feeAmountRaw = String(ctx?.waybill_charge_amount || '').trim();
      const feeCurrency = String(ctx?.waybill_charge_currency || '').trim();
      const attId = ctx?.attachment_id;
      const attLabel = String(ctx?.attachment_label || '').trim();
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>仓库购买运单信息上传</div>
          {carrier ? <div>承运商：{carrier}</div> : null}
          {trackingNo ? <div>物流跟踪号：{trackingNo}</div> : null}
          {feeAmountRaw ? <div>出库尾程物流费用：{feeAmountRaw}{feeCurrency ? ` ${feeCurrency}` : ''}</div> : null}
          {attId ? (
            <div>
              附件：{attLabel || '物流运单'}（ID: {String(attId)}）
            </div>
          ) : null}
        </div>
      );
    }
    return <span>-</span>;
  };

  const renderReadableContext = (evt: string, ctx: any) => {
    try {
      const items: Array<React.ReactNode> = [];
      const usedKeys = new Set<string>();
      if (evt === 'instruction.status_changed') {
        const before = extractStatusValue(ctx?.before_status ?? ctx?.before);
        const after = extractStatusValue(ctx?.after_status ?? ctx?.after);
        usedKeys.add('before'); usedKeys.add('after'); usedKeys.add('before_status'); usedKeys.add('after_status');
        items.push(
          <div key="sc">
            <Text type="secondary">状态：</Text>
            <Tag color={statusColor(String(before||''))}>{statusText(String(before||''))}</Tag>
            <span style={{ margin: '0 6px' }}>→</span>
            <Tag color={statusColor(String(after||''))}>{statusText(String(after||''))}</Tag>
          </div>
        );
      }
      if (evt === 'instruction.created') {
        const code = ctx?.instruction_system_code || ctx?.system_code;
        const name = ctx?.instruction_name || ctx?.name;
        if (code || name) {
          usedKeys.add('instruction_system_code'); usedKeys.add('system_code'); usedKeys.add('instruction_name'); usedKeys.add('name');
          items.push(
            <div key="created-basic">
              <Text type="secondary">指令：</Text>
              <Space size={8}>
                {name ? <span>{name}</span> : null}
                {code ? <Tag color="blue">{code}</Tag> : null}
              </Space>
            </div>
          );
        }
      }
      if (evt === 'instruction.cancelled') {
        const reason = ctx?.reason || ctx?.remark || ctx?.remarks;
        if (reason) {
          usedKeys.add('reason'); usedKeys.add('remark'); usedKeys.add('remarks');
          items.push(
            <div key="cancel-reason">
              <Text type="secondary">原因：</Text>
              <span>{String(reason)}</span>
            </div>
          );
        }
      }
      if (evt === 'instruction.review_rejected') {
        const reason = ctx?.reason || ctx?.remark || ctx?.remarks;
        if (reason) {
          usedKeys.add('reason'); usedKeys.add('remark'); usedKeys.add('remarks');
          items.push(
            <div key="reject-reason">
              <Text type="secondary">驳回原因：</Text>
              <span>{String(reason)}</span>
            </div>
          );
        }
      }
      if (evt === 'instruction.plan_override_updated') {
        const before = Array.isArray(ctx?.before) ? ctx.before : [];
        const after = Array.isArray(ctx?.after) ? ctx.after : [];
        usedKeys.add('before'); usedKeys.add('after');
        const fmt = (arr:any[]) => arr.map((s:any, i:number) => String(s?.template_code || `步骤${i+1}`) + (Array.isArray(s?.after) && s.after.length ? ` (after: ${s.after.join('、')})` : '')).join('；');
        items.push(
          <div key="plan-diff">
            <Text type="secondary">计划变更：</Text>
            <div>
              <div><Text type="secondary">前：</Text><span>{before.length ? fmt(before) : '-'}</span></div>
              <div><Text type="secondary">后：</Text><span>{after.length ? fmt(after) : '-'}</span></div>
            </div>
          </div>
        );
      }
      if (evt === 'instruction.stage_started' || evt === 'instruction.stage_completed') {
        const idx = (() => { try { const n = Number(ctx?.stage_index); return Number.isFinite(n) ? n : undefined; } catch { return undefined; } })();
        const cnt = (() => { try { const c = Number(ctx?.work_order_count ?? ctx?.started_count ?? ctx?.completed_count); return Number.isFinite(c) ? c : undefined; } catch { return undefined; } })();
        if (idx !== undefined) items.push(<div key="stage-line"><Text type="secondary">阶段：</Text><span>第 {idx} 阶段</span></div>);
        if (cnt !== undefined) items.push(<div key="stage-cnt"><Text type="secondary">工单数：</Text><span>{cnt}</span></div>);
        usedKeys.add('stage_index'); usedKeys.add('work_order_count'); usedKeys.add('started_count'); usedKeys.add('completed_count');
        try {
          const list = Array.isArray(ctx?.work_orders) ? ctx.work_orders.slice(0, 10) : [];
          if (list.length > 0) {
            items.push(
              <div key="stage-wos">
                <Text type="secondary">本阶段工单（部分）：</Text>
                <div style={{ marginLeft: 8, color: '#595959' }}>
                  {list.map((w:any, i:number) => (
                    <div key={i}>
                      {String(w?.system_code || `#${w?.id || i+1}`)}
                      {(w?.template_name || w?.template_code) ? ` · ${w?.template_name || ''}${w?.template_name && w?.template_code ? ' · ' : ''}${w?.template_code || ''}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          usedKeys.add('work_orders'); usedKeys.add('work_order_ids');
        } catch {}
      }
      if (evt === 'instruction.waybill_uploaded') {
        const carrier = String(ctx?.waybill_carrier || '').trim();
        const trackingNo = String(ctx?.waybill_tracking_no || '').trim();
        const feeAmountRaw = String(ctx?.waybill_charge_amount || '').trim();
        const feeCurrency = String(ctx?.waybill_charge_currency || '').trim();
        const attId = ctx?.attachment_id;
        const attLabel = String(ctx?.attachment_label || '').trim();
        usedKeys.add('waybill_carrier');
        usedKeys.add('waybill_tracking_no');
        usedKeys.add('waybill_charge_amount');
        usedKeys.add('waybill_charge_currency');
        usedKeys.add('attachment_id');
        usedKeys.add('attachment_label');
        if (carrier) {
          items.push(<div key="wb-carrier"><Text type="secondary">承运商：</Text><span>{carrier}</span></div>);
        }
        if (trackingNo) {
          items.push(<div key="wb-tracking"><Text type="secondary">物流跟踪号：</Text><span>{trackingNo}</span></div>);
        }
        if (feeAmountRaw) {
          items.push(
            <div key="wb-fee">
              <Text type="secondary">出库尾程物流费用：</Text>
              <span>{feeAmountRaw}{feeCurrency ? ` ${feeCurrency}` : ''}</span>
            </div>
          );
        }
        if (attId) {
          items.push(
            <div key="wb-att">
              <Text type="secondary">附件：</Text>
              <span>{attLabel || '物流运单'}（ID: {String(attId)}）</span>
            </div>
          );
        }
      }
      // 补充其它可读的基础字段（仅展示原始类型为 string/number/boolean 的扁平键）
      Object.entries(ctx || {}).forEach(([k, v]) => {
        if (usedKeys.has(k)) return;
        const t = typeof v;
        if (t === 'string' || t === 'number' || t === 'boolean') {
          items.push(
            <div key={`kv-${k}`}>
              <Text type="secondary">{k}：</Text>
              <span>{String(v)}</span>
            </div>
          );
        }
      });
      if (items.length === 0) return <span style={{ color: '#999' }}>无可读详情</span>;
      return <Space direction="vertical" size={4} style={{ width: '100%' }}>{items}</Space>;
    } catch {
      return <span style={{ color: '#999' }}>无可读详情</span>;
    }
  };

  // 设置浏览器标签标题：指令详情-<id>
  useEffect(() => {
    try {
      if (instructionId) {
        document.title = `指令详情-${instructionId}`;
        if (tabsCtx && typeof tabsCtx.updateTabTitle === 'function') {
          tabsCtx.updateTabTitle(`指令详情-${instructionId}`);
        }
      }
    } catch {}
  }, [instructionId, tabsCtx]);

  const statusColor = (s: string) => {
    return s === 'completed' ? 'green'
      : s === 'in_progress' ? 'blue'
      : s === 'operation_done' ? 'orange'
      : s === 'pending' ? 'gold'
      : s === 'accepted' ? 'cyan'
      : s === 'rejected' ? 'red'
      : s === 'cancelled' ? 'red'
      : 'default';
  };
  const statusText = (s: string) => {
    const map: Record<string, string> = {
      pending: '待受理',
      accepted: '已受理',
      in_progress: '执行中',
      operation_done: '执行阶段完成(待终审)',
      completed: '已完成',
      rejected: '已驳回',
      cancelled: '已取消',
    };
    return map[s] || s || '-';
  };
  const severityColor = (s?: string) => (s === 'error' ? 'red' : s === 'warn' ? 'orange' : 'blue');
  const priorityText = (p: string) => (p === 'super' ? '超级优先' : p === 'high' ? '一般优先' : '普通');

  const canEdit = detail?.status === 'pending' || detail?.status === 'rejected';
  const canAppend = detail?.status === 'completed';
  const canAppendEnabled = canAppend && appendAvailableIocCount > 0 && !appendAvailabilityLoading;
  const appendTip = canAppendEnabled
    ? '创建新指令,并将当前指令的执行结果作为新指令的操作对象'
    : '当前指令无可用的指令产出结果,无法直接追加指令';

  useEffect(() => {
    if (!instructionId || !canAppend) {
      setAppendAvailableIocCount(0);
      setAppendAvailabilityLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setAppendAvailabilityLoading(true);
        const resp = await request.post('/api/v1/instruction/instructions/append-availability/', {
          instruction_ids: [instructionId],
        });
        if (cancelled) return;
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const found = rows.find((x: any) => Number(x?.instruction_id) === Number(instructionId));
        setAppendAvailableIocCount(Number(found?.available_ioc_count || 0) || 0);
      } catch {
        if (!cancelled) setAppendAvailableIocCount(0);
      } finally {
        if (!cancelled) setAppendAvailabilityLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [instructionId, canAppend]);

  // 操作对象分页
  const [targetsPage, setTargetsPage] = useState(1);
  const [targetsPageSize, setTargetsPageSize] = useState(10);
  const [historyIocOverviewRows, setHistoryIocOverviewRows] = useState<any[]>([]);
  const [historyIocOverviewLoading, setHistoryIocOverviewLoading] = useState(false);
  const [historyInstructionBriefById, setHistoryInstructionBriefById] = useState<Record<number, any>>({});
  useEffect(() => { setTargetsPage(1); }, [detail?.id]);

  const handleCancel = async () => {
    if (!detail) return;
    Modal.confirm({
      title: '确定取消该指令？',
      content: `系统编码：${detail?.system_code || detail?.id}`,
      okText: '确认取消',
      okButtonProps: { danger: true },
      cancelText: '再想想',
      onOk: async () => {
        try {
          await request.post(`/api/v1/instruction/instructions/${detail.id}/cancel/`);
          message.success('已取消');
          refresh();
          try {
            window.dispatchEvent(new CustomEvent('instruction:changed', { detail: { id: detail.id, action: 'cancelled' } }));
          } catch {}
        } catch (e: any) {
          message.error(e?.response?.data?.detail || '取消失败');
        }
      }
    });
  };

  const renderOverview = () => {
    const projects = Array.isArray(detail?.projects_detail) ? detail.projects_detail : [];
    const projectsNode = (() => {
      if (projects.length > 0) {
        return (
          <span>
            {projects.map((p:any, idx:number) => {
              const code = p.project_code || p.code || p.id;
              const name = p.name || '-';
              return (
                <span key={idx}>
                  <span style={{ color: '#1677ff' }}>{code}</span>
                  {` - ${name}`}{idx < projects.length - 1 ? '、' : ''}
                </span>
              );
            })}
          </span>
        );
      }
      const pd = detail?.project_detail || detail?.project;
      if (pd && (pd.name || pd.project_code || pd.code || pd.id)) {
        const code = pd.project_code || pd.code || pd.id;
        const name = pd.name || '-';
        return (
          <span>
            <span style={{ color: '#1677ff' }}>{code}</span>
            {` - ${name}`}
          </span>
        );
      }
      return <span style={{ color: '#999' }}>-</span>;
    })();
    const templateNode = (() => {
      const ts = detail?.template_snapshot || {};
      const td = detail?.template_detail || {};
      const code = ts.system_code || ts.code || td.system_code;
      const name = ts.name || td.name;
      const brief = ts.brief_description || td.brief_description || ts.description;
      if (code || name) {
        return (
          <Space align="start" style={{ display: 'flex' }}>
            <Space direction="vertical" size={0}>
              <span>{name || '-'}</span>
              {code ? <Tag color="blue">{code}</Tag> : null}
            </Space>
            {brief ? (
              <Button size="small" type="primary" onClick={() => setShowTplBrief(true)}>查看指令模板说明</Button>
            ) : null}
          </Space>
        );
      }
      // 无快照或可读信息时不显示ID
      return '-';
    })();
    const warehouseNode = (() => {
      const w = detail?.warehouse_detail || detail?.warehouse;
      if (w && (w.name || w.code || w.warehouse_code)) {
        return (
          <Space direction="vertical" size={0}>
            <span>{w.name || '-'}</span>
            <Tag>{w.code || w.warehouse_code || ''}</Tag>
          </Space>
        );
      }
      return <span style={{ color: '#999' }}>-</span>;
    })();
    const creator = detail?.created_by_username
      || (detail?.created_by_detail?.username || detail?.created_by_detail?.name)
      || (typeof detail?.created_by === 'object' ? (detail?.created_by?.username || detail?.created_by?.name) : undefined)
      || (detail?.created_by_id != null ? `用户${detail.created_by_id}` : '-');
    return (
      <Descriptions
        bordered
        size="small"
        column={2}
        style={{ tableLayout: 'fixed' }}
        labelStyle={{ width: 120, whiteSpace: 'nowrap' }}
        contentStyle={{ maxWidth: 0, wordBreak: 'break-word' }}
      >
        {/* 行1：左=系统编码 右=指令模板 */}
        <Descriptions.Item label="系统编码" span={1}>
          {detail?.system_code ? (
            <Text strong style={{ fontSize: 16 }} code copyable={{ text: String(detail.system_code) }}>{detail.system_code}</Text>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="指令模板" span={1}>{templateNode}</Descriptions.Item>
        {/* 行2：左=名称 右=状态 */}
        <Descriptions.Item label="名称" span={1}>{detail?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="状态" span={1}><Tag color={statusColor(detail?.status)}>{statusText(detail?.status)}</Tag></Descriptions.Item>
        {/* 行3：左=项目 右=仓库 */}
        <Descriptions.Item label="项目" span={1}>{projectsNode}</Descriptions.Item>
        <Descriptions.Item label="仓库" span={1}>{warehouseNode}</Descriptions.Item>
        {/* 行4：左=优先级 右=创建者 */}
        <Descriptions.Item label="优先级" span={1}><Tag color={detail?.priority==='super'?'red': detail?.priority==='high'?'orange':'default'}>{priorityText(detail?.priority)}</Tag></Descriptions.Item>
        <Descriptions.Item label="创建者" span={1}>{creator}</Descriptions.Item>
        {/* 行5：左=创建时间 右=备注 */}
        <Descriptions.Item label="创建时间" span={1}>{detail?.created_at ? <TimezoneAwareTime time={detail.created_at} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-'}</Descriptions.Item>
        <Descriptions.Item label="备注" span={1}>
          {detail?.remarks
            ? <span style={{ color: '#cf1322', fontWeight: 700 }}>{detail.remarks}</span>
            : '-'
          }
        </Descriptions.Item>
        {/* 审核驳回原因（如有），单独占一行 */}
        {detail?.status === 'rejected' && detail?.reject_reason ? (
          <Descriptions.Item label="驳回原因" span={2}>
            <span style={{ whiteSpace: 'pre-wrap' }}>{detail.reject_reason}</span>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    );
  };

  // 承运商展示统一为：编码 - 名称（优先取问题集选项，其次取承运商主数据）
  const [carrierNameByCode, setCarrierNameByCode] = useState<Record<string, string>>({});
  const carrierLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    try {
      const qn = (detail?.questionnaire_snapshot || {}) as any;
      const qs: any[] = Array.isArray(qn?.questions) ? qn.questions : [];
      qs.forEach((q: any) => {
        const key = String(q?.key || '').trim().toLowerCase();
        const label = String(q?.label || '').trim();
        const isCarrierQuestion =
          key === 'waybill_carrier'
          || key === 'preferred_carrier'
          || key === 'carrier'
          || label.includes('承运商');
        if (!isCarrierQuestion) return;
        const options = Array.isArray(q?.options) ? q.options : [];
        options.forEach((o: any) => {
          const code = String(o?.value ?? '').trim();
          const name = String(o?.label ?? '').trim();
          if (!code || !name) return;
          map[code] = name;
          map[code.toUpperCase()] = name;
        });
      });
    } catch {}
    return map;
  }, [detail?.questionnaire_snapshot]);

  useEffect(() => {
    let cancelled = false;
    const loadCarrierNames = async () => {
      try {
        const qn = (detail?.questionnaire_snapshot || {}) as any;
        const qs: any[] = Array.isArray(qn?.questions) ? qn.questions : [];
        const sources: any[] = [];
        qs.forEach((q: any) => {
          const source = q?.options_source || q?.optionsSource;
          if (!source || String(source?.type || '') !== 'model') return;
          if (String(source?.model || '').trim() !== 'logistics_provider') return;
          sources.push(source);
        });
        if (sources.length === 0) return;
        const source = sources[0];
        const valueField = String(source?.value_field || 'code').trim();
        const resp = await request.get('/api/v1/logistics/providers/', { params: { is_active: 1, page_size: 1000 } });
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const next: Record<string, string> = {};
        (rows || []).forEach((r: any) => {
          const value = String(r?.[valueField] ?? '').trim();
          const code = String(r?.code ?? value).trim();
          const name = String(r?.name ?? '').trim();
          if (!code || !name) return;
          next[code] = name;
          next[code.toUpperCase()] = name;
          if (value) {
            next[value] = name;
            next[value.toUpperCase()] = name;
          }
        });
        if (cancelled) return;
        setCarrierNameByCode(next);
      } catch {
        if (!cancelled) setCarrierNameByCode({});
      }
    };
    void loadCarrierNames();
    return () => { cancelled = true; };
  }, [detail?.questionnaire_snapshot]);

  const formatCarrierDisplay = useCallback((raw: any, hintLabel?: string) => {
    const code = String(raw ?? '').trim();
    if (!code) return '-';
    const builtinNameMap: Record<string, string> = {
      USPS: '美国邮政',
      UPS: '联合包裹',
      FEDEX: '联邦快递',
      DHL: 'DHL',
    };
    const label = String(
      hintLabel
      || carrierLabelMap[code]
      || carrierLabelMap[code.toUpperCase()]
      || carrierNameByCode[code]
      || carrierNameByCode[code.toUpperCase()]
      || builtinNameMap[code.toUpperCase()]
      || ''
    ).trim();
    if (!label) return code;
    const lc = code.toLowerCase();
    const ll = label.toLowerCase();
    if (ll === lc || ll.startsWith(`${lc} -`)) return label;
    return `${code} - ${label}`;
  }, [carrierLabelMap, carrierNameByCode]);

  const renderQuestionnaire = () => {
    const qn = (detail?.questionnaire_snapshot || {}) as any;
    const qs = Array.isArray(qn?.questions) ? qn.questions : [];
    const ans = (detail?.customer_answers || {}) as any;
    const isEmptyLike = (v: any) => {
      if (v === undefined || v === null) return true;
      if (Array.isArray(v)) return v.length === 0;
      const s = String(v).trim().toLowerCase();
      return s === '' || s === 'none' || s === 'null' || s === 'undefined';
    };
    if (!qs || qs.length === 0) {
      return (
        <div style={{ padding: '8px 0' }}>无</div>
      );
    }
    const renderStateBadge = (text: string, tone: 'red' | 'green') => {
      const isRed = tone === 'red';
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            border: `1px solid ${isRed ? '#ff4d4f' : '#95de64'}`,
            background: isRed ? '#fff1f0' : '#f6ffed',
            color: isRed ? '#cf1322' : '#389e0d',
            borderRadius: 4,
            padding: '0 8px',
            height: 22,
          }}
        >
          {text}
        </span>
      );
    };
    const parseRepackAnswer = (raw: any): 'yes' | 'no' | '' => {
      if (raw === true) return 'yes';
      if (raw === false) return 'no';
      const s = String(raw ?? '').trim().toLowerCase();
      if (!s) return '';
      if (['true', '1', 'yes', 'y', '是', '需要', '需重新包装', '需要重新包装'].includes(s)) return 'yes';
      if (['false', '0', 'no', 'n', '否', '不需要', '无需重新包装', '不需重新包装', '不需要重新包装'].includes(s)) return 'no';
      if (s.includes('无需') || s.includes('不需要')) return 'no';
      if (s.includes('需') || s.includes('需要')) return 'yes';
      return '';
    };
    const parseMappingAnswer = (raw: any): 'has' | 'none' | '' => {
      if (raw === true) return 'has';
      if (raw === false) return 'none';
      const s = String(raw ?? '').trim().toLowerCase();
      if (!s) return '';
      if (['has_mapping', 'true', '1', 'yes', 'y'].includes(s)) return 'has';
      if (['no_mapping', 'false', '0', 'no', 'n'].includes(s)) return 'none';
      if (s.includes('有对应关系')) return 'has';
      if (s.includes('无对应关系')) return 'none';
      return '';
    };
    const renderVal = (q:any) => {
      const key = String(q.key||'').trim();
      const keyLower = key.toLowerCase();
      const keyLabel = String(q.label || q.key || '').trim();
      const val = ans[key];
      const type = String(q.type||'');
      if (isEmptyLike(val)) {
        return <span />;
      }
      const isRepackQuestion = keyLower === 'is_repack_required' || keyLower === 'repack_required' || keyLabel === '是否需重新包装';
      const isMappingQuestion = keyLower === 'shipping_label_to_target_mapping' || keyLabel === '运输标签与出库对象是否存在对应关系';
      if (isRepackQuestion) {
        const parsed = parseRepackAnswer(val);
        if (parsed === 'yes') return renderStateBadge('需重新包装', 'red');
        if (parsed === 'no') return renderStateBadge('无需重新包装', 'green');
      }
      if (type === 'boolean') {
        const isSpecial = [
          '是否需要签名服务',
          '是否需要保险',
          '是否需要升降尾板服务',
          '送货是否需提前预约',
          '派送车辆是否受限',
        ].includes(String(keyLabel).trim());
        if (isSpecial) {
          const s = String(val ?? '').trim().toLowerCase();
          const yes = val === true || ['true', '1', 'yes', 'y', '是', '需要'].includes(s);
          const style: React.CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 10px',
            height: 24,
            borderRadius: 4,
            fontWeight: 600,
            border: `1px solid ${yes ? '#ff4d4f' : '#52c41a'}`,
            background: yes ? '#fff1f0' : '#f6ffed',
            color: yes ? '#cf1322' : '#389e0d',
          };
          return <span style={style}>{yes ? '是' : '否'}</span>;
        }
        return val ? '是' : '否';
      }
      if (type === 'select') {
        const opt = (q.options||[]).find((o:any)=> String(o.value)===String(val));
        if (isMappingQuestion) {
          const parsed = parseMappingAnswer((opt?.label ?? val));
          if (parsed === 'has') return renderStateBadge('有对应关系', 'red');
          if (parsed === 'none') return renderStateBadge('无对应关系', 'green');
        }
        const qKey = String(q?.key || '').trim().toLowerCase();
        const qLabel = String(q?.label || '').trim();
        const isCarrierQuestion =
          qKey === 'preferred_carrier'
          || qKey === 'waybill_carrier'
          || qKey === 'carrier'
          || qLabel === '偏好物流承运商'
          || qLabel === '运单所属物流承运商';
        if (isCarrierQuestion) {
          const selectedCode = String(opt?.value ?? val ?? '').trim();
          const selectedLabel = String(opt?.label ?? '').trim();
          return formatCarrierDisplay(selectedCode, selectedLabel);
        }
        return opt ? (opt.label || opt.value) : String(val);
      }
      if (isMappingQuestion) {
        const parsed = parseMappingAnswer(val);
        if (parsed === 'has') return renderStateBadge('有对应关系', 'red');
        if (parsed === 'none') return renderStateBadge('无对应关系', 'green');
      }
      if (type === 'multiselect') {
        const arr = Array.isArray(val) ? val : [];
        const labels = arr.map((v:any)=>{
          const opt = (q.options||[]).find((o:any)=> String(o.value)===String(v));
          return opt ? (opt.label || opt.value) : String(v);
        });
        return labels.join('、');
      }
      if (type === 'number') return String(val) + (q.unit ? ` ${q.unit}` : '');
      return String(val);
    };
    const visibleQs = (showAllQuestions
      ? qs
      : qs.filter((q: any) => {
          const key = String(q?.key || '').trim();
          const v = ans?.[key];
          return !isEmptyLike(v);
        })
    ) as any[];
    if (!visibleQs || visibleQs.length === 0) {
      return (
        <div style={{ padding: '8px 0', color: '#999' }}>无</div>
      );
    }
    return (
      <div className="space-y-2">
        {visibleQs.map((q:any, idx:number) => (
          <div key={idx} style={rowBoxStyle}>
            <div style={col1BoxStyle}>
              <span>{q.label || q.key || `问题${idx+1}`}</span>
            </div>
            <div style={col2BoxStyle}>
              {renderVal(q)}
            </div>
            <div style={col3BoxStyle}>
              {q.tips ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setQTipTitle(String(q.label || q.key || '').trim());
                    setQTipText(String(q.tips));
                    setShowQTip(true);
                  }}
                  style={{ background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
                >
                  查看问题说明
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 操作对象：辅助渲染与数据补全
  const [unitContentCache, setUnitContentCache] = useState<Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>>>({});
  const [unitTypeCache, setUnitTypeCache] = useState<Record<string, string>>({});
  const [unitSortingGroupCache, setUnitSortingGroupCache] = useState<Record<string, { code: string; name: string }>>({});
  const parseIocCodesFromSelector = (selector: any): string[] => {
    try {
      if (selector && typeof selector === 'object') {
        const arr = Array.isArray(selector?.ioc_codes) ? selector.ioc_codes : [];
        return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
      }
      const s = String(selector || '').trim();
      if (!s) return [];
      try {
        const j = JSON.parse(s);
        const arr = Array.isArray(j?.ioc_codes) ? j.ioc_codes : [];
        return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
      } catch {}
      try {
        const j2 = JSON.parse(s.replace(/'/g, '"'));
        const arr = Array.isArray(j2?.ioc_codes) ? j2.ioc_codes : [];
        return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
      } catch {}
      return [];
    } catch {
      return [];
    }
  };
  const normalizeIocCode = (v: any) => String(v || '').trim().toUpperCase();
  const unitTypeToReadable = (v?: string) => {
    const key = String(v || '').toUpperCase();
    const map: Record<string, string> = {
      LIU: '散货库存单元',
      BIU: '箱库存单元',
      PIU: '托盘库存单元',
      AIU: '异常库存单元',
      NIU: '非产品库存单元',
    };
    return map[key] || '';
  };
  const getUnitTypeFromRow = (row: any): string | '' => {
    try {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const direct = snap?.unit_type || row?.unit_type || row?.target?.unit_type || snap?.inventory_unit?.unit_type;
      if (direct) return String(direct);
    } catch {}
    try {
      const uid = row?.target?.inventory_unit_id ?? row?.inventory_unit_id;
      if (uid != null) {
        const cached = unitTypeCache[String(uid)];
        if (cached) return cached;
      }
    } catch {}
    return '';
  };
  const renderTargetTypeReadable = (row: any) => {
    const v = String(row?.target?.target_type || row?.target_type || '').trim();
    if (v === 'inventory_unit') {
      const ut = unitTypeToReadable(getUnitTypeFromRow(row));
      return ut ? ut : '库存单元';
    }
    const map: Record<string, string> = {
      inventory_unit: '库存单元',
      sorting_group: '分拣组',
      project_all: '项目内全部',
      instruction_output: '上游指令产出',
    };
    return map[v] || v || '-';
  };
  const extractUnitContentItems = (row: any): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
    try {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.target?.content?.items,
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const p = it?.product || {};
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            const productName = String(it?.product_name || p?.name || '').trim();
            const customCode = String(
              it?.product_custom_code
              || p?.custom_code
              || it?.product_code
              || ''
            ).trim();
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                it?.product_system_code
                || it?.system_code
                || it?.product_code
                || ''
              );
            }
            return { barcode, qty, productName, customCode };
          }).filter(x => x.barcode || x.qty);
        }
      }
    } catch {}
    // 缓存兜底
    try {
      const uid = row?.target?.inventory_unit_id ?? row?.inventory_unit_id;
      if (uid != null) {
        const cached = unitContentCache[String(uid)];
        return Array.isArray(cached) ? cached : [];
      }
    } catch {}
    return [];
  };
  const renderUnitContentItems = (row: any) => {
    const items = extractUnitContentItems(row);
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {items.map((it, idx) => (
          <div key={idx}>
            {(() => {
              const productName = String((it as any)?.productName || '').trim();
              const customCode = String((it as any)?.customCode || '').trim();
              const barcode = String((it as any)?.barcode || '').trim();
              const meta = [customCode, barcode].filter(Boolean).join(' ');
              if (productName && meta) return `${productName}(${meta})`;
              if (productName) return productName;
              if (meta) return meta;
              return '-';
            })()} × {it.qty || 0}
          </div>
        ))}
      </div>
    );
  };
  // 缺失内容项/类型时，按需补拉库存单元详情
  useEffect(() => {
    (async () => {
      try {
        const rows = Array.isArray(detail?.targets_detail) ? detail.targets_detail : [];
        const pendingIds = new Set<string>();
        rows.forEach((r: any) => {
          const has = extractUnitContentItems(r);
          const uid = r?.target?.inventory_unit_id ?? r?.inventory_unit_id;
          if (uid != null) {
            const idStr = String(uid);
            const hasType = !!unitTypeCache[idStr] || !!getUnitTypeFromRow(r);
            const snap = r?.target?.snapshot || r?.snapshot || {};
            const info = r?.sorting_group_info || r?.target?.sorting_group_info || snap?.sorting_group_info || {};
            const hasSortingGroup = !!unitSortingGroupCache[idStr]
              || !!String(info?.system_code || info?.code || '').trim()
              || !!String(info?.name || '').trim()
              || !!String(r?.sorting_group_code || r?.target?.sorting_group_code || '').trim()
              || !!String(r?.sorting_group_name || r?.target?.sorting_group_name || '').trim();
            if ((!has || has.length === 0) || !hasType || !hasSortingGroup) pendingIds.add(idStr);
          }
        });
        if (pendingIds.size === 0) return;
        const contentUpdates: Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>> = {};
        const typeUpdates: Record<string, string> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            if (d?.unit_type) { typeUpdates[id] = String(d.unit_type); }
            try {
              const sg = d?.sorting_group_info || {};
              const code = String(sg?.system_code || sg?.code || '').trim();
              const name = String(sg?.name || '').trim();
              if (code || name) sortingGroupUpdates[id] = { code, name };
            } catch {}
            const mapped = (arr || []).map((it: any) => {
              const p = it?.product || {};
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              const productName = String(it?.product_name || p?.name || '').trim();
              const customCode = String(
                it?.product_custom_code
                || p?.custom_code
                || it?.product_code
                || ''
              ).trim();
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                );
              }
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) contentUpdates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(contentUpdates).length > 0) setUnitContentCache(prev => ({ ...prev, ...contentUpdates }));
        if (Object.keys(typeUpdates).length > 0) setUnitTypeCache(prev => ({ ...prev, ...typeUpdates }));
        if (Object.keys(sortingGroupUpdates).length > 0) setUnitSortingGroupCache(prev => ({ ...prev, ...sortingGroupUpdates }));
      } catch {}
    })();
  }, [detail?.targets_detail, unitSortingGroupCache, unitTypeCache]);

  useEffect(() => {
    let cancelled = false;
    const loadHistoryIocOverviewRows = async () => {
      const list = Array.isArray(detail?.targets_detail) ? detail.targets_detail : [];
      const hasHistoryIoc = (list || []).some((r:any) => String(r?.target_type || '').trim() === 'instruction_output');
      if (!hasHistoryIoc) {
        setHistoryIocOverviewRows([]);
        return;
      }
      setHistoryIocOverviewLoading(true);
      try {
        const projectById = new Map<number, { code?: string; name?: string }>();
        try {
          const prows = Array.isArray(detail?.projects_detail) ? detail.projects_detail : [];
          prows.forEach((p:any) => {
            const id = Number(p?.id);
            if (!Number.isFinite(id)) return;
            projectById.set(id, { code: p?.project_code, name: p?.name });
          });
        } catch {}
        const baseRows = (list || [])
          .filter((r:any) => String(r?.target_type || '').trim() === 'instruction_output')
          .map((r:any, idx:number) => {
            const snap = r?.snapshot || {};
            const selector = r?.output_selector_obj ?? r?.output_selector;
            const codes = parseIocCodesFromSelector(selector);
            const pid = Number(r?.project_id);
            const p = Number.isFinite(pid) ? projectById.get(pid) : undefined;
            return {
              __key: `hoc-${String(r?.id || idx)}`,
              project_id: Number.isFinite(pid) ? pid : undefined,
              project_code: r?.project_code || snap?.project_code || p?.code,
              project_name: r?.project_name || snap?.project_name || p?.name,
              instruction_id: Number(r?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id || 0) || undefined,
              instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
              output_container_id: snap?.output_container_id ?? snap?.container_id ?? undefined,
              output_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
              display_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
              container_type_display: snap?.container_type_display || snap?.container_type || '-',
              output_content_kind: snap?.output_content_kind || '',
              output_content_kind_display: snap?.output_content_kind_display || '-',
              output_contents_summary: snap?.output_contents_summary || '-',
              output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
              output_contents_direct_total_qty: Number(
                snap?.output_contents_direct_total_qty
                ?? snap?.direct_total_qty
                ?? snap?.output_contents_total_qty
                ?? snap?.total_qty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
              parent_container_id: snap?.parent_container_id,
              parent_code: String(snap?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(snap?.child_codes) ? snap.child_codes : [],
              is_parent_container: !!snap?.is_parent_container,
              is_child_container: !!snap?.is_child_container,
              length_cm: snap?.length_cm,
              width_cm: snap?.width_cm,
              height_cm: snap?.height_cm,
              weight_g: snap?.weight_g,
              created_at: snap?.created_at,
            } as any;
          });
        let mergedRows = baseRows;
        const prevIds: number[] = Array.from(new Set(
          (baseRows || [])
            .map((x:any) => Number(x?.instruction_id))
            .filter((v:number) => Number.isFinite(v) && v > 0)
        ));
        if (prevIds.length > 0) {
          const detailByCode = new Map<string, any>();
          const detailById = new Map<string, any>();
          await Promise.all(prevIds.map(async (pid) => {
            try {
              const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
              const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
              (arr || []).forEach((oc:any) => {
                const idKey = `${pid}::id::${String(oc?.id || '')}`;
                const codeKey = `${pid}::code::${normalizeIocCode(oc?.code)}`;
                if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
                if (normalizeIocCode(oc?.code)) detailByCode.set(codeKey, oc);
              });
            } catch {}
          }));
          mergedRows = (baseRows || []).map((row:any) => {
            const pid = Number(row?.instruction_id);
            if (!Number.isFinite(pid) || pid <= 0) return row;
            const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
            const byCode = detailByCode.get(`${pid}::code::${normalizeIocCode(row?.output_code || row?.display_code)}`);
            const hit = byId || byCode;
            if (!hit) return row;
            const contents = Array.isArray(hit?.contents) ? hit.contents : [];
            const totalQty = contents.reduce((acc:number, c:any) => acc + (Number(c?.quantity || 0) || 0), 0);
            const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
            const contentKindDisplay = kind === 'exception' ? '异常内容容器' : '正常内容容器';
            const detailText = (contents || []).map((c:any) => {
              const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
              const qty = Number(c?.quantity || 0) || 0;
              return code ? `${code}×${qty}` : '';
            }).filter(Boolean).join('；');
            return {
              ...row,
              output_container_id: hit?.id ?? row?.output_container_id,
              output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
                ? row.container_type_display
                : (String(hit?.container_type || '').trim() || '-'),
              output_content_kind: kind || row?.output_content_kind || '',
              output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
                ? row.output_content_kind_display
                : contentKindDisplay,
              output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
                ? row.output_contents_summary
                : (contents.length > 0 ? `${contents.length}项` : '-'),
              output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
              output_contents_direct_total_qty: Number(
                row?.output_contents_direct_total_qty
                ?? hit?.output_contents_direct_total_qty
                ?? row?.output_contents_total_qty
                ?? totalQty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
              parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
              parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
              is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
              is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              length_cm: hit?.length_cm ?? row?.length_cm,
              width_cm: hit?.width_cm ?? row?.width_cm,
              height_cm: hit?.height_cm ?? row?.height_cm,
              weight_g: hit?.weight_g ?? row?.weight_g,
              created_at: hit?.created_at ?? row?.created_at,
            };
          });
        }
        // 再以 unified 接口口径补齐显示字段：
        // - 对换标来源 IOC，output_contents_detail 会使用“新标签和数量”而非系统编码
        try {
          const projectIds: number[] = Array.from(new Set(
            (mergedRows || [])
              .map((r:any) => Number(r?.project_id))
              .filter((v:number) => Number.isFinite(v) && v > 0)
          ));
          if (projectIds.length > 0) {
            const warehouseCode = String(detail?.warehouse_detail?.code || detail?.warehouse?.code || '').trim();
            const unifiedByCode = new Map<string, any>();
            await Promise.all(projectIds.map(async (pid:number) => {
              try {
                const params: any = {
                  page: 1,
                  page_size: 500,
                  container_kind: 'output',
                  project: String(pid),
                  instruction_id: Number(detail?.id || instructionId || 0) || undefined,
                  ordering: '-created_at',
                };
                if (warehouseCode) params.warehouse_code = warehouseCode;
                const resp = await request.get('/api/v1/storage/containers/unified/', { params });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                (rows || []).forEach((r:any) => {
                  const code = normalizeIocCode(r?.system_code || r?.display_code || '');
                  if (code) unifiedByCode.set(`${pid}::${code}`, r);
                });
              } catch {}
            }));
            mergedRows = (mergedRows || []).map((row:any) => {
              const pid = Number(row?.project_id);
              const code = normalizeIocCode(row?.output_code || row?.display_code);
              if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
              const hit = unifiedByCode.get(`${pid}::${code}`);
              if (!hit) return row;
              return {
                ...row,
                output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
                display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
                container_type_display: hit?.container_type_display || row?.container_type_display || '-',
                output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
                output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
                output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
                // 关键：统一使用 unified 返回的容器内容文案（换标IOC为“新标签和数量”）
                output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
                output_contents_direct_total_qty: Number(
                  hit?.output_contents_direct_total_qty
                  ?? row?.output_contents_direct_total_qty
                  ?? hit?.output_contents_total_qty
                  ?? row?.output_contents_total_qty
                  ?? 0,
                ) || 0,
                output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
                parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              };
            });
          }
        } catch {}
        // 补一层层级关系：确保 parent/child 信息稳定可用（用于“层级标识 + 同组边框”）
        try {
          const instrIds: number[] = Array.from(new Set(
            (mergedRows || [])
              .map((r:any) => Number(r?.instruction_id))
              .filter((v:number) => Number.isFinite(v) && v > 0),
          ));
          if (instrIds.length > 0) {
            const hierByInstrCode = new Map<string, any>();
            await Promise.all(instrIds.map(async (pid:number) => {
              try {
                const resp = await request.get('/api/v1/storage/output-containers/', {
                  params: { instruction_id: pid, page: 1, page_size: 500, ordering: '-created_at' },
                });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                (rows || []).forEach((x:any) => {
                  const code = normalizeIocCode(x?.code || x?.system_code || x?.display_code || '');
                  if (!code) return;
                  hierByInstrCode.set(`${pid}::${code}`, {
                    parent_code: String(x?.parent_code || '').trim() || undefined,
                    child_codes: Array.isArray(x?.child_codes) ? x.child_codes.map((c:any) => String(c || '').trim()).filter(Boolean) : [],
                    is_parent_container: !!x?.is_parent_container || (Array.isArray(x?.child_codes) && x.child_codes.length > 0),
                    is_child_container: !!x?.is_child_container || !!x?.parent_container_id || !!x?.parent_code,
                    parent_container_id: x?.parent_container_id,
                  });
                });
              } catch {}
            }));
            if (hierByInstrCode.size > 0) {
              mergedRows = (mergedRows || []).map((row:any) => {
                const pid = Number(row?.instruction_id);
                const code = normalizeIocCode(row?.output_code || row?.display_code);
                if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
                const hit = hierByInstrCode.get(`${pid}::${code}`);
                if (!hit) return row;
                return {
                  ...row,
                  parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                  parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                  child_codes: Array.isArray(hit?.child_codes) && hit.child_codes.length > 0
                    ? hit.child_codes
                    : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                  is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                  is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
                };
              });
            }
          }
        } catch {}
        if (!cancelled) setHistoryIocOverviewRows(mergedRows);
      } catch {
        if (!cancelled) setHistoryIocOverviewRows([]);
      } finally {
        if (!cancelled) setHistoryIocOverviewLoading(false);
      }
    };
    void loadHistoryIocOverviewRows();
    return () => { cancelled = true; };
  }, [detail?.id, detail?.targets_detail, detail?.projects_detail]);

  // 为 IOC 列表中的“来源历史指令”补齐摘要字段（模板名/指令名/备注/创建时间）
  useEffect(() => {
    let cancelled = false;
    const loadInstructionBriefs = async () => {
      try {
        const ids = Array.from(new Set(
          (historyIocOverviewRows || [])
            .map((r:any) => Number(r?.instruction_id || 0))
            .filter((id:number) => Number.isFinite(id) && id > 0)
        )).filter((id:number) => !historyInstructionBriefById[id]);
        if (ids.length === 0) return;
        const next: Record<number, any> = {};
        await Promise.all(ids.map(async (id:number) => {
          try {
            const resp = await request.get(`/api/v1/instruction/instructions/${id}/`);
            const d = resp?.data || {};
            next[id] = {
              system_code: String(d?.system_code || d?.systemCode || d?.code || '').trim(),
              template_name: String(d?.template_snapshot?.name || d?.template_detail?.name || '').trim(),
              name: String(d?.name || '').trim(),
              remarks: String(d?.remarks || '').trim(),
              created_at: d?.created_at || '',
            };
          } catch {}
        }));
        if (cancelled) return;
        if (Object.keys(next).length > 0) {
          setHistoryInstructionBriefById((prev) => ({ ...prev, ...next }));
        }
      } catch {}
    };
    void loadInstructionBriefs();
    return () => { cancelled = true; };
  }, [historyIocOverviewRows, historyInstructionBriefById]);

  const targetsColumns = [
    { title: '类型', dataIndex: ['target', 'target_type'], key: 'type', render: (_: any, row: any) => renderTargetTypeReadable(row) },
    { title: '项目', key: 'project', render: (_: any, row: any) => {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const code = row?.project_code || snap.project_code || snap.code;
      const name = row?.project_name || snap.project_name || snap.name;
      return (code || name) ? `${code || ''}${name ? (code ? ' - ' : '') + name : ''}` : '-';
    }},
    { title: '库存单元系统编码', key: 'inventory_unit', render: (_: any, row: any) => {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const code = snap.system_barcode || row?.inventory_unit_system_barcode || row?.target?.inventory_unit_system_barcode;
      return code || row?.target?.inventory_unit_id || row?.inventory_unit_id || '-';
    }},
    { title: '分拣组', key: 'sorting_group', render: (_: any, row: any) => {
      const targetType = String(row?.target?.target_type || row?.target_type || '').trim();
      if (targetType && targetType !== 'inventory_unit') return '-';
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const uid = String(row?.target?.inventory_unit_id ?? row?.inventory_unit_id ?? '').trim();
      const cached = uid ? unitSortingGroupCache[uid] : undefined;
      const info = row?.sorting_group_info || row?.target?.sorting_group_info || snap?.sorting_group_info || {};
      const code = String(info?.system_code || info?.code || row?.sorting_group_code || row?.target?.sorting_group_code || '').trim();
      const name = String(info?.name || row?.sorting_group_name || row?.target?.sorting_group_name || '').trim();
      const finalCode = code || String(cached?.code || '').trim();
      const finalName = name || String(cached?.name || '').trim();
      if (finalCode && finalName) return `${finalCode} (${finalName})`;
      if (finalCode) return finalCode;
      if (finalName) return finalName;
      return '-';
    }},
    { title: '库存单元内容项', key: 'inventory_unit_items', render: (_: any, row: any) => renderUnitContentItems(row) },
    { title: '预计数量', dataIndex: 'expected_qty', key: 'expected_qty', render: (v: any) => (v ?? 0) },
    { title: '是否操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', render: (v: any) => {
      const isAll = !!v;
      const text = isAll ? '是' : '否';
      const style: React.CSSProperties = isAll
        ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' }
        : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' };
      return <span style={style}>{text}</span>;
    }},
  ];

  const historyIocTargetsColumns = [
    {
      title: '项目',
      key: 'project',
      render: (_: any, row: any) => {
        const code = row?.project_code || row?.project_id;
        const name = row?.project_name || '';
        return code ? `${code}${name ? ` - ${name}` : ''}` : '-';
      }
    },
    {
      title: '系统编码',
      dataIndex: 'output_code',
      key: 'output_code',
      render: (v:any, r:any) => {
        const text = String(v || r?.display_code || '-') || '-';
        const instId = Number(r?.instruction_id || 0);
        const openDetail = () => {
          try {
            if (!Number.isFinite(instId) || instId <= 0) return;
            const w: any = window as any;
            const path = String(w?.location?.pathname || '');
            const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
            const role = m?.[1] || 'customer';
            const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
            if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
              w.tabsManager.openTabByPath(detailUrl);
              return;
            }
            w.open(detailUrl, '_blank');
          } catch {}
        };
        return (
          <Button
            type="link"
            size="small"
            disabled={!Number.isFinite(instId) || instId <= 0}
            style={{ padding: 0, height: 'auto', fontFamily: 'monospace' }}
            onClick={(e) => { e.stopPropagation(); openDetail(); }}
          >
            {text}
          </Button>
        );
      }
    },
    { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', render: (v:any) => String(v || '-') },
    {
      title: '内容类型',
      dataIndex: 'output_content_kind_display',
      key: 'output_content_kind_display',
      render: (v:any, r:any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const isException = kind === 'exception';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
              background: isException ? '#fff7e6' : '#f6ffed',
              color: isException ? '#fa8c16' : '#389e0d',
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {String(v || '-')}
          </span>
        );
      }
    },
    { title: '容器内容统计', dataIndex: 'output_contents_summary', key: 'output_contents_summary', render: (_:any, r:any) => {
      const kind = String(r?.output_content_kind || '').toLowerCase();
      const qty = Number(r?.output_contents_total_qty || 0);
      if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
      return String(r?.output_contents_summary || '-');
    }},
    {
      title: '容器内容',
      dataIndex: 'output_contents_detail',
      key: 'output_contents_detail',
      render: (v:any, r:any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        if (kind === 'exception') {
          return (
            <Tooltip title="无法操作等异常产品">
              <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                无法操作等异常产品
              </div>
            </Tooltip>
          );
        }
        const full = String(v || '').trim();
        if (!full) return '-';
        const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
        if (lines.length === 0) return '-';
        const preview = lines.slice(0, 3);
        const hasMore = lines.length > 3;
        return (
          <div style={{ maxWidth: 360 }}>
            {preview.map((line, idx) => (
              <div
                key={`${line}-${idx}`}
                style={{
                  position: 'relative',
                  minHeight: 20,
                  marginBottom: idx === preview.length - 1 ? 0 : 2,
                }}
              >
                <Tooltip title={line}>
                  <div
                    style={{
                      color: '#595959',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                    }}
                  >
                    {line}
                  </div>
                </Tooltip>
                {hasMore && idx === preview.length - 1 ? (
                  <Button
                    type="link"
                    size="small"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: -2,
                      padding: 0,
                      height: 'auto',
                      lineHeight: 1.2,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const iocCode = String(r?.output_code || r?.display_code || '-');
                      Modal.info({
                        title: (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                            <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                            <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                            <span />
                          </div>
                        ),
                        width: 760,
                        centered: true,
                        content: (
                          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            {lines.map((x, i) => (
                              <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                {i + 1}. {x}
                              </div>
                            ))}
                          </div>
                        ),
                      });
                    }}
                  >
                    [...]
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }
    },
    { title: '容器尺寸', key: 'dims', render: (_:any, r:any) => (r?.length_cm != null && r?.width_cm != null && r?.height_cm != null) ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm` : '-' },
    { title: '容器重量', dataIndex: 'weight_g', key: 'weight_g', render: (v:any) => (v != null ? `${v}g` : '-') },
    {
      title: '来源历史指令',
      key: 'source_instruction',
      render: (_:any, r:any) => {
        const instId = Number(r?.instruction_id || 0);
        const brief = Number.isFinite(instId) && instId > 0 ? historyInstructionBriefById[instId] : undefined;
        const code = String(r?.instruction_code || brief?.system_code || '').trim();
        const text = code || (Number.isFinite(instId) && instId > 0 ? `#${instId}` : '');
        if (!text) return '-';
        const createdDateText = (() => {
          const raw = brief?.created_at || r?.created_at;
          if (!raw) return '-';
          try {
            const d = new Date(raw);
            if (!Number.isFinite(d.getTime())) return String(raw);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          } catch {
            return String(raw);
          }
        })();
        const summary = (
          <div style={{ maxWidth: 360, lineHeight: 1.6 }}>
            <div><b>来源历史指令摘要</b></div>
            <div>系统编码：{String(code || '-')}</div>
            <div>指令模板：{String(r?.instruction_template_name || brief?.template_name || '-')}</div>
            <div>指令名称：{String(r?.instruction_name || brief?.name || '-')}</div>
            <div>备注：{String(r?.instruction_remarks || brief?.remarks || '-')}</div>
            <div>创建时间：{createdDateText}</div>
          </div>
        );
        const openDetail = () => {
          try {
            if (!Number.isFinite(instId) || instId <= 0) return;
            const w: any = window as any;
            const path = String(w?.location?.pathname || '');
            const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
            const role = m?.[1] || 'customer';
            const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
            if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
              w.tabsManager.openTabByPath(detailUrl);
              return;
            }
            w.open(detailUrl, '_blank');
          } catch {}
        };
        return (
          <Tooltip title={summary}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, fontFamily: 'monospace' }}
              onClick={(e) => { e.stopPropagation(); openDetail(); }}
            >
              {text}
            </Button>
          </Tooltip>
        );
      }
    },
  ];

  const parseExecResultIocCodeSortMeta = useCallback((row: any) => {
    const code = String(row?.output_code || row?.display_code || '').trim().toUpperCase();
    if (!code) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
    const parts = code.split('-');
    if (parts.length < 2) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
    const groupRaw = parts[parts.length - 2];
    const tailRaw = parts[parts.length - 1];
    const groupNum = Number(groupRaw);
    const tailNum = Number(tailRaw);
    return {
      groupNum: Number.isFinite(groupNum) ? groupNum : -1,
      tailNum: Number.isFinite(tailNum) ? tailNum : Number.MAX_SAFE_INTEGER,
    };
  }, []);

  const execResultIocVisibleRows = useMemo(() => {
    const rows = Array.isArray(execResultIocRows) ? execResultIocRows : [];
    if (isAdminOrStaff) return rows;
    return rows.filter((r: any) => String(r?.output_content_kind || '').toLowerCase() !== 'exception');
  }, [execResultIocRows, isAdminOrStaff]);

  const execResultIocSortedRows = useMemo(() => {
    const rows = Array.isArray(execResultIocVisibleRows) ? [...execResultIocVisibleRows] : [];
    const parentByCode = execResultIocHierarchyByCode?.parentByCode || {};
    const childByParentCode = execResultIocHierarchyByCode?.childByParentCode || {};
    const getSortMeta = (r: any) => {
      const code = String(r?.output_code || r?.display_code || '').trim();
      const parentCode = String(parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(childByParentCode?.[code]) ? childByParentCode[code] : [];
      const role: 'parent' | 'child' | 'standalone' = parentCode ? 'child' : (childCodes.length > 0 ? 'parent' : 'standalone');
      const rootCode = parentCode || code;
      const groupKey = role === 'standalone' ? `self::${code}` : `hier::${rootCode}`;
      const rootMeta = parseExecResultIocCodeSortMeta({ output_code: rootCode });
      const selfMeta = parseExecResultIocCodeSortMeta({ output_code: code });
      const roleOrder = role === 'parent' ? 0 : (role === 'child' ? 1 : 0);
      return { code, groupKey, rootMeta, selfMeta, roleOrder };
    };
    rows.sort((a: any, b: any) => {
      const ga = getSortMeta(a);
      const gb = getSortMeta(b);
      if (ga.rootMeta.groupNum !== gb.rootMeta.groupNum) return gb.rootMeta.groupNum - ga.rootMeta.groupNum;
      if (ga.rootMeta.tailNum !== gb.rootMeta.tailNum) return ga.rootMeta.tailNum - gb.rootMeta.tailNum;
      if (ga.groupKey !== gb.groupKey) return ga.groupKey.localeCompare(gb.groupKey);
      if (ga.roleOrder !== gb.roleOrder) return ga.roleOrder - gb.roleOrder;
      if (ga.selfMeta.tailNum !== gb.selfMeta.tailNum) return ga.selfMeta.tailNum - gb.selfMeta.tailNum;
      return ga.code.localeCompare(gb.code);
    });
    return rows;
  }, [execResultIocVisibleRows, execResultIocHierarchyByCode, parseExecResultIocCodeSortMeta]);

  const execResultIocGroupingMeta = useMemo(() => {
    const rowToGroup = new Map<string, string>();
    const groupToRows = new Map<string, string[]>();
    const roleByRow = new Map<string, 'parent' | 'child' | 'standalone'>();
    const parentByCode = execResultIocHierarchyByCode?.parentByCode || {};
    const childByParentCode = execResultIocHierarchyByCode?.childByParentCode || {};
    (execResultIocSortedRows || []).forEach((r: any) => {
      const rowKey = String(r?.__key || r?.output_container_id || r?.output_code || '');
      const code = String(r?.output_code || r?.display_code || '').trim();
      if (!rowKey || !code) return;
      const parentCode = String(parentByCode?.[code] || '').trim();
      const childCodes = Array.isArray(childByParentCode?.[code]) ? childByParentCode[code] : [];
      const role: 'parent' | 'child' | 'standalone' = parentCode ? 'child' : (childCodes.length > 0 ? 'parent' : 'standalone');
      roleByRow.set(rowKey, role);
      const rootCode = parentCode || code;
      const groupKey = role === 'standalone' ? `standalone:${code}` : `hier:${rootCode}`;
      rowToGroup.set(rowKey, groupKey);
      const arr = groupToRows.get(groupKey) || [];
      arr.push(rowKey);
      groupToRows.set(groupKey, arr);
    });
    return { rowToGroup, groupToRows, roleByRow };
  }, [execResultIocSortedRows, execResultIocHierarchyByCode]);

  const execResultIocVisualMeta = useMemo(() => {
    const rowBgMap = new Map<string, string>();
    const rowGroupBorderMap = new Map<string, { top: boolean; bottom: boolean; grouped: boolean }>();
    const groupCount = new Map<string, number>();
    let nextStripe = 0;
    let lastVisualGroup = '';
    (execResultIocSortedRows || []).forEach((r: any) => {
      const rowKey = String(r?.__key || r?.output_container_id || r?.output_code || '');
      if (!rowKey) return;
      const visualGroupKey = String(execResultIocGroupingMeta.rowToGroup.get(rowKey) || '');
      if (!visualGroupKey) return;
      groupCount.set(visualGroupKey, (groupCount.get(visualGroupKey) || 0) + 1);
      if (visualGroupKey !== lastVisualGroup) nextStripe += 1;
      lastVisualGroup = visualGroupKey;
      const idx = nextStripe % 2;
      rowBgMap.set(rowKey, idx === 0 ? '#e6f7ff' : '#fff7e6');
    });
    (execResultIocSortedRows || []).forEach((r: any, idx: number) => {
      const rowKey = String(r?.__key || r?.output_container_id || r?.output_code || '');
      if (!rowKey) return;
      const groupKey = String(execResultIocGroupingMeta.rowToGroup.get(rowKey) || '');
      const prevKey = idx > 0
        ? String(execResultIocGroupingMeta.rowToGroup.get(String(execResultIocSortedRows[idx - 1]?.__key || execResultIocSortedRows[idx - 1]?.output_container_id || execResultIocSortedRows[idx - 1]?.output_code || '')) || '')
        : '';
      const nextKey = idx < (execResultIocSortedRows.length - 1)
        ? String(execResultIocGroupingMeta.rowToGroup.get(String(execResultIocSortedRows[idx + 1]?.__key || execResultIocSortedRows[idx + 1]?.output_container_id || execResultIocSortedRows[idx + 1]?.output_code || '')) || '')
        : '';
      const grouped = groupKey.startsWith('hier:') && Number(groupCount.get(groupKey) || 0) > 1;
      rowGroupBorderMap.set(rowKey, {
        top: grouped && prevKey !== groupKey,
        bottom: grouped && nextKey !== groupKey,
        grouped,
      });
    });
    return { rowBgMap, rowGroupBorderMap };
  }, [execResultIocSortedRows, execResultIocGroupingMeta]);

  const execResultIocColumns = [
    {
      title: '容器系统编码',
      dataIndex: 'output_code',
      key: 'output_code',
      width: 220,
      render: (v: any, r: any) => {
        const code = String(v || r?.display_code || '-');
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
            {code && code !== '-' ? (
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(code);
                    message.success('已复制系统编码');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            ) : null}
          </span>
        );
      },
    },
    { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 130, render: (v:any) => String(v || '-') },
    {
      title: '层级标识',
      key: '__hierarchy_role__',
      width: 120,
      render: (_: any, r: any) => {
        const rowKey = String(r?.__key || r?.output_container_id || r?.output_code || '');
        const role = execResultIocGroupingMeta.roleByRow.get(rowKey) || 'standalone';
        const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
        const roleStyle = role === 'parent'
          ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
          : (role === 'child'
            ? { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' }
            : { color: '#389e0d', border: '#b7eb8f', bg: '#f6ffed' });
        return (
          <span
            style={{
              display: 'inline-block',
              minWidth: 76,
              textAlign: 'center',
              padding: '2px 10px',
              borderRadius: 6,
              border: `1px solid ${roleStyle.border}`,
              color: roleStyle.color,
              background: roleStyle.bg,
              lineHeight: 1.4,
            }}
          >
            {roleText}
          </span>
        );
      }
    },
    {
      title: '内容类型',
      dataIndex: 'output_content_kind_display',
      key: 'output_content_kind_display',
      width: 130,
      render: (v:any, r:any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const isException = kind === 'exception';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
              background: isException ? '#fff7e6' : '#f6ffed',
              color: isException ? '#fa8c16' : '#389e0d',
              lineHeight: 1.5,
            }}
          >
            {String(v || '-')}
          </span>
        );
      }
    },
    {
      title: '容器内容统计',
      dataIndex: 'output_contents_summary',
      key: 'output_contents_summary',
      width: 150,
      render: (_:any, r:any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const qty = Number(r?.output_contents_total_qty || 0);
        if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
        return String(r?.output_contents_summary || '-');
      }
    },
    {
      title: '容器内容',
      dataIndex: 'output_contents_detail',
      key: 'output_contents_detail',
      width: 320,
      render: (v:any, r:any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        if (kind === 'exception') {
          return (
            <Tooltip title="无法操作等异常产品">
              <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                无法操作等异常产品
              </div>
            </Tooltip>
          );
        }
        const full = String(v || '').trim();
        if (!full) return '';
        const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
        if (lines.length === 0) return '';
        const preview = lines.slice(0, 3);
        const hasMore = lines.length > 3;
        return (
          <div style={{ maxWidth: 300 }}>
            {preview.map((line, idx) => (
              <div
                key={`${line}-${idx}`}
                style={{
                  position: 'relative',
                  minHeight: 20,
                  marginBottom: idx === preview.length - 1 ? 0 : 2,
                }}
              >
                <Tooltip title={line}>
                  <div
                    style={{
                      color: '#595959',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                    }}
                  >
                    {line}
                  </div>
                </Tooltip>
                {hasMore && idx === preview.length - 1 ? (
                  <Button
                    type="link"
                    size="small"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: -2,
                      padding: 0,
                      height: 'auto',
                      lineHeight: 1.2,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const iocCode = String(r?.output_code || r?.display_code || '-');
                      const fullInfoText = [
                        `容器系统编码: ${iocCode}`,
                        ...lines.map((x, i) => `${i + 1}. ${x}`),
                      ].join('\n');
                      Modal.info({
                        title: (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                            <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                            <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                            <span style={{ justifySelf: 'end' }}>
                              <Button
                                type="link"
                                size="small"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(fullInfoText);
                                    message.success('已复制完整信息');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              >
                                复制信息
                              </Button>
                            </span>
                          </div>
                        ),
                        width: 760,
                        centered: true,
                        content: (
                          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            {lines.map((x, i) => (
                              <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                {i + 1}. {x}
                              </div>
                            ))}
                          </div>
                        ),
                      });
                    }}
                  >
                    [...]
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }
    },
    { title: '容器尺寸', key: 'dims', width: 180, render: (_:any, r:any) => (r?.length_cm != null && r?.width_cm != null && r?.height_cm != null) ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm` : '' },
    { title: '容器重量', dataIndex: 'weight_g', key: 'weight_g', width: 140, render: (v:any) => (v != null ? `${v}g` : '') },
    {
      title: (
        <Tooltip
          placement="topRight"
          overlayInnerStyle={{ maxWidth: 'min(460px, calc(100vw - 32px))', width: 'min(460px, calc(100vw - 32px))' }}
          title={(
            <div style={{ lineHeight: 1.7 }}>
              <div>未占用：此容器未作为其他指令的操作对象被占用,一般仍保持为容器生成时的状态；</div>
              <div>已占用：该容器正作为其他指令的操作对象被占用,如换标装箱后的容器作为出库指令的操作对象等待出库/破损等异常产品容器作为销毁指令的操作对象等待销毁,但尚未操作完成；</div>
              <div>已消耗:该容器已经被处理,如已出库/已销毁等,通常已不存在于仓库中</div>
            </div>
          )}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>容器状态</span>
            <QuestionCircleOutlined style={{ color: '#1677ff' }} />
          </span>
        </Tooltip>
      ),
      key: 'latest_occupy_status',
      width: 130,
      render: (_:any, r:any) => {
        const s = String(r?.latest_occupy_status || '').trim().toLowerCase();
        const rawText = String(r?.latest_occupy_status_display || '-').trim();
        const baseStatusStyle: React.CSSProperties = {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 10px',
          height: 22,
          borderRadius: 4,
          fontWeight: 400,
          fontSize: 12,
          lineHeight: '20px',
        };
        // 注意：不能用 includes('occup')，否则会把 "unoccupied" 误判为占用中
        const isOccupied = ['active', 'occupied', 'occupying', 'hold', 'holding'].includes(s);
        const isReleased = s.includes('released') || rawText === '已释放';
        const isConsumed = s.includes('consumed') || rawText.includes('已消耗');
        if (isOccupied) {
          const occupiedText = (rawText && rawText !== '-' ? rawText : '已占用') === '占用中' ? '已占用' : (rawText && rawText !== '-' ? rawText : '已占用');
          return (
            <span
              style={{
                ...baseStatusStyle,
                border: '1px solid #ff7875',
                background: '#fff1f0',
                color: '#cf1322',
              }}
            >
              {occupiedText}
            </span>
          );
        }
        if (isConsumed) {
          const consumedText = '已消耗';
          return (
            <span
              style={{
                ...baseStatusStyle,
                border: '1px solid #ffe58f',
                background: '#fffbe6',
                color: '#ad8b00',
              }}
            >
              {consumedText}
            </span>
          );
        }
        // “已释放”与“未占用”统一显示为“未占用”（绿色）
        const unoccupiedText = (isReleased || rawText === '未占用' || !rawText || rawText === '-') ? '未占用' : rawText;
        return (
          <span
            style={{
              ...baseStatusStyle,
              border: '1px solid #b7eb8f',
              background: '#f6ffed',
              color: '#389e0d',
            }}
          >
            {unoccupiedText}
          </span>
        );
      }
    },
    {
      title: '相关占用/消耗指令',
      key: 'occupying_instruction',
      width: 180,
      render: (_:any, r:any) => {
        const s = String(r?.latest_occupy_status || '').trim().toLowerCase();
        const rawText = String(r?.latest_occupy_status_display || '').trim();
        const isConsumed = s.includes('consumed') || rawText.includes('已消耗');
        const instId = isConsumed
          ? Number(r?.consuming_instruction_id || 0)
          : Number(r?.occupying_instruction_id || 0);
        const code = isConsumed
          ? String(r?.consuming_instruction_code || '').trim()
          : String(r?.occupying_instruction_code || '').trim();
        const text = code || (Number.isFinite(instId) && instId > 0 ? `#${instId}` : '-');
        if (text === '-') return '-';
        const openDetail = () => {
          try {
            if (!Number.isFinite(instId) || instId <= 0) return;
            const w: any = window as any;
            const path = String(w?.location?.pathname || '');
            const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
            const role = m?.[1] || 'customer';
            const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
            if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
              w.tabsManager.openTabByPath(detailUrl);
              return;
            }
            w.open(detailUrl, '_blank');
          } catch {}
        };
        return (
          <Button
            type="link"
            size="small"
            disabled={!Number.isFinite(instId) || instId <= 0}
            style={{ padding: 0, height: 'auto', fontFamily: 'monospace' }}
            onClick={(e) => { e.stopPropagation(); openDetail(); }}
          >
            {text}
          </Button>
        );
      }
    },
  ];

  const actionsColumns = [
    { title: '操作类型', dataIndex: ['operation_type'], key: 'operation_type', render: (_: any, row: any) => (row?.operation_type || '-') },
    { title: '计费口径', dataIndex: 'basis', key: 'basis', render: (v: any) => v || '-' },
    { title: '声明数量', dataIndex: 'declared_qty', key: 'declared_qty', render: (v: any) => v ?? '-' },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (v: any) => v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-' },
  ];

  const renderAttachments = () => {
    return (
      <Descriptions size="small" column={1} bordered>
        {attachments.map((a:any) => (
          <Descriptions.Item key={a.id} label={`${a.label || a.mime_type || '附件'} (${a.scope || 'order'})`}>
            <Space>
              <span>{a.file_name || a.file_path || a.file}</span>
              <Button size="small" onClick={() => previewAttachment(a)}>预览</Button>
              <Button size="small" type="primary" onClick={() => handlePreviewDownload(a)}>下载</Button>
              <Button size="small" danger onClick={async () => {
                try {
                  await request.delete(`/api/v1/instruction/attachments/${a.id}/`);
                  message.success('已删除');
                  refresh();
                } catch {
                  message.error('删除失败');
                }
              }}>删除</Button>
            </Space>
          </Descriptions.Item>
        ))}
        {attachments.length === 0 && (
          <Descriptions.Item label="附件">无</Descriptions.Item>
        )}
      </Descriptions>
    );
  };
  const renderPackAndWaybill = () => {
    const modeText = (() => {
      if (detail?.waybill_source === 'warehouse') return '仓库提供';
      const m = detail?.waybill_submit_mode;
      if (m === 'immediate') return '立即上传运单';
      if (m === 'after_pack') return '先申请包装数据';
      return '-';
    })();
    const packStatusText = (() => {
      const s = detail?.pack_result_status;
      if (s === 'pending') return '待提供';
      if (s === 'provided') return '已提供';
      if (s === 'not_required') return '不需要';
      return '-';
    })();
    const ans = (detail?.customer_answers || {}) as any;
    const waybillSource = normalizeWaybillSource(ans?.waybill_source);
    const submitMode = normalizeWaybillSubmitMode(ans?.waybill_submit_mode);
    const allowPackEdit = waybillSource === 'warehouse' || (waybillSource === 'upload' && submitMode === 'after_pack');
    const fmt1 = (v: number) => (Math.round(v * 10) / 10).toFixed(1);
    const toInch = (cm: number) => cm / 2.54;
    const toLb = (g: number) => g / 453.59237;
    const estPackRequiredNode = detail?.is_pack_required === true
      ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #ff4d4f', background: '#fff1f0', color: '#cf1322', borderRadius: 4, padding: '0 8px', height: 22 }}>
          需打包
        </span>
      )
      : detail?.is_pack_required === false ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #52c41a', background: '#f6ffed', color: '#389e0d', borderRadius: 4, padding: '0 8px', height: 22 }}>
          无需打包
        </span>
      ) : '-';
    const estMethodText = (() => {
      const m = String(detail?.estimated_packing_method || '');
      if (m === 'warehouse_free') return '仓库免费包材';
      if (m === 'warehouse_paid') return '仓库收费包材';
      if (m === 'customer_carton') return '客户自购包材(纸箱)';
      if (m === 'customer_non_carton') return '客户自购包材(非纸箱)';
      if (m === 'special_packing') return '特殊包装';
      return m || '-';
    })();
    const estPkg = (detail as any)?.estimated_packaging_detail
      || (detail as any)?.estimated_packaging_detail_snapshot
      || (detail as any)?.estimated_packaging_snapshot
      || (detail as any)?.estimated_packaging;
    const estPkgText = (() => {
      if (!estPkg) return '-';
      if (typeof estPkg === 'number' || typeof estPkg === 'string') return String(estPkg);
      const code = estPkg?.system_code || estPkg?.code;
      const name = estPkg?.name;
      const id = estPkg?.id;
      if (code && name) return `${code} · ${name}`;
      return code || name || (id != null ? String(id) : '-');
    })();
    const estResultText = (() => {
      const l = Number(detail?.estimated_packaged_length_cm ?? estPkg?.length_cm ?? estPkg?.length);
      const w = Number(detail?.estimated_packaged_width_cm ?? estPkg?.width_cm ?? estPkg?.width);
      const h = Number(detail?.estimated_packaged_height_cm ?? estPkg?.height_cm ?? estPkg?.height);
      const g = Number(detail?.estimated_packaged_weight_g ?? estPkg?.weight_g ?? estPkg?.weight);
      if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0) {
        const size = `${fmt1(l)} × ${fmt1(w)} × ${fmt1(h)} cm | ${fmt1(toInch(l))} × ${fmt1(toInch(w))} × ${fmt1(toInch(h))} in`;
        if (Number.isFinite(g) && g > 0) {
          const weight = `${fmt1(g / 1000)} kg | ${fmt1(toLb(g))} lb`;
          return `尺寸: ${size}；重量: ${weight}`;
        }
        return `尺寸: ${size}；重量: -`;
      }
      return '-';
    })();
    const sizeText = (() => {
      const l = Number(detail?.actual_pack_length_cm);
      const w = Number(detail?.actual_pack_width_cm);
      const h = Number(detail?.actual_pack_height_cm);
      if (!Number.isFinite(l) || !Number.isFinite(w) || !Number.isFinite(h) || l <= 0 || w <= 0 || h <= 0) return '-';
      return `${fmt1(l)} × ${fmt1(w)} × ${fmt1(h)} cm | ${fmt1(toInch(l))} × ${fmt1(toInch(w))} × ${fmt1(toInch(h))} in`;
    })();
    const weightText = (() => {
      const g = Number(detail?.actual_gross_weight_g);
      if (!Number.isFinite(g) || g <= 0) return '-';
      return `${fmt1(g / 1000)} kg | ${fmt1(toLb(g))} lb`;
    })();
    const carrierDisplay = formatCarrierDisplay(detail?.waybill_carrier);
    const packInfoText = [
      `运单提供方式: ${modeText}`,
      `包装结果状态: ${packStatusText}`,
      `实际包装尺寸: ${sizeText}`,
      `实际包装重量: ${weightText}`,
      `物流承运商: ${carrierDisplay}`,
      `物流跟踪号: ${detail?.waybill_tracking_no || '-'}`,
    ].join('\n');
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <span style={{ fontWeight: 600 }}>包装与运单</span>
            <Button size="small" onClick={async () => {
              const ok = await copyPackInfo(packInfoText);
              if (ok) setPackCopyDone(true);
            }}>
              {packCopyDone ? '已复制' : '复制'}
            </Button>
          </div>
          <Space size={8}>
            {isAdminOrStaff && allowPackEdit ? (
              <Tooltip title={ALLOW_MANUAL_PACK_RESULT_EDIT ? undefined : '此按钮已禁用，必须通过指令执行阶段来提交包装和运单信息'}>
                {/* 禁用状态的按钮不会触发 Tooltip，需要包一层 span 承接鼠标事件 */}
                <span style={{ display: 'inline-block' }}>
                  <Button size="small" disabled={!ALLOW_MANUAL_PACK_RESULT_EDIT} onClick={openPackResultEditor}>填写包装/运单信息</Button>
                </span>
              </Tooltip>
            ) : null}
          </Space>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>包装预估</div>
            {(() => {
              const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
                <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, flex: 1 }}>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', background: 'transparent', color: '#595959', whiteSpace: 'nowrap' }}>{label}</div>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', borderLeft: '0', flex: 1, minWidth: 0, color: '#262626', wordBreak: 'break-word' }}>{value}</div>
                </div>
              );
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Field label="是否需要打包" value={estPackRequiredNode} />
                    <Field label="打包选项" value={estMethodText} />
                    <Field label="包材预估" value={estPkgText} />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Field label="包装预估结果" value={estResultText} />
                  </div>
                </>
              );
            })()}
          </div>
          <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>实际打包结果</div>
            {(() => {
              const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
                <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, flex: 1 }}>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', background: 'transparent', color: '#595959', whiteSpace: 'nowrap' }}>{label}</div>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', borderLeft: '0', flex: 1, minWidth: 0, color: '#262626', wordBreak: 'break-word' }}>{value}</div>
                </div>
              );
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Field label="实际包装尺寸" value={sizeText} />
                  <Field label="实际包装重量" value={weightText} />
                </div>
              );
            })()}
          </div>
          <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>运单信息</div>
            {(() => {
              const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
                <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, flex: 1 }}>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', background: 'transparent', color: '#595959', whiteSpace: 'nowrap' }}>{label}</div>
                  <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', borderLeft: '0', flex: 1, minWidth: 0, color: '#262626', wordBreak: 'break-word' }}>{value}</div>
                </div>
              );
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Field label="运单提供方式" value={modeText} />
                  <Field label="物流承运商" value={carrierDisplay} />
                  <Field label="物流跟踪号" value={detail?.waybill_tracking_no || '-'} />
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };
  const renderRegularOutboundPackAndWaybill = () => {
    const ans = (detail?.customer_answers || {}) as any;
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = ans?.[k];
        if (v !== undefined && v !== null && String(v).trim() !== '') return v;
      }
      return undefined;
    };
    const fmtYesNo = (v: any) => {
      const raw = String(v ?? '').trim();
      const s = raw.toLowerCase();
      if (s === '') return '-';
      if (v === true) return '是';
      if (v === false) return '否';
      if (['true', 'yes', 'y', '1', '是', '需要', '需重新包装', '需要重新包装'].includes(s)) return '是';
      if (['false', 'no', 'n', '0', '否', '不需要', '无需重新包装', '不需重新包装', '不需要重新包装'].includes(s)) return '否';
      if (raw.includes('无需') || raw.includes('不需要')) return '否';
      if (raw.includes('需') || raw.includes('需要')) return '是';
      return raw;
    };
    const renderRepackFlag = (v: any) => {
      const yesNo = fmtYesNo(v);
      if (yesNo === '是') {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #ff85c0', background: '#fff0f6', color: '#c41d7f', borderRadius: 4, padding: '0 8px', height: 22 }}>
            需重新包装
          </span>
        );
      }
      if (yesNo === '否') {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #95de64', background: '#f6ffed', color: '#389e0d', borderRadius: 4, padding: '0 8px', height: 22 }}>
            无需重新包装
          </span>
        );
      }
      return String(v ?? '-');
    };
    const fmtPackingMode = (v: any) => {
      const s = String(v ?? '').trim().toLowerCase();
      if (!s) return '-';
      if (['carton_only', '仅装箱'].includes(s)) return '仅装箱';
      if (['carton_then_pallet', '先装箱后打托盘', '装箱后打托盘'].includes(s)) return '先装箱后打托盘';
      if (['pallet_only', '直接打托盘'].includes(s)) return '直接打托盘';
      return String(v);
    };
    const fmtCartonSource = (v: any) => {
      const s = String(v ?? '').trim().toLowerCase();
      if (!s) return '-';
      if (['warehouse_free_carton', '仓库免费纸箱'].includes(s)) return '仓库免费纸箱';
      if (['customer_owned_carton', '客户自购纸箱'].includes(s)) return '客户自购纸箱';
      return String(v);
    };
    const modeText = (() => {
      const source = normalizeWaybillSource(pick('waybill_source'));
      const submitMode = normalizeWaybillSubmitMode(pick('waybill_submit_mode'));
      if (source === 'warehouse') return '仓库提供运单';
      if (source === 'upload') {
        if (submitMode === 'after_pack') return '先申请包装数据';
        return '立即上传运单';
      }
      if (detail?.waybill_source === 'warehouse') return '仓库提供运单';
      const m = detail?.waybill_submit_mode;
      if (m === 'after_pack') return '先申请包装数据';
      if (m === 'immediate') return '立即上传运单';
      return '-';
    })();
    const carrier = formatCarrierDisplay(detail?.waybill_carrier || pick('waybill_carrier'));
    const trackingText = (() => {
      const t = pick('waybill_tracking_no', 'tracking_number');
      if (t) return String(t);
      const detailTracking = String(detail?.waybill_tracking_no || '').trim();
      if (detailTracking) return detailTracking;
      const bol = pick('bol_no', 'bol_number');
      if (bol) return String(bol);
      const pro = pick('pro_no', 'pro_number');
      if (pro) return String(pro);
      return '-';
    })();
    const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
      <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, flex: 1 }}>
        <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', background: 'transparent', color: '#595959', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ padding: '4px 8px', border: '1px solid #f0f0f0', borderLeft: '0', flex: 1, minWidth: 0, color: '#262626', wordBreak: 'break-word' }}>{value}</div>
      </div>
    );
    return (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>包装与运单</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>包装信息</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Field label="是否需重新包装" value={renderRepackFlag(pick('is_repack_required', 'repack_required'))} />
              <Field label="包装方式" value={fmtPackingMode(pick('packing_mode', 'repack_method'))} />
              <Field label="装箱包材选择" value={fmtCartonSource(pick('carton_source'))} />
              <Field label="托盘种类" value={String(pick('pallet_type') ?? '-')} />
            </div>
          </div>
          <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>运单信息</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Field label="运单提供方式" value={modeText} />
              <Field label="物流承运商" value={carrier} />
              <Field label="物流跟踪号/BOL提单号码/PRO号码" value={trackingText} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>指令详情</Title>
              {detail ? (
                <Space size={8}>
                  {detail.system_code ? (
                    <Tag color="blue"><span style={{ fontWeight: 700 }}>{detail.system_code}</span></Tag>
                  ) : null}
                  {detail.name ? (
                    <Text strong style={{ color: '#1677ff' }}>{detail.name}</Text>
                  ) : null}
                </Space>
              ) : null}
            </div>
			<div style={{ display:'flex', alignItems:'center', gap: 16 }}>
				{detail && (() => {
					const ts = (detail?.template_snapshot || {}) as any;
					const td = (detail?.template_detail || {}) as any;
					const code = ts.system_code || ts.code || td.system_code || td.code;
					const name = ts.name || td.name;
					if (!code && !name) return null;
					return (
						<span style={{ color: '#ff7875', fontWeight: 600, fontSize: 16 }}>
							{String(name || '-')}{code ? ` · ${String(code)}` : ''}
						</span>
					);
				})()}
				<Space>
					<Button icon={<ReloadOutlined />} onClick={() => refresh()}>刷新</Button>
					{canEdit && (
						<Button type="primary" icon={<EditOutlined />} onClick={() => {
							try {
								const w:any = window as any;
								const path = String(w?.location?.pathname || '');
								const base = path.startsWith('/dashboard/staff') ? '/dashboard/staff' : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
								// 先关闭当前详情标签
								w?.tabsManager?.closeCurrentTab?.();
								navigate(`${base}/instructions/${instructionId}/edit`);
							} catch {}
						}}>修改</Button>
					)}
					{canEdit && (
						<Button danger icon={<CloseCircleOutlined />} onClick={handleCancel}>取消</Button>
					)}
					{canAppend && (
            <Tooltip title={appendTip}>
              <span>
                <Button
                  icon={<PlusOutlined />}
                  disabled={!canAppendEnabled}
                  style={canAppendEnabled ? { background: '#52c41a', borderColor: '#52c41a', color: '#fff', fontWeight: 700 } : undefined}
                  onClick={() => {
                    try {
                      const path = String((window as any)?.location?.pathname || '');
                      const base = path.startsWith('/dashboard/staff') ? '/dashboard/staff' : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
                      sessionStorage.setItem('ci_append_from_hint', JSON.stringify({ id: Number(instructionId), ts: Date.now() }));
                      navigate(`${base}/instructions/create?append_from=${instructionId}`);
                    } catch {}
                  }}
                >
                  追加指令
                </Button>
              </span>
            </Tooltip>
					)}
				</Space>
			</div>
          </div>
        </div>

        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: '概览',
              children: (
                <div style={{ paddingTop: 8 }}>
                  {renderOverview()}
                  <div style={{ marginTop: 16 }}>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <InstructionTargetsOverview
                      targets={Array.isArray(detail?.targets_detail) ? detail.targets_detail : []}
                      loading={loading}
                      page={targetsPage}
                      pageSize={targetsPageSize}
                      onPageChange={(p, ps) => { setTargetsPage(p); setTargetsPageSize(ps); }}
                      inventoryColumns={targetsColumns as any}
                      historyIocColumns={historyIocTargetsColumns as any}
                      historyIocRows={historyIocOverviewRows}
                      historyIocLoading={historyIocOverviewLoading}
                      historyTitleText="操作对象(历史指令执行结果)"
                    />
                  </div>
                  {(() => {
                    const tplName = String(detail?.template_snapshot?.name || detail?.template_detail?.name || '');
                    if (tplName.includes('常规出库')) {
                      return (
                        <div style={{ marginTop: 16 }}>
                          {renderRegularOutboundPackAndWaybill()}
                        </div>
                      );
                    }
                    if (tplName.includes('一件代发')) {
                      return (
                        <div style={{ marginTop: 16 }}>
                          {renderPackAndWaybill()}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )
            },
            // 库存非标准操作指令：执行记录（客户只看到对客户可见的内容；其他指令不显示这个页签）。详情页不显示收费，收费在终审页录入、提交终审后在财务管理的收费记录里查看
            ...(String(detail?.template_snapshot?.meta?.engine || '') === 'nonstandard_ops' && detail?.id ? [{
              key: 'nonstd_ops',
              label: '非标准操作指令执行记录',
              children: (
                <div style={{ paddingTop: 8 }}>
                  <NonStdWorkbench instructionId={Number(detail.id)} mode="view" hideCharges />
                </div>
              )
            }] : []),
            {
              key: 'tpl_qna_attachments',
              label: '指令模板及附件',
              children: (
                <div className="space-y-6" style={{ paddingTop: 8 }}>
                  {/* 模板区 */}
                  <Card size="small" title="指令模板">
                    <div style={rowBoxStyle}>
                      {(() => {
                        const ts = detail?.template_snapshot || {};
                        const td = detail?.template_detail || {};
                        const code = ts.system_code || ts.code || td.system_code;
                        const name = ts.name || td.name;
                        return (
                          <>
                            <div style={col1BoxStyle}>
                              <span>{name || '-'}</span>
                            </div>
                            <div style={col2BoxStyle}>
                              {code ? <Tag color="blue">{code}</Tag> : <span style={{ color: '#999' }}>-</span>}
                            </div>
                          </>
                        );
                      })()}
                      <div style={col3BoxStyle}>
                        {(() => {
                          const ts = detail?.template_snapshot || {};
                          const td = detail?.template_detail || {};
                          const brief = ts.brief_description || td.brief_description || ts.description;
                          return brief ? (
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => setShowTplBrief(true)}
                              style={{ background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
                            >
                              查看指令模板说明
                            </Button>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </Card>
                  {/* 问题集区 */}
                  <Card
                    size="small"
                    title={(
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span>问题集</span>
                        <Space size={8}>
                          <span style={{ color: '#595959', fontSize: 12 }}>显示所有问题项</span>
                          <Switch size="small" checked={showAllQuestions} onChange={setShowAllQuestions} />
                        </Space>
                      </div>
                    )}
                  >
                    {renderQuestionnaire()}
                  </Card>
                  {/* 附件区：必交/可选分组 */}
                  <Card
                    size="small"
                    title={
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `${COL1_WIDTH}px ${COL2_WIDTH}px ${COL3_WIDTH}px`,
                          alignItems: 'center',
                        }}
                      >
                        <div><span>附件</span></div>
                        <div />
                        <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0' }}>
                          <Button
                            size="small"
                            type="primary"
                            onClick={handleBatchDownloadAll}
                            style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}
                          >
                            批量下载附件
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    {(() => {
                      const isInitialPhaseAttachment = (a: any): boolean => {
                        const meta = (a?.meta && typeof a.meta === 'object') ? a.meta : {};
                        const phase = String((meta as any)?.source_phase || (meta as any)?.phase || '').trim().toLowerCase();
                        if (phase) return phase === 'initial';
                        const from = String((meta as any)?.from || '').trim().toLowerCase();
                        if (['step_attachment', 'stage_submission', 'ioc_with_contents', 'baseline_file', 'final_review'].includes(from)) return false;
                        const label = String(a?.label || '').trim().toLowerCase();
                        if (label === 'final_review') return false;
                        return true;
                      };
                      const rawList = (Array.isArray(detail?.attachments_detail) && detail.attachments_detail.length > 0)
                        ? detail.attachments_detail
                        : (attachments || []);
                      const initialOnlyList = Array.isArray(rawList) ? rawList.filter((a: any) => isInitialPhaseAttachment(a)) : [];
                      const waybillLabelSet = new Set(['物流运单', '运输标签(物流运单/箱唛/托盘标)']);
                      const ans = (detail?.customer_answers || {}) as any;
                      const waybillSource = normalizeWaybillSource(ans?.waybill_source);
                      const shouldHideWarehouseWaybill = !isAdminOrStaff && waybillSource === 'warehouse';
                      const list = shouldHideWarehouseWaybill
                        ? initialOnlyList.filter((a: any) => !waybillLabelSet.has(String(a?.label || '').trim()))
                        : initialOnlyList;
                      const ts = (detail?.template_snapshot || {}) as any;
                      const td = (detail?.template_detail || {}) as any;
                      const reqRoot = (ts?.attachment_requirements || td?.attachment_requirements || {}) as any;

                      const normalize = (v: any): string => (String(v || '').trim().toLowerCase());

                      const reqLabelSet = new Set<string>();
                      const reqMimeSet = new Set<string>();
                      const reqExtSet = new Set<string>();
                      const requiredScope: Record<string, boolean> = {};
                      let requiredGlobal = false;

                      const collectRequired = (node: any, scope?: string) => {
                        if (!node) return;
                        const sc = node.scope || scope; // 继承上层scope

                        // 显式 required=true 或 min/min_count>=1 则标记该scope为必交
                        const min = Number(node.min ?? node.min_count ?? 0);
                        if (node.required === true || (!isNaN(min) && min >= 1)) {
                          if (sc) requiredScope[String(sc)] = true; else requiredGlobal = true;
                        }

                        // 数组 required 列表
                        const reqArr = Array.isArray(node.required) ? node.required : [];
                        reqArr.forEach((it: any) => {
                          if (typeof it === 'string') reqLabelSet.add(normalize(it));
                          else if (it && typeof it === 'object') {
                            if (it.label) reqLabelSet.add(normalize(it.label));
                            if (it.mime_type) reqMimeSet.add(normalize(it.mime_type));
                            if (it.ext) reqExtSet.add(normalize(it.ext));
                          }
                        });

                        // 节点自身的可识别键
                        if (node.label) reqLabelSet.add(normalize(node.label));
                        if (node.mime_type) reqMimeSet.add(normalize(node.mime_type));
                        if (node.ext) reqExtSet.add(normalize(node.ext));

                        if (Array.isArray(node)) node.forEach((child) => collectRequired(child, sc));
                        else if (typeof node === 'object') {
                          Object.entries(node).forEach(([key, child]) => {
                            // 识别常见作用域键
                            const nextScope = ['order','orders','action','actions','*'].includes(String(key)) ? String(key) : sc;
                            collectRequired(child, nextScope);
                          });
                        }
                      };

                      try { collectRequired(reqRoot); } catch {}

                      // 移除调试信息构建与输出

                      const isRequiredAttachment = (a: any): boolean => {
                        const lbl = normalize(a?.label);
                        const mime = normalize(a?.mime_type);
                        // 从文件名推断扩展名
                        const name = String(a?.file_name || a?.file || a?.file_path || '');
                        const ext = normalize(name.includes('.') ? name.split('.').pop() : '');
                        const sc = String(a?.scope || 'order');

                        if (requiredGlobal) return true;
                        if (lbl && reqLabelSet.has(lbl)) return true;
                        if (mime && reqMimeSet.has(mime)) return true;
                        if (ext && reqExtSet.has(ext)) return true;
                        if (requiredScope[sc] === true) return true;
                        return false;
                      };

                      let required: any[] = [];
                      let optional: any[] = [];
                      if (list.length > 0 && typeof list[0]?.required === 'boolean') {
                        required = list.filter((a:any) => a.required === true);
                        optional = list.filter((a:any) => a.required !== true);
                      } else {
                        required = list.filter((a:any) => isRequiredAttachment(a));
                        optional = list.filter((a:any) => !isRequiredAttachment(a));
                      }

                      const paperSizeLabel = (v: string) => {
                        if (v === '4x6_thermal') return '4×6英寸热敏纸打印';
                        if (v === 'A4') return 'A4激光打印';
                        return v || '-';
                      };
                      const placementLabel = (v: string) => {
                        if (v === 'inside') return '放入包裹内';
                        if (v === 'outside') return '贴在包裹外';
                        return v || '-';
                      };
                      const renderGroup = (title: string, arr: any[], isRequiredGroup?: boolean) => (
                        <>
                          <div style={{ margin: '8px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontWeight: 600 }}>{title}</span>
                            {arr.length > 0 ? (
                              <span style={{ fontSize: 12, color: '#595959' }}>{`已上传 ${arr.length}个附件`}</span>
                            ) : null}
                          </div>
                          <div className="space-y-2">
                            {arr.map((a:any) => {
                              const pc = a?.meta?.print_config;
                              const fileName = a.file_name || a.file_path || a.file;
                              const printText = pc?.print === true
                                ? `打印设置：${pc.copies ?? '-'} 份 · ${paperSizeLabel(pc.paper_size)} · ${placementLabel(pc.placement)}`
                                : null;
                              const printTextColor = '#ff4d4f';
                              return (
                              <div key={a.id} style={rowBoxStyle}>
                                <div style={col1BoxStyle}>
                                  {isRequiredGroup ? <Star /> : null}
                                  <span>{a.label || a.mime_type || '附件'}</span>
                                </div>
                                <div style={{ ...col2BoxStyle, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span>{fileName}</span>
                                  {printText ? <span style={{ fontSize: 12, color: printTextColor }}>{printText}</span> : null}
                                </div>
                                <div style={col3BoxStyle}>
                                  <Space>
                                    <Button size="small" type="primary" onClick={() => previewAttachment(a)}>预览</Button>
                                    <Button
                                      size="small"
                                      onClick={() => handlePreviewDownload(a)}
                                      style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}
                                    >
                                      下载
                                    </Button>
                                  </Space>
                                </div>
                              </div>
                            ); })}
                            {arr.length === 0 && (
                              <div style={{ color: '#999' }}>无</div>
                            )}
                          </div>
                        </>
                      );

                      return (
                        <div className="space-y-4" style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 8 }}>
                          {renderGroup('必须提交', required, true)}
                          {renderGroup('可选择提交', optional, false)}
                          {/* 调试信息已移除 */}
                        </div>
                      );
                    })()}
                  </Card>
                </div>
              )
            },
            ...(isAdminOrStaff ? [{
              key: 'plan_and_route_admin',
              label: '工单计划及路由(管理端)',
              children: (
                <div style={{ padding: 8, minHeight: 200 }}>
                  <Card size="small" style={{ marginBottom: 12 }} title="最终工单计划（预览）" extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadPlanPreview} loading={planLoading}>刷新预览</Button>}>
                    {(() => {
                      if (planLoading) return <div style={{ color:'#999' }}>加载中…</div>;
                      if (!planPreview) return <div style={{ color:'#999' }}>暂无计划（等待受理或配置默认工单模板）</div>;
                      return (
                        <div>
                          {(Array.isArray(planPreview.errors) && planPreview.errors.length>0) && (
                            <div style={{ color:'#d4380d', marginBottom: 6 }}>错误：{planPreview.errors.join('；')}</div>
                          )}
                          {(Array.isArray(planPreview.warnings) && planPreview.warnings.length>0) && (
                            <div style={{ color:'#fa8c16', marginBottom: 6 }}>警告：{planPreview.warnings.join('；')}</div>
                          )}
                          <Table
                            size="small"
                            rowKey={(_r:any, i?:number)=> String(i ?? 0)}
                            dataSource={Array.isArray(planPreview.steps) ? planPreview.steps : []}
                            pagination={false}
                            columns={[
                              { title: '工单模板', dataIndex: 'template_code' },
                              { title: '名称', dataIndex: 'template_name' },
                              { title: '操作类型', dataIndex: 'operation_type' },
                              { title: '依赖', dataIndex: 'after', render: (v:any)=> (Array.isArray(v) && v.length>0) ? v.join('、') : '-' },
                            ] as any}
                          />
                        </div>
                      );
                    })()}
                  </Card>

                  <Card size="small" style={{ marginBottom: 12 }} title="工单路由 / DAG">
                    {(() => {
                      if (!woList || woList.length === 0) return <span style={{ color: '#999' }}>暂无工单</span>;
                      const idSet = new Set<number>();
                      const idToInfo = new Map<number, { sys: string; name: string; tplCode: string }>();
                      woList.forEach((w:any) => {
                        if (w?.id) {
                          const id = Number(w.id);
                          idSet.add(id);
                          const sys = (w?.system_code || `WO#${id}`) as string;
                          const name = (w?.template_snapshot?.name || w?.template?.name || '') as string;
                          const tplCode = (w?.type_code || w?.template_snapshot?.code || w?.template?.code || '') as string;
                          idToInfo.set(id, { sys, name, tplCode });
                        }
                      });
                      const dependsMap: Record<string, number[]> = {};
                      woList.forEach((w:any) => {
                        const ids = (w?.context?.depends_on_wo_ids || []) as number[];
                        const raw = Array.isArray(ids) ? ids : [];
                        // 仅保留当前集合内的依赖，去重并排除自依赖
                        const uniq = Array.from(new Set(raw.map(n => Number(n))));
                        const filtered = uniq.filter(x => idSet.has(Number(x)) && Number(x) !== Number(w.id));
                        dependsMap[String(w.id)] = filtered;
                      });
                      // 依赖反向映射：被哪些节点依赖
                      const reverseMap: Record<number, number[]> = {};
                      Array.from(idSet).forEach(id => reverseMap[id] = []);
                      Object.entries(dependsMap).forEach(([tid, arr]) => {
                        arr.forEach((dep) => { reverseMap[dep].push(Number(tid)); });
                      });
                      const indeg: Record<number, number> = {};
                      idSet.forEach(id => { indeg[id] = 0; });
                      // 入度应为"该节点依赖数量"，而不是"被依赖次数"
                      Object.entries(dependsMap).forEach(([tid, arr]) => {
                        const t = Number(tid);
                        indeg[t] = (indeg[t] || 0) + (Array.isArray(arr) ? arr.length : 0);
                      });
                      const layers: number[][] = [];
                      let frontier = Array.from(idSet).filter(id => (indeg[id] || 0) === 0);
                      const used = new Set<number>();
                      while (frontier.length > 0) {
                        layers.push(frontier);
                        const next: number[] = [];
                        frontier.forEach(id => {
                          used.add(id);
                          Array.from(idSet).forEach(tid => {
                            const deps = dependsMap[String(tid)] || [];
                            if (deps.includes(id)) {
                              indeg[tid] = Math.max(0, (indeg[tid] || 0) - 1);
                              if (indeg[tid] === 0 && !used.has(tid)) next.push(tid);
                            }
                          });
                        });
                        frontier = next.filter((v, i, a) => a.indexOf(v) === i);
                      }
                      const remaining = Array.from(idSet).filter(id => !used.has(id));
                      return (
                        <div>
                          <div style={{ marginBottom: 8, color: '#666' }}>提示：同一层可并行执行，箭头指向依赖顺序。</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {layers.map((layer, idx) => (
                              <div key={idx} style={{ width: '100%' }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>第 {idx+1} 层</div>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                  {layer.map(id => {
                                    const w = woList.find((x:any) => Number(x.id) === id);
                                    const isFocus = dagFocusId != null && id === dagFocusId;
                                    const isDep = dagFocusId != null && (dependsMap[String(dagFocusId)] || []).includes(id);
                                    const isReverseDep = dagFocusId != null && (reverseMap[dagFocusId] || []).includes(id);
                                    const borderColor = isFocus ? '#fa541c' : isDep ? '#1677ff' : isReverseDep ? '#52c41a' : '#1677ff';
                                    return (
                                      <Card size="small" key={id} style={{ borderLeft: `3px solid ${borderColor}`, cursor: 'pointer' }} onMouseEnter={() => setDagFocusId(id)} onMouseLeave={() => setDagFocusId(null)}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                            {(() => {
                                              const info = idToInfo.get(id) || { sys: `WO#${id}`, name: '', tplCode: '' };
                                              const display = `${info.sys}${info.name ? ` - ${info.name}` : ''}`;
                                              const title = `${info.sys}${info.name ? ` · ${info.name}` : ''}${info.tplCode ? ` · ${info.tplCode}` : ''}`;
                                              return (
                                                <Tooltip title={title}>
                                                  <span style={{ maxWidth: 560, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                                                </Tooltip>
                                              );
                                            })()}
                                            {/* 工单详情页已移除：避免死链 */}
                                          </div>
                                          {(dependsMap[String(id)] || []).length > 0 && (() => {
                                            const arr = (dependsMap[String(id)] || []);
                                            const texts = arr.map((depId:number) => {
                                              const info = idToInfo.get(depId) || { sys: `WO#${depId}`, name: '', tplCode: '' };
                                              return `${info.sys}${info.name ? ` - ${info.name}` : ''}`;
                                            });
                                            return <span style={{ color: '#999' }}>依赖：{texts.join('、')}</span>;
                                          })()}
                                        </Space>
                                      </Card>
                                    );
                                  })}
                                </Space>
                              </div>
                            ))}
                          </div>
                          {remaining.length > 0 && (
                            <div style={{ marginTop: 8, color: '#fa8c16' }}>检测到循环依赖或未解析节点：{remaining.map(id=>`#${id}`).join('、')}</div>
                          )}
                        </div>
                      );
                    })()}
                  </Card>
                </div>
              )
            }] : []),
            {
              key: 'logs_and_results',
              label: '日志',
              children: (
              <div style={{ padding: 8, minHeight: 200 }}>
                {detail?.reject_reason ? (
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <Space>
                      <Tag color="red">驳回原因</Tag>
                      <span style={{ whiteSpace: 'pre-wrap' }}>{detail.reject_reason}</span>
                    </Space>
                  </Card>
                ) : null}
                {isAdminPath && billingSummary ? (
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <Space>
                      <Tag color="blue">指令计费汇总</Tag>
                      <span>
                        {(() => {
                          const t = billingSummary?.totals || {};
                          const keys = Object.keys(t);
                          if (keys.length === 0) return '暂无收费记录';
                          return keys.map((k) => `${k} ${t[k]}`).join('；');
                        })()}
                      </span>
                    </Space>
                  </Card>
                ) : null}
              {isAdminOrStaff && (
                <div style={{ display:'flex', justifyContent:'flex-start', marginBottom: 8 }}>
                    <Button type="primary" onClick={() => setAddNoteOpen(true)}>添加日志</Button>
                  </div>
                )}
                <Table
                  size="small"
                  rowKey={(r:any, index?: number) => String(r?.id ?? index)}
                  dataSource={logs}
                  loading={logLoading}
                  columns={[
                    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v:any) => v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-' },
						{ title: '事件', dataIndex: 'event_type', key: 'event_type', width: 220, render: (_v:any, r:any) => eventTextWithRow(r) },
                    { title: '操作人', key: 'actor', width: 160, render: (_:any, r:any) => {
                      const snap = r?.actor_snapshot || {};
                      const snapName = snap?.display_name || snap?.username;
                      if (snapName) return <span>{snapName}</span>;
                      const direct = r?.actor_username;
                      if (direct) return <span>{direct}</span>;
                      const aid = r?.actor_id != null ? String(r.actor_id) : '';
                      const name = aid ? (actorNameMap[aid] || `用户${aid}`) : '-';
                      return <span>{name}</span>;
                    } },
                    { title: '级别', dataIndex: 'severity', key: 'severity', width: 90, render: (v:any) => {
                      const val = String(v || 'info');
                      const text = val === 'warn' ? '警告' : val === 'error' ? '错误' : '信息';
                      return <Tag color={severityColor(val)}>{text}</Tag>;
                    }},
                    { title: '内容', key: 'content', render: (_:any, r:any) => (
                      <Space size={8}>
                        {renderLogContent(r)}
                        <Button size="small" type="link" onClick={() => setLogInspect(r)}>详情</Button>
                      </Space>
                    ) },
                  ]}
                  pagination={{
                    current: logsPage,
                    pageSize: logsPageSize,
                    total: logsTotal,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10','20','50','100'],
                    onChange: (p:number, ps:number) => { setLogsPage(p); setLogsPageSize(ps); },
                  }}
                />
                <Modal
                  title={logInspect ? `日志详情 #${logInspect?.id}` : '日志详情'}
                  open={!!logInspect}
                  onCancel={() => setLogInspect(null)}
                  footer={null}
                  width={720}
                >
                  {logInspect && (
                    <div>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <div>
                          <Text type="secondary">时间：</Text>
                          <Text>{logInspect?.created_at ? String(logInspect.created_at) : '-'}</Text>
                        </div>
                        <div>
                          <Text type="secondary">事件：</Text>
							<Text>{eventTextWithRow(logInspect) || '-'}</Text>
                        </div>
                        <div>
                          <Text type="secondary">级别：</Text>
                          <Tag color={severityColor(String(logInspect?.severity||'info'))}>{logInspect?.severity || 'info'}</Tag>
                        </div>
                        <div>
                          <Text type="secondary">内容：</Text>
                          <div>{renderLogContent(logInspect)}</div>
                        </div>
                        {isAdminOrStaff && (
                          <>
                            <div>
                              <Text type="secondary">详情：</Text>
                              <div>{renderReadableContext(String(logInspect?.event_type||''), logInspect?.context || {})}</div>
                            </div>
                            <Collapse bordered={false} style={{ background: 'transparent' }}>
                              <Collapse.Panel header="原始上下文(JSON)" key="raw">
                                <pre style={{ background: '#fafafa', padding: 12, border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 360, overflow: 'auto' }}>
                                  {JSON.stringify(logInspect?.context || {}, null, 2)}
                                </pre>
                              </Collapse.Panel>
                            </Collapse>
                          </>
                        )}
                      </Space>
                    </div>
                  )}
                </Modal>
                {/* 添加日志弹窗 */}
                <Modal
                  title="添加日志"
                  open={addNoteOpen}
                  onCancel={() => { if (!addNoteSubmitting) setAddNoteOpen(false); }}
                  onOk={async () => {
                    if (!instructionId) return;
                    const text = String(noteText || '').trim();
                    if (!text) { message.warning('请输入日志内容'); return; }
                    setAddNoteSubmitting(true);
                    try {
                      await request.post(`/api/v1/instruction/instructions/${instructionId}/logs/add-note/`, {
                        text,
                        severity: noteSeverity,
                        visibility: noteVisibility,
                      });
                      message.success('已添加');
                      setAddNoteOpen(false);
                      setNoteText('');
                      setNoteSeverity('info');
                      setNoteVisibility('public');
                      fetchInstructionLogs();
                    } catch (e:any) {
                      message.error(e?.response?.data?.detail || '添加失败');
                    } finally {
                      setAddNoteSubmitting(false);
                    }
                  }}
                  okText="提交"
                  cancelText="取消"
                  confirmLoading={addNoteSubmitting}
                >
                  <div className="space-y-2">
                    <div>
                      <div style={{ marginBottom: 6 }}>日志内容</div>
                      <Input.TextArea
                        value={noteText}
                        onChange={(e)=> setNoteText(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        showCount
                        placeholder="请输入要记录在该指令下的备注/进展（仅管理员与员工可添加）"
                      />
                    </div>
                    <div style={{ display:'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 6 }}>可见性</div>
                        <Select
                          value={noteVisibility}
                          onChange={(v)=> setNoteVisibility(v as any)}
                          style={{ width: '100%' }}
                          options={[
                            { label: '内部（仅管理/员工可见）', value: 'internal' },
                            { label: '公开（客户也可见）', value: 'public' },
                          ]}
                        />
                      </div>
                      <div style={{ width: 200 }}>
                        <div style={{ marginBottom: 6 }}>级别</div>
                        <Select
                          value={noteSeverity}
                          onChange={(v)=> setNoteSeverity(v as any)}
                          style={{ width: '100%' }}
                          options={[
                            { label: '信息', value: 'info' },
                            { label: '提醒', value: 'warn' },
                            { label: '错误', value: 'error' },
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                </Modal>
              </div>
              )
            },
            {
              key: 'exec_results',
              label: '指令执行结果',
              children: (
                <div style={{ padding: 8, minHeight: 200 }}>
                  {(() => {
                    const list = (Array.isArray(detail?.attachments_detail) && detail.attachments_detail.length > 0)
                      ? detail.attachments_detail
                      : [];
                    const finalReviewAtts = list
                      .filter((a: any) => String(a?.label || '').trim().toLowerCase() === 'final_review')
                      .filter((a: any) => String(a?.status || '').trim().toLowerCase() !== 'archived');

                    const rankOf = (a: any): number => {
                      const ext = String(getExtLower(a) || '').trim().toLowerCase();
                      if (['xlsx', 'xls', 'csv'].includes(ext)) return 1; // Excel
                      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 2; // 图片
                      if (ext === 'pdf') return 3; // PDF
                      return 9; // 其他
                    };
                    const tsOf = (a: any): number => {
                      try {
                        const s = a?.created_at || a?.updated_at || '';
                        const t = new Date(String(s)).getTime();
                        return Number.isFinite(t) ? t : 0;
                      } catch { return 0; }
                    };
                    const sorted = [...finalReviewAtts].sort((a: any, b: any) => {
                      const ra = rankOf(a);
                      const rb = rankOf(b);
                      if (ra !== rb) return ra - rb;
                      return tsOf(b) - tsOf(a);
                    });
                    const noteText = String(
                      (detail as any)?.final_review_notes
                      ?? (detail as any)?.meta?.final_review_notes
                      ?? ''
                    ).trim();
                    const allIocRows = Array.isArray(execResultIocRows) ? execResultIocRows : [];
                    const normalIocCount = allIocRows.filter((r:any) => String(r?.output_content_kind || '').toLowerCase() !== 'exception').length;
                    const exceptionIocCount = allIocRows.filter((r:any) => String(r?.output_content_kind || '').toLowerCase() === 'exception').length;
                    const totalIocCount = allIocRows.length;

                    const allKeys = sorted.map((a: any, idx: number) => String(a?.id ?? idx)).filter(Boolean);
                    const sel = execResultSelectedKeys.length;
                    const checkedAll = allKeys.length > 0 && sel === allKeys.length;
                    const indeterminate = sel > 0 && allKeys.length > 0 && sel < allKeys.length;

                    const doBatchDownload = async () => {
                      if (execResultSelectedKeys.length === 0) {
                        message.warning('请先选择要下载的附件');
                        return;
                      }
                      const keySet = new Set(execResultSelectedKeys);
                      const picked = sorted.filter((a: any, idx: number) => keySet.has(String(a?.id ?? idx)));
                      if (picked.length === 0) {
                        message.warning('未找到选中的附件');
                        return;
                      }
                      message.success(`开始批量下载：${picked.length} 个文件`);
                      // 逐个触发下载（浏览器可能会弹出多次下载提示，符合“批量下载”预期）
                      for (const a of picked) {
                        try { handlePreviewDownload(a); } catch {}
                      }
                    };

                    return (
                      <div>
                        <Card
                          size="small"
                          title={(
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 }}>
                              <span style={{ justifySelf: 'start' }}>指令产出容器（IOC）</span>
                              <span style={{ justifySelf: 'center', fontWeight: 600 }}>
                                <span style={{ color: '#389e0d' }}>正常内容容器 {normalIocCount}</span>
                                {isAdminOrStaff ? (
                                  <>
                                    <span style={{ color: '#595959' }}>  | </span>
                                    <span style={{ color: '#fa8c16' }}>异常内容容器 {exceptionIocCount}</span>
                                    <span style={{ color: '#595959' }}> | </span>
                                    <span style={{ color: '#1677ff' }}>产出容器总数量 {totalIocCount}</span>
                                  </>
                                ) : null}
                              </span>
                              <span />
                            </div>
                          )}
                          style={{ marginBottom: 12 }}
                          extra={(
                            <Button size="small" type="primary" style={{ marginRight: 40 }} onClick={downloadExecResultIocList}>
                              下载
                            </Button>
                          )}
                          headStyle={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
                          bodyStyle={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
                        >
                          <style>{`
                            .exec-result-ioc-table-wrap { position: relative; }
                            .exec-result-ioc-table-wrap .ant-table-pagination,
                            .exec-result-ioc-table-wrap .ant-table-pagination-right {
                              display: flex !important;
                              justify-content: flex-end !important;
                              align-items: center !important;
                              width: 100% !important;
                            }
                            .exec-result-ioc-table-wrap .ant-pagination-total-text {
                              margin-left: 0 !important;
                              position: absolute !important;
                              left: 0 !important;
                              transform: translateY(0);
                            }
                            .exec-result-ioc-table .ant-table-tbody > tr.exec-ioc-group-row > td {
                              border-left: 2px solid #91caff !important;
                              border-right: 2px solid #91caff !important;
                            }
                            .exec-result-ioc-table .ant-table-tbody > tr.exec-ioc-group-start > td {
                              border-top: 2px solid #91caff !important;
                            }
                            .exec-result-ioc-table .ant-table-tbody > tr.exec-ioc-group-end > td {
                              border-bottom: 2px solid #91caff !important;
                            }
                          `}</style>
                          <div className="exec-result-ioc-table-wrap">
                          <Table
                            className="exec-result-ioc-table"
                            size="small"
                            rowKey={(r:any, index?: number) => String(r?.__key || r?.output_container_id || r?.output_code || index)}
                            dataSource={execResultIocSortedRows}
                            columns={execResultIocColumns as any}
                            loading={execResultIocLoading}
                            onRow={(record: any) => {
                              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || '');
                              const borderMeta = execResultIocVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                              return {
                                style: {
                                  background: execResultIocVisualMeta.rowBgMap.get(rowKey) || '#ffffff',
                                  borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                                  borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                                  borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                                  borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                                },
                              };
                            }}
                            rowClassName={(record: any) => {
                              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || '');
                              const borderMeta = execResultIocVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                              if (!borderMeta.grouped) return '';
                              if (borderMeta.top) return 'exec-ioc-group-row exec-ioc-group-start';
                              if (borderMeta.bottom) return 'exec-ioc-group-row exec-ioc-group-end';
                              return 'exec-ioc-group-row';
                            }}
                            scroll={{ x: 1800 }}
                            pagination={{
                              defaultPageSize: 10,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              pageSizeOptions: ['10','20','50','100'],
                              showTotal: () => (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: '#595959', marginLeft: 0 }}>
                                  <ExclamationCircleFilled style={{ color: '#ff4d4f', marginTop: 2 }} />
                                  <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.6 }}>
                                    <span>同一外边框中多个容器具有父/子层级关系</span>
                                    <span>若需格式化后的完整包装数据,可参考下方"请查看或下载执行结果附件"板块中的"总包装数据"附件表格</span>
                                  </span>
                                </div>
                              ),
                            }}
                            locale={{ emptyText: '暂无指令产出容器（IOC）' }}
                          />
                          </div>
                        </Card>
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Card
                            size="small"
                            title="请查看或下载执行结果附件"
                            style={{ flex: '1 1 680px', minWidth: 520 }}
                            headStyle={{ background: '#e6f4ff', borderColor: '#91caff' }}
                            bodyStyle={{ background: '#e6f4ff', borderColor: '#91caff' }}
                            extra={
                              <Button
                                type="primary"
                                style={{ marginTop: 6 }}
                                onClick={() => { void doBatchDownload(); }}
                                disabled={execResultSelectedKeys.length === 0}
                              >
                                批量下载
                              </Button>
                            }
                          >
                          <Table
                            size="small"
                            rowKey={(r:any, index?: number) => String(r?.id ?? index)}
                            dataSource={sorted}
                            columns={[
                              {
                                title: (
                                  <Checkbox
                                    checked={checkedAll}
                                    indeterminate={indeterminate}
                                    onChange={() => {
                                      if (checkedAll) setExecResultSelectedKeys([]);
                                      else setExecResultSelectedKeys(allKeys);
                                    }}
                                  >
                                    选择
                                  </Checkbox>
                                ),
                                key: '__sel',
                                width: 90,
                                render: (_: any, r: any, idx: number) => {
                                  const k = String(r?.id ?? idx);
                                  const checked = execResultSelectedKeys.includes(k);
                                  return (
                                    <Checkbox
                                      checked={checked}
                                      onChange={() => {
                                        setExecResultSelectedKeys((prev) => {
                                          const s = new Set(prev || []);
                                          if (s.has(k)) s.delete(k); else s.add(k);
                                          return Array.from(s);
                                        });
                                      }}
                                    />
                                  );
                                },
                              },
                              {
                                title: '文件名',
                                key: 'file_name',
                                render: (_: any, r: any) => (
                                  <span style={{ fontFamily: 'monospace' }}>{String(r?.file_name || r?.file || r?.file_path || '-')}</span>
                                ),
                              },
                              {
                                title: '类型',
                                key: 'ext',
                                width: 110,
                                render: (_: any, r: any) => <span style={{ fontFamily: 'monospace' }}>{String(getExtLower(r) || '-')}</span>,
                              },
                              {
                                title: '操作',
                                key: 'ops',
                                width: 180,
                                render: (_: any, r: any) => (
                                  <Space size={6}>
                                    <Button size="small" onClick={() => previewAttachment(r)}>预览</Button>
                                    <Button size="small" type="primary" onClick={() => handlePreviewDownload(r)}>下载</Button>
                                  </Space>
                                ),
                              },
                            ]}
                            pagination={{
                              defaultPageSize: 10,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              pageSizeOptions: ['10','20','50','100'],
                              showTotal: (total:number, range:[number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                            }}
                          />
                          </Card>

                          <Card
                            size="small"
                            title="仓库备注"
                            style={{ flex: '1 1 420px', minWidth: 360 }}
                            headStyle={{ background: '#fff7e6', borderColor: '#ffd591' }}
                            bodyStyle={{ background: '#fff7e6', borderColor: '#ffd591' }}
                          >
                            <Input.TextArea
                              value={noteText || ''}
                              placeholder="（无终审备注）"
                              rows={10}
                              readOnly
                              style={{ background: 'rgba(255,255,255,0.85)' }}
                            />
                          </Card>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ),
            },
          ]}
        />
      </Card>




      {/* 附件预览弹窗 */}
      <Modal
        open={attPreviewOpen}
        onCancel={() => {
          // 条目视图（blob） -> 返回列表视图
          if (attPvUrl && attPvUrl.startsWith('blob:')) {
            try { URL.revokeObjectURL(attPvUrl); } catch {}
            if (attPvArchList && attPvArchList.length > 0 && attPvArchiveRoot) {
              setAttPvKind('other');
              setAttPvUrl(attPvArchiveRoot);
              setAttPvEntryName('');
              return;
            }
          }
          // 列表视图 -> 关闭弹窗
          closeAttPreview();
          setAttPvArchiveRoot('');
          setAttPvEntryName('');
        }}
        footer={null}
        width={1280}
        destroyOnClose
        title={<span style={{ color: '#1677ff', fontWeight: 700 }}>
          {attPvTitle || '附件预览'}{attPvEntryName ? ` / ${attPvEntryName}` : ''}
        </span>}
      >
        {attPvLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
          attPvError ? <div style={{ color:'#cf1322' }}>{attPvError}</div> : (
            // 自定义：当 attPvUrl 以 "arc:" 开头时，展示压缩包文件列表（仅前端解压预览，不改后端附件）
            (attPvUrl && (attPvUrl.startsWith('arc:') || attPvUrl.startsWith('zip:'))) ? (
              <div>
                <div style={{ marginBottom: 8, color:'#666' }}>在线解压（只读预览，不改变原附件）：{attPvArchList.length} 项</div>
                <Table
                  size="small"
                  rowKey={(r:any, i?:number)=> String(r?.path || i)}
                  dataSource={attPvArchList}
                  pagination={{
                    current: attPvArchPage,
                    pageSize: attPvArchPageSize,
                    total: attPvArchList.length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10','20','50','100'] as any,
                    showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                    onChange: (p:number, ps:number) => { setAttPvArchPage(p); setAttPvArchPageSize(ps); },
                  }}
                  columns={[
                    { title: '序号', key: '__idx', width: 80, align: 'center', render: (_:any, __:any, idx:number) => ((attPvArchPage - 1) * attPvArchPageSize + idx + 1) },
                    { title: '路径', dataIndex: 'path', render: (v:any, r:any) => r.dir ? <span style={{ color:'#8c8c8c' }}>[目录] {v}</span> : v },
                    { title: '操作', key: 'ops', width: 200, render: (_:any, r:any) => {
                      if (r.dir) return <span style={{ color:'#999' }}>-</span>;
                      return (
                        <Space size={8}>
                          <Button size="small" onClick={async () => {
                            try {
                              let ab: ArrayBuffer;
                              const kind = attPvArchiveKind || 'zip';
                              const buf = attPvArchiveBuf;
                              if (!buf && kind !== 'rar') throw new Error('归档数据未缓存');
                              if (kind === 'zip') {
                                let JSZip: any = null;
                                try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
                                if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
                                const zip = await JSZip.loadAsync(buf);
                                const file = zip.file(r.path);
                                if (!file) throw new Error('条目不存在');
                                ab = await file.async('arraybuffer');
                              } else if (kind === 'rar') {
                                const id = attPvArchiveAttachId;
                                if (!id) throw new Error('缺少附件ID');
                                const api = `/api/v1/instruction/attachments/${id}/archive/entry/`;
                                const res = await (request as any).get(api, { params: { path: r.path }, responseType: 'blob', withCredentials: true });
                                const blobObj = res?.data as Blob;
                                ab = await blobObj.arrayBuffer();
                              } else if (kind === 'tar') {
                                if (!buf) throw new Error('归档数据未缓存');
                                const tarBuf = buf as ArrayBuffer;
                                const entries = parseTarEntriesFromBuffer(tarBuf);
                                const hit = entries.find(e => e.path === r.path && !e.dir);
                                if (!hit) throw new Error('条目不存在');
                                ab = tarBuf.slice(hit.offset, hit.offset + hit.size);
                              } else {
                                throw new Error('该归档类型的条目预览暂不支持');
                              }
                              const ext = String(r.path || '').toLowerCase().split('.').pop() || '';
                              const imgSet = new Set(['png','jpg','jpeg','webp','gif','apng','svg','bmp','ico']);
                              const mime = ext === 'pdf'
                                ? 'application/pdf'
                                : imgSet.has(ext) ? (
                                    ext === 'svg' ? 'image/svg+xml'
                                    : ext === 'ico' ? 'image/x-icon'
                                    : ext === 'jpg' ? 'image/jpeg'
                                    : `image/${ext}`
                                  )
                                : '';
                              const blob = new Blob([ab], { type: mime || undefined });
                              const href = URL.createObjectURL(blob);
                              if (ext === 'pdf') {
                                // 在当前预览弹窗内展示 PDF
                                setAttPvKind('pdf');
                                setAttPvUrl(href);
                                try {
                                  const base = String(r.path || '');
                                  const name = base.split('/').pop() || base;
                                  setAttPvEntryName(name);
                                } catch { setAttPvEntryName(''); }
                              } else if (imgSet.has(ext)) {
                                // 在当前预览弹窗内展示图片
                                setAttPvKind('image' as any);
                                setAttPvUrl(href);
                                try {
                                  const base = String(r.path || '');
                                  const name = base.split('/').pop() || base;
                                  setAttPvEntryName(name);
                                } catch { setAttPvEntryName(''); }
                              } else {
                                // 其他类型：新窗口打开
                                const a = document.createElement('a');
                                a.href = href;
                                a.target = '_blank';
                                a.rel = 'noopener';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                setTimeout(()=> URL.revokeObjectURL(href), 2000);
                              }
                            } catch { message.error('预览失败'); }
                          }}>预览</Button>
                          <Button size="small" type="primary" onClick={async () => {
                            try {
                              let ab: ArrayBuffer;
                              const kind = attPvArchiveKind || 'zip';
                              const buf = attPvArchiveBuf;
                              if (!buf && kind !== 'rar') throw new Error('归档数据未缓存');
                              if (kind === 'zip') {
                                let JSZip: any = null;
                                try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
                                if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
                                const zip = await JSZip.loadAsync(buf);
                                const file = zip.file(r.path);
                                if (!file) throw new Error('条目不存在');
                                ab = await file.async('arraybuffer');
                              } else if (kind === 'rar') {
                                const id = attPvArchiveAttachId;
                                if (!id) throw new Error('缺少附件ID');
                                const api = `/api/v1/instruction/attachments/${id}/archive/entry/`;
                                const res = await (request as any).get(api, { params: { path: r.path }, responseType: 'blob', withCredentials: true });
                                const blobObj = res?.data as Blob;
                                ab = await blobObj.arrayBuffer();
                              } else if (kind === 'tar') {
                                if (!buf) throw new Error('归档数据未缓存');
                                const tarBuf = buf as ArrayBuffer;
                                const entries = parseTarEntriesFromBuffer(tarBuf);
                                const hit = entries.find(e => e.path === r.path && !e.dir);
                                if (!hit) throw new Error('条目不存在');
                                ab = tarBuf.slice(hit.offset, hit.offset + hit.size);
                              } else {
                                throw new Error('该归档类型的条目下载暂不支持');
                              }
                              const ext = String(r.path || '').toLowerCase().split('.').pop() || '';
                              const mime = ext === 'pdf' ? 'application/pdf' : '';
                              const blob = new Blob([ab], { type: mime || undefined });
                              const href = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = href;
                              a.download = r.path.split('/').pop() || 'entry';
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              setTimeout(()=> URL.revokeObjectURL(href), 2000);
                            } catch { message.error('下载失败'); }
                          }}>下载条目</Button>
                        </Space>
                      );
                    }}
                  ] as any}
                />
              </div>
            ) : (attPvKind as any) === 'video' && attPvUrl ? (
              <div>
                <video key={attPvUrl} src={attPvUrl} controls preload="metadata" style={{ width: '100%', maxHeight: '70vh', background: '#000', borderRadius: 4 }} />
                <Space style={{ marginTop: 12 }} wrap>
                  <Button
                    loading={attPvVideoDownloading}
                    onClick={async () => {
                      setAttPvVideoDownloading(true);
                      try { await downloadFileAsBlob(attPvUrl, attPvVideoName); } catch { message.error('下载失败'); } finally { setAttPvVideoDownloading(false); }
                    }}
                  >下载</Button>
                  <Button onClick={() => window.open(attPvUrl, '_blank')}>在新窗口打开</Button>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>如果无法播放（例如 iPhone 拍摄的 HEVC 编码 .mov），请下载后用本地播放器打开。</span>
                </Space>
              </div>
            ) : attPvKind === 'pdf' ? (
              <iframe title="pdf" src={attPvUrl} style={{ width:'100%', height: 600, border: 0 }} />
            ) : (attPvKind as any) === 'image' ? (
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
                <img alt={attPvEntryName || 'image'} src={attPvUrl} style={{ maxWidth:'100%', maxHeight: 680, objectFit: 'contain' }} />
              </div>
            ) : attPvKind === 'csv' || attPvKind === 'xlsx' ? (
              <div>
                {attPvKind === 'xlsx' && attPvSheetNames.length > 1 ? (
                  <Tabs
                    size="small"
                    activeKey={attPvActiveSheet || attPvSheetNames[0]}
                    onChange={(k) => {
                      const name = String(k || '').trim();
                      setAttPvActiveSheet(name);
                      setAttPvSheetName(name);
                      // 切换 sheet：优先缓存，否则即时解析
                      try {
                        const cached = attPvXlsxCacheRef.current?.[name];
                        if (cached) {
                          setAttPvCols(cached.cols || []);
                          setAttPvData(cached.data || []);
                          return;
                        }
                        const wb = attPvXlsxWbRef.current;
                        const XLSX = attPvXlsxLibRef.current;
                        const sh = wb?.Sheets?.[name];
                        const aoa: any[] = sh ? XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false }) : [];
                        const arr = Array.isArray(aoa) ? aoa.filter((r:any)=> Array.isArray(r) && r.length>0) : [];
                        if (arr.length === 0) {
                          setAttPvCols([]);
                          setAttPvData([]);
                          return;
                        }
                        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
                        const dataRows = arr.slice(1);
                        const cols = headers.map((h:any, i:number) => ({
                          title: String(h || `列${i+1}`),
                          dataIndex: `c${i}`,
                          align: 'center',
                          width: undefined,
                        }));
                        const data = dataRows.map((row:any[], idx:number) => {
                          const obj: any = { __idx: idx + 1 };
                          cols.forEach((c:any, i:number) => { obj[c.dataIndex] = String(row?.[i] ?? ''); });
                          return obj;
                        });
                        attPvXlsxCacheRef.current[name] = { cols, data };
                        setAttPvCols(cols);
                        setAttPvData(data);
                      } catch {}
                    }}
                    items={attPvSheetNames.map((name) => ({
                      key: name,
                      label: <span style={{ fontFamily: 'monospace' }}>{name}</span>,
                    }))}
                  />
                ) : (attPvSheetName ? <div style={{ marginBottom: 6, color: '#666' }}>工作表：{attPvSheetName}</div> : null)}
                <Table
                  size="small"
                  rowKey={(r:any, i?:number)=> String(r?.__idx ?? i ?? 0)}
                  dataSource={attPvData}
                  pagination={{
                    pageSize: attPvPageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100] as any,
                    onShowSizeChange: (_:number, size:number) => setAttPvPageSize(size),
                    onChange: (_page:number, size:number) => { if (size !== attPvPageSize) setAttPvPageSize(size); },
                  }}
                  columns={(() => {
                    const base = (attPvCols || []).filter(Boolean);
                    // Excel 预览：不额外添加“序号”列，只展示文件本身内容
                    const cols = (attPvKind === 'xlsx') ? base : [{ title: '序号', dataIndex: '__idx', align: 'center', width: 80 }, ...base];
                    return cols.map((col:any)=> ({
                      ...col,
                      onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center' }})
                    }));
                  })()}
                  scroll={{ y: 600 }}
                  tableLayout="fixed"
                />
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 8, color:'#666' }}>无法在线解析该格式，点击下方打开原文件：</div>
                <Button type="link" onClick={()=> { if (attPvUrl) window.open(attPvUrl, '_blank'); }}>在新窗口打开</Button>
              </div>
            )
          )
        )}
      </Modal>

      {/* 模板说明弹窗 */}
      <Modal
        title={(() => {
          const ts = detail?.template_snapshot || {};
          const td = detail?.template_detail || {};
          const code = String(ts?.system_code || ts?.code || td?.system_code || td?.code || '').trim();
          const name = String(ts?.name || td?.name || '').trim();
          const titleText = [code, name].filter(Boolean).join(' - ') || '指令模板说明';
          return (
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#1677ff', fontWeight: 700 }}>{titleText}</span>
            </div>
          );
        })()}
        open={showTplBrief}
        onCancel={() => setShowTplBrief(false)}
        footer={null}
        width={720}
      >
        {(() => {
          const ts = detail?.template_snapshot || {};
          const td = detail?.template_detail || {};
          const raw = String(ts.brief_description || td.brief_description || ts.description || '').trim();
          if (!raw) return <div>-</div>;
          const normalized = raw
            .replace(/\r\n/g, '\n')
            .replace(/(适用于[：:]|附件要求[：:]|明细要求[：:]|操作要求[：:]|执行要求[：:]|注意事项[：:]|补充说明[：:])/g, '\n$1')
            .replace(/\n{3,}/g, '\n\n');
          const blocks = normalized
            .split(/\n\s*\n/)
            .map((x) => x.trim())
            .filter(Boolean);
          const finalBlocks = blocks.length > 0
            ? blocks
            : normalized.split('\n').map((x) => x.trim()).filter(Boolean);
          return (
            <div>
              {finalBlocks.map((b, i) => (
                <div
                  key={`tpl-brief-${i}`}
                  style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.85,
                    marginBottom: i === finalBlocks.length - 1 ? 0 : 14,
                  }}
                >
                  {b}
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>
      {/* 问题说明弹窗 */}
      <Modal
        title={(
          <div style={{ textAlign: 'center' }}>
            <span style={{ color: '#1677ff', fontWeight: 700 }}>
              {qTipTitle ? `问题说明 - ${qTipTitle}` : '问题说明'}
            </span>
          </div>
        )}
        open={showQTip}
        onCancel={() => { setShowQTip(false); setQTipTitle(''); }}
        footer={null}
        width={720}
      >
        <div style={{ whiteSpace: 'pre-wrap' }}>{qTipText || '-'}</div>
      </Modal>
      {/* 包装结果/运单信息（仓库回填） */}
      <Modal
        title="填写包装与运单信息"
        open={packResultOpen}
        onCancel={() => setPackResultOpen(false)}
        onOk={savePackResult}
        confirmLoading={packResultSaving}
        width={720}
      >
        <Form form={packResultForm} layout="vertical">
          {(() => {
            const ans = (detail?.customer_answers || {}) as any;
            const waybillSource = normalizeWaybillSource(ans?.waybill_source);
            const submitMode = normalizeWaybillSubmitMode(ans?.waybill_submit_mode);
            const showPackFields = waybillSource === 'warehouse' || (waybillSource === 'upload' && submitMode === 'after_pack');
            const showWaybillFields = waybillSource === 'warehouse';
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {showPackFields ? (
                    <>
                      <Form.Item name="actual_pack_length_cm" label="实际包装长度(cm)">
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入长度" />
                      </Form.Item>
                      <Form.Item name="actual_pack_width_cm" label="实际包装宽度(cm)">
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入宽度" />
                      </Form.Item>
                      <Form.Item name="actual_pack_height_cm" label="实际包装高度(cm)">
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入高度" />
                      </Form.Item>
                      <Form.Item name="actual_gross_weight_g" label="实际包装毛重(g)">
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入毛重" />
                      </Form.Item>
                    </>
                  ) : null}
                  {showWaybillFields ? (
                    <>
                      <Form.Item name="waybill_carrier" label="物流承运商">
                        <Input placeholder="例如 USPS / UPS / DHL" />
                      </Form.Item>
                      <Form.Item name="waybill_tracking_no" label="物流跟踪号">
                        <Input placeholder="请输入物流跟踪号" />
                      </Form.Item>
                    </>
                  ) : null}
                </div>
                <div style={{ color: '#8c8c8c' }}>
                  {showWaybillFields
                    ? '请填写包装信息与运单信息，保存后同步给客户。'
                    : '请填写包装信息，保存后客户可依据该数据补交运单。'}
                </div>
              </>
            );
          })()}
        </Form>
      </Modal>


    </div>
  );
};

export default InstructionDetailPage;


